import {
  AccommodationDocumentType,
  AccommodationPaymentMethod,
  AccommodationOfferOptions,
  AccommodationPriceType,
  AccommodationSwitches,
  AccommodationTravelStatus,
  AccommodationType,
  AccommodationTypeOf,
  ActionType,
  ApprovedAuthorizationOption,
  BookingCSVReportType,
  BookingCannotBeRemovedReason,
  BookingInvoiceType,
  BookingReportStatus,
  BookingReportType,
  BookingStatusType,
  BookingSystemEvents,
  BookingWeekConsultantDocumentWithOutBookingType,
  BookingWeekDocumentTypes,
  BulkActionType,
  ConsultantBookingStatus,
  ConsultantContractAccommodationAlt,
  ConsultantContractBonusType,
  ConsultantContractCurrency,
  ConsultantContractOvertimeOpt,
  ConsultantContractPayoutType,
  ConsultantContractPensionSolution,
  ConsultantContractPeriod,
  ConsultantContractPriceType,
  ConsultantContractSalaryType,
  ConsultantContractTravelsAlt,
  ConsultantContractTravelsPeriod,
  ConsultantContractType,
  ConsultantEventType,
  ConsultantGetsPaidPer,
  ConsultantLeadStatus,
  ConsultantReferenceRelation,
  ConsultantQueryBookingStatus,
  ConsultantStatus,
  ConsultantTableColumns,
  ContractType,
  ContractsPriceTemplateType,
  CriminalRecordType,
  Currency,
  CustomerContractAgreementType,
  CustomerContractCompany,
  CustomerContractDocumentTypes,
  CustomerContractStatus,
  CustomerDocumentTypes,
  CustomerEventType,
  CustomerType,
  GDPRCommunicationChannel,
  GDPRConsentType,
  GDPROptionType,
  HospitalityPaidBy,
  InitialContact,
  InvoiceMonth,
  InvoiceSplitType,
  InvoicingStrategyType,
  LanguageSkillLevel,
  LastContactType,
  LuggageType,
  LegalBasis,
  LegalPersonType,
  LegalPersonTypeWithNoOption,
  NodeType,
  NorwayConsultantStatus,
  OrganizationType,
  PayPeriod,
  PayUnit,
  PriceListDay,
  PriceListWorkType,
  RecruitingPortalType,
  RideType,
  SMSDeliveryStatus,
  SearchQualityCheckStatus,
  SelfRegisterOption,
  BookingKitStatusType,
  BookingKitType,
  TimeReportStatus,
  TimeReportTemplateType,
  TimeReportType,
  TodoStatus,
  TransportationType,
  TravelRequestTime,
  WorkedInNorwayOption,
  NextContactDateReason,
  SearchSAHLRGroup,
  ConsultantPipelineStatus,
  CardType,
  DrivingLicenceType,
  BulkActionCommunicationSubType,
  ConsultantReferenceType,
  ConsultantReferenceStatus,
  ConsultantUnfitType,
  BookingKitTableColumns,
  Tenant,
  AccommodationRequestTableColumns,
  AccommodationRequestStatusType,
  TravelRequestStatusType,
  TravelRequestTableColumns,
  ConsultantContractSelfInvoicingCheck,
  TicketDifficultyLevel,
  QualityDocumentOrderType
} from "@bonliva-crm/shared";
import { sv } from "date-fns/locale";
import moment from "moment";
import "moment/locale/sv";

import { customerContractCard } from "./sections/customer_contract_card";
import { customerContractPriceLists } from "./sections/customer_contract_price_lists";
import { customerContractProfile } from "./sections/customer_contract_profile";
import { toLocale } from "./toLocale";

export type Country = keyof (typeof svLocale)["countries"];

export function assertCountry(v: string): Country {
  if (Object.keys(svLocale.countries).includes(v)) {
    return v as Country;
  }

  throw new Error(`Unknown country "${v}"`);
}

function setLocale() {
  moment.updateLocale("sv", {
    workinghours: {
      0: null,
      1: ["08:00:00", "17:00:00"],
      2: ["08:00:00", "17:00:00"],
      3: ["08:00:00", "17:00:00"],
      4: ["08:00:00", "17:00:00"],
      5: ["08:00:00", "17:00:00"],
      6: null
    },
    holidays: [
      "*-01-01",
      "*-01-06",
      "*-03-30",
      "*-04-01",
      "*-04-02",
      "*-05-01",
      "*-05-10",
      "*-05-20",
      "*-06-06",
      "*-06-22",
      "*-06-23",
      "*-10-03",
      "*-12-24",
      "*-12-25",
      "*-12-26",
      "*-12-31"
    ]
  });

  moment.locale("sv");
}

const tenants: Record<Tenant, string> = {
  [Tenant.BonlivaAB]: "Bonliva AB",
  [Tenant.WCC]: "We Connect Care"
};

const tenantsShort: Record<Tenant, string> = {
  [Tenant.BonlivaAB]: "Bonliva",
  [Tenant.WCC]: "WCC"
};

const bookingInvoiceType: Record<BookingInvoiceType, string> = {
  [BookingInvoiceType.SickPay]: "Sjuklön",
  [BookingInvoiceType.Introduction]: "Introduktion",
  [BookingInvoiceType.OrderCreated]: "Skapade order",
  [BookingInvoiceType.Completion]: "Komp",
  [BookingInvoiceType.Other]: "Övrig"
};

const invoiceSplitType: Record<InvoiceSplitType, string> = {
  [InvoiceSplitType.ByShift]: "Per pass",
  [InvoiceSplitType.ByDate]: "Per datum"
};

const invoiceMonth: Record<InvoiceMonth, string> = {
  [InvoiceMonth.Full]: "Hela veckan",
  [InvoiceMonth.FirstMonth]: "Månad 1",
  [InvoiceMonth.SecondMonth]: "Månad 2"
};

const invoicingStrategyType: Record<InvoicingStrategyType, string> = {
  [InvoicingStrategyType.CreationDate]: "Priser knutna till bokningstillfället",
  [InvoicingStrategyType.ExecutionDate]: "Priser justeras efter prisjustering"
};

const bookingWeekDocumentTypes: Record<BookingWeekDocumentTypes, string> = {
  [BookingWeekDocumentTypes.UncertifiedTimeReport]: "Ej attesterad tidrapport",
  [BookingWeekDocumentTypes.CertifiedTimeReport]: "Attesterad tidrapport",
  [BookingWeekDocumentTypes.ExpensesReceipts]: "Utlägg/kvitton",
  [BookingWeekDocumentTypes.InvoiceDocs]: "Fakturaunderlag",
  [BookingWeekDocumentTypes.SalaryBasis]: "Löneunderlag",
  [BookingWeekDocumentTypes.BenefitTaxation]: "Förmånsbeskattning",
  [BookingWeekDocumentTypes.TravelConfirmation]: "Resebekräftelser",
  [BookingWeekDocumentTypes.AccommodationConfirmations]: "Boendebekräftelser",
  [BookingWeekDocumentTypes.AccommodationTravelInvoices]: "Rese- och boendefakturor",
  [BookingWeekDocumentTypes.AccTravelConfirmationsWithOutBooking]:
    "Boende/Resebekräftelser ej uppladdade på bokningsveckor",
  [BookingWeekDocumentTypes.SubcontractorInvoices]: "Underleverantörsfakturor",
  [BookingWeekDocumentTypes.StandingCertificateFromCustomer]: "Stående attest från kund",
  [BookingWeekDocumentTypes.SickPayBasis]: "Sjuklöneunderlag",
  [BookingWeekDocumentTypes.CreditUnderSupplierInvoice]: "Kredit Underleverantörsfaktura",
  [BookingWeekDocumentTypes.Other]: "Övrigt",
  [BookingWeekDocumentTypes.CallResponse]: "Avropssvar",
  [BookingWeekDocumentTypes.TimeReport]: "Tidrapport",
  [BookingWeekDocumentTypes.InvoiceForm]: "Fakturaformulär",
  [BookingWeekDocumentTypes.PayForm]: "Löneförmulär",
  [BookingWeekDocumentTypes.SickPayForm]: "Sjuklönemall",
  [BookingWeekDocumentTypes.SubcontractorPayForm]: "Fakturautkast underleverantör"
};

const bookingWeekConsultantDocumentWithOutBookingType: Record<
  BookingWeekConsultantDocumentWithOutBookingType,
  string
> = {
  [BookingWeekConsultantDocumentWithOutBookingType.AccTravelConfirmationsWithOutBooking]:
    "Boende/Resebekräftelser ej uppladdade på bokningsveckor",
  [BookingWeekConsultantDocumentWithOutBookingType.CallResponse]: "Avropssvar",
  [BookingWeekConsultantDocumentWithOutBookingType.RentalAdviceAgreement]:
    "Hyresavi/avtal",
  [BookingWeekConsultantDocumentWithOutBookingType.AccommodationInvoices]:
    "Boendefakturor",
  [BookingWeekConsultantDocumentWithOutBookingType.Receipts]: "Kvitton",
  [BookingWeekConsultantDocumentWithOutBookingType.CreditUnderSupplierInvoice]:
    "Kredit Underleverantörsfaktura",
  [BookingWeekConsultantDocumentWithOutBookingType.OtherBookingWeekDocWithOutBookingType]:
    "Övrigt",
  [BookingWeekConsultantDocumentWithOutBookingType.Canceled]: "Avbokat",
  [BookingWeekConsultantDocumentWithOutBookingType.BookingConfirmationToCustomer]:
    "Bokningsbekräftelse till kund"
};

const customerDocumentTypes: Record<CustomerDocumentTypes, string> = {
  [CustomerDocumentTypes.StandingCertificateForTR]: "Stående attest för tidrapporter",
  [CustomerDocumentTypes.Other]: "Övrigt"
};

const customerContractStatus: Record<CustomerContractStatus, string> = {
  [CustomerContractStatus.Active]: "Aktivt",
  [CustomerContractStatus.Coming]: "Kommande",
  [CustomerContractStatus.Completed]: "Avslutad",
  [CustomerContractStatus.Deleted]: "Raderad"
};

const bookingReportStatus: Record<BookingReportStatus, string> = {
  [BookingReportStatus.NotBilledNotPaidNotPassed]: "Kommande",
  [BookingReportStatus.NotBilledNotPaid]: "Öppen",
  [BookingReportStatus.Billed]: "Fakturerad",
  [BookingReportStatus.Paid]: "Utbetald",
  [BookingReportStatus.BilledAndPaid]: "Stängd",
  [BookingReportStatus.Total]: "Total"
};

const bookingReportType: Record<BookingReportType, string> = {
  [BookingReportType.ByConsultant]: "Konsult",
  [BookingReportType.ByDepartment]: "Avdelning",
  [BookingReportType.ByHospital]: "Sjukhus",
  [BookingReportType.ByOrganization]: "Organisation",
  [BookingReportType.ByUser]: "Användare",
  [BookingReportType.ByWeek]: "Vecka",
  [BookingReportType.ByProject]: "Projekt"
};

const bookingCSVReportType: Record<BookingCSVReportType, string> = {
  [BookingCSVReportType.Bookings]: "Bokningar",
  [BookingCSVReportType.ProfitabilityPerConsultant]: "Lönsamhet per konsult",
  [BookingCSVReportType.OpenPosts]: "Öppna Poster",
  [BookingCSVReportType.OpenHalfOpenRecords]: "Öppna + Halvöppna poster",
  [BookingCSVReportType.HalfOpenRecords]: "Halvöppna poster",
  [BookingCSVReportType.Continuity]: "Kontinuitet",
  [BookingCSVReportType.NationalProcurementStatistics]: "Statistik Nationella",
  [BookingCSVReportType.SAHLRStatus]: "S/A-HLR-status",
  [BookingCSVReportType.ProjectReconciliation]: "Projektavstämning",
  [BookingCSVReportType.ClosedBookingWeeks]: "Stängda bokningsveckor"
};

const bookingStatus: Record<BookingStatusType, string> = {
  [BookingStatusType.Open]: "Öppen",
  [BookingStatusType.Billed]: "Fakturerad",
  [BookingStatusType.Paid]: "Utbetald",
  [BookingStatusType.Closed]: "Stängd",
  [BookingStatusType.Canceled]: "Avbokat"
};

const consultantBookingStatus: Record<ConsultantBookingStatus, string> = {
  [ConsultantBookingStatus.NeverBooked]: "Lead",
  [ConsultantBookingStatus.BookedPreviously]: "Tidigare bokad",
  [ConsultantBookingStatus.Booked]: "Bokad"
};

const consultantLeadStatus: Record<ConsultantLeadStatus, string> = {
  [ConsultantLeadStatus.NotContacted]: "Ej kontaktad",
  [ConsultantLeadStatus.ContactedNoAnswer]: "Kontaktad, inget svar",
  [ConsultantLeadStatus.Assigned]: "Tilldelad",
  [ConsultantLeadStatus.BadTiming]: "Dålig tajming",
  [ConsultantLeadStatus.NotRelevant]: "Ej aktuell",
  [ConsultantLeadStatus.RelevantLater]: "Aktuell längre fram",
  [ConsultantLeadStatus.Warm]: "Varm",
  [ConsultantLeadStatus.Unknown]: "Vet ej"
};

const drivingLicenceType: Record<DrivingLicenceType, string> = {
  [DrivingLicenceType.Automatic]: "Automat",
  [DrivingLicenceType.Manual]: "Manuell"
};

const nodeType: Record<NodeType, string> = {
  Accommodation: "Boende",
  AccommodationRequest: "",
  Action: "",
  Blacklist: "",
  Booking: "Bokning",
  CallList: "",
  Consultant: "Konsult",
  ConsultantContractOption: "",
  ContactPerson: "",
  ContractArticle: "",
  Customer: "Kund",
  CustomerContract: "Kundavtal",
  CustomerContractOption: "",
  Day: "",
  Event: "",
  ExternalUser: "",
  File: "",
  GeoPosition: "",
  Invoice: "",
  Memo: "",
  Notification: "",
  Project: "",
  SMSMessage: "",
  Team: "",
  TodoItem: "",
  TravelRequest: "",
  User: ""
};

const bookingCannotBeRemovedReason: Record<BookingCannotBeRemovedReason, string> = {
  [BookingCannotBeRemovedReason.AccommodationBenefitTax]:
    "Denna veckan kan inte avbokas då det finns en boendekostnad administrerad i ERP för veckan. Gå till reservationens fördelningssida och fördela om antalet nätter till 0 för denna vecka först.",
  [BookingCannotBeRemovedReason.CustomerBilled]:
    "Denna veckan kan inte avbokas då den är hanterad av ekonomiavdelningen.",
  [BookingCannotBeRemovedReason.ConsultantPaid]:
    "Denna veckan kan inte avbokas då den är hanterad av ekonomiavdelningen.",
  [BookingCannotBeRemovedReason.TimereportNotBlank]:
    "Denna veckan kan inte avbokas då det finns en påbörjad tidrapport. Radera tiderna på tidrapporten för att avboka veckan.",
  [BookingCannotBeRemovedReason.HasInvoiceNumber]:
    "Denna veckan kan inte avbokas då den är hanterad av ekonomiavdelningen."
};

const timeReportStatus: Record<TimeReportStatus, string> = {
  [TimeReportStatus.Blank]: "Tom",
  [TimeReportStatus.Draft]: "Utkast",
  [TimeReportStatus.SubmittedByConsultant]: "Inskickad",
  [TimeReportStatus.SentToConsultant]: "Skickad till konsult",
  [TimeReportStatus.SentToCustomer]: "Skickad till kund",
  [TimeReportStatus.ApprovedByCustomer]: "Godkänd av kund",
  [TimeReportStatus.RejectedByCustomer]: "Ej godkänd av kund",
  [TimeReportStatus.ConsultantPaymentProcessed]: "Utbetald",
  [TimeReportStatus.Canceled]: "Avbokat",
  // BookingKitStatusType
  [TimeReportStatus.SentToHBA]: "Skickad till HBA",
  [TimeReportStatus.RejectByHBA]: "Ej godkänd av HBA",
  [TimeReportStatus.SentToOC]: "Skickad till OC",
  [TimeReportStatus.RejectByOC]: "Ej godkänd av OC",
  [TimeReportStatus.RejectByFinance]: "Nekad av Ekonomi",
  [TimeReportStatus.SentToFinance]: "Skickad till Ekonomi",
  [TimeReportStatus.Disabled]: "Inaktiverad"
};

const bookingKitStatusType: Record<BookingKitStatusType, string> = {
  [BookingKitStatusType.SentToHBA]: "Skickad till HBA",
  [BookingKitStatusType.ApprovedByHBA]: "Godkänd av HBA",
  [BookingKitStatusType.RejectedByHBA]: "Nekad av HBA",

  [BookingKitStatusType.SentToOC]: "Skickad till OC",
  [BookingKitStatusType.ApprovedByOC]: "Godkänd av OC",
  [BookingKitStatusType.RejectedByOC]: "Nekad av OC",

  [BookingKitStatusType.SentToFinance]: "Skickad till Ekonomi",
  [BookingKitStatusType.ApprovedByFinance]: "Godkänd av Ekonomi",
  [BookingKitStatusType.RejectedByFinance]: "Nekad av Ekonomi",

  [BookingKitStatusType.SentToFinancePayment]: "Skickad till Ekonomi - Betalning",
  [BookingKitStatusType.ApprovedByFinancePayment]: "Godkänd av Ekonomi - Betalning",
  [BookingKitStatusType.RejectedByFinancePayment]: "Nekad av Ekonomi - Betalning",

  [BookingKitStatusType.SentToFinanceInvoicing]: "Skickad till Ekonomi - Kundfakturering",
  [BookingKitStatusType.ApprovedByFinanceInvoicing]:
    "Godkänd av Ekonomi - Kundfakturering",
  [BookingKitStatusType.RejectedByFinanceInvoicing]: "Nekad av Ekonomi - Kundfakturering",

  [BookingKitStatusType.Created]: "Skapad",
  [BookingKitStatusType.Done]: "Hanterad",
  [BookingKitStatusType.Deleted]: "Raderad"
};

const bookingKitType: Record<BookingKitType, string> = {
  [BookingKitType.Complete]: "Komplett",
  [BookingKitType.AccruedIncome]: "Upplupen intäkt",
  [BookingKitType.AccruedCost]: "Upplupen kostnad",
  [BookingKitType.SickPay]: "Sjuklön",
  [BookingKitType.PreviouslyAccruedCost]: "Tidigare Upplupen Kostnad",
  [BookingKitType.PreviouslyAccruedIncome]: "Tidigare Upplupen Intäkt",
  [BookingKitType.Credit]: "Kredit",
  [BookingKitType.Compensation]: "Komp"
};

const timeReportTemplateType: Record<TimeReportTemplateType, string> = {
  [TimeReportTemplateType.Basic]: "Standard",
  [TimeReportTemplateType.KronobergLakare]: "Kronoberg Lakare",
  [TimeReportTemplateType.KronobergSSK]: "Kronoberg SSK",
  [TimeReportTemplateType.DalarnaLakare]: "Dalarna Lakare",
  [TimeReportTemplateType.GotlandLakare]: "Gotland Lakare",
  [TimeReportTemplateType.GotlandSSK]: "Gotland SSK",
  [TimeReportTemplateType.BlekingeLakare]: "Blekinge Lakare",
  [TimeReportTemplateType.BlekingeSSK]: "Blekinge SSK",
  [TimeReportTemplateType.OstergotlandLakare]: "Ostergotland Lakare",
  [TimeReportTemplateType.OstergotlandSSK]: "Ostergotland SSK",
  [TimeReportTemplateType.GavleborgLakare]: "Gavleborg Lakare",
  [TimeReportTemplateType.HallandSSK]: "Halland SSK",
  [TimeReportTemplateType.HallandLakare]: "Halland Lakare"
};

const bookingSystemEvents: Record<BookingSystemEvents, string> = {
  [BookingSystemEvents.Created]: "Skapad",
  [BookingSystemEvents.UpdateAccStatus]: "har uppdaterat Boende-status till",
  [BookingSystemEvents.UpdateTravelStatus]: "har uppdaterat Resa-status till",
  [BookingSystemEvents.UpdateFinanceStatus]: "har uppdaterat Ekonomi-status till",
  [BookingSystemEvents.InvoicePaidOn]: "har uppdaterat underleverantörsfaktura till",
  [BookingSystemEvents.InvoicePaidOff]: "har uppdaterat underleverantörsfaktura till",
  [BookingSystemEvents.InvoicePaidOnEmp]: "har uppdaterat lön till",
  [BookingSystemEvents.InvoicePaidOffEmp]: "har uppdaterat lön till",
  [BookingSystemEvents.EditCost]: "har uppdaterad kostnader",
  [BookingSystemEvents.UpdateWorkedHours]: "Fält för arbetade timmar har uppdaterats",
  [BookingSystemEvents.UpdateSubContractorInvoice]: "har uppdaterat kundfaktura",
  [BookingSystemEvents.UploadSubcontractorDocument]:
    "har laddat upp Underleverantörsfaktura",
  [BookingSystemEvents.RemoveSubcontractorDocument]:
    "har raderat Underleverantörsfaktura",
  [BookingSystemEvents.UploadSubCustomerInvoice]: "har laddat upp Kundfaktura",
  [BookingSystemEvents.RemoveCustomerInvoice]: "har raderat Kundfaktura",
  [BookingSystemEvents.WriteNote]: "har skrivit en anteckning",
  [BookingSystemEvents.UpdatePatientId]: "har skrivit en Patient-ID",
  [BookingSystemEvents.AdditionalWorkOn]: "har klickat för Övertid",
  [BookingSystemEvents.AdditionalWorkOff]: "har klickat ur Övertid",
  [BookingSystemEvents.RemoveAccConfirmation]: "Ta bort bekräftelse på boende",
  [BookingSystemEvents.RestoreAccConfirmation]: "Återställ bekräftelse på boende",
  [BookingSystemEvents.RemoveTravelConfirmation]: "Ta bort bekräftelse på resan",
  [BookingSystemEvents.RestoreTravelConfirmation]: "Återställ bekräftelse på resan",
  [BookingSystemEvents.Canceled]: "Avbokat",
  [BookingSystemEvents.Restored]: "Avbokning ångrad",
  [BookingSystemEvents.SaveInvoice]: "Spara faktura",
  [BookingSystemEvents.True]: "sann",
  [BookingSystemEvents.False]: "falsk"
};

const contractType: Record<ContractType, string> = {
  [ContractType.Hourly]: "Timlön",
  [ContractType.HourlyPension]: "Timlön (+pens.)",
  [ContractType.HourlyPensionInsurance]: "Timlön (+pens., + sjukförs.)",
  [ContractType.NationalProcurement]: "Nationella Upphandlingen"
};

const criminalRecordType: Record<CriminalRecordType, string> = {
  School: "Arbete inom skolväsendet",
  LSS: "Arbete med barn enligt LSS",
  HVB: "Arbete på HVB-hem",
  WorkWithOtherKid: "Arbete med barn i annan verksamhet än skola och barnomsorg",
  HomeCareElderlyDisabled:
    "Arbete i hemmet med äldre och personer med funktionsnedsättning",
  MunicipalityLeadingPosition: "Arbete till ledande befattning inom kommuner",
  Norway: "Utdrag från annat EU-land",
  OTHER: "Kontrollera egna uppgifter"
};

const consultantEventTypes: Record<ConsultantEventType, string> = {
  [ConsultantEventType.Call]: "Samtal",
  [ConsultantEventType.Comment]: "Kommentarer",
  [ConsultantEventType.QualityUpdate]: "Kvalitetsuppdateringar",
  [ConsultantEventType.Document]: "Filer",
  [ConsultantEventType.Noanswer]: "Inget svar",
  [ConsultantEventType.Email]: "Email",
  [ConsultantEventType.MetInPerson]: "Träffats personligen",
  [ConsultantEventType.Registered]: "Registrering",
  [ConsultantEventType.Reminder]: "Påminnelse",
  [ConsultantEventType.SMS]: "SMS",
  [ConsultantEventType.SystemLog]: "Systemlogg",
  [ConsultantEventType.ConsultantApp]: "Uppdateringar Bonliva-appen",
  [ConsultantEventType.Interest]: "Intresseanmälan",
  [ConsultantEventType.GDPR]: "GDPR",
  [ConsultantEventType.UserSettings]: "Användarinställningar",
  [ConsultantEventType.WelcomeEmail]: "Välkomstmail",
  [ConsultantEventType.MarketingMessage]: "Marknadsutskick"
};

const consultantEventTypesNew: Record<
  Exclude<ConsultantEventType, "MetInPerson">,
  string
> = {
  [ConsultantEventType.Call]: "Samtal",
  [ConsultantEventType.Comment]: "Anteckning",
  [ConsultantEventType.QualityUpdate]: "Kvalitetsuppdateringar",
  [ConsultantEventType.Document]: "Filer",
  [ConsultantEventType.Noanswer]: "Ej svar",
  [ConsultantEventType.Email]: "Maila",
  [ConsultantEventType.Registered]: "Registrering",
  [ConsultantEventType.Reminder]: "Påminnelse",
  [ConsultantEventType.SMS]: "SMSa",
  [ConsultantEventType.SystemLog]: "Systemlogg",
  [ConsultantEventType.ConsultantApp]: "Uppdatering Bonliva-appen",
  [ConsultantEventType.Interest]: "Intresseanmälan",
  [ConsultantEventType.GDPR]: "GDPR",
  [ConsultantEventType.UserSettings]: "Användarinställningar",
  [ConsultantEventType.WelcomeEmail]: "Välkomstmail",
  [ConsultantEventType.MarketingMessage]: "Marknadsutskick"
};

const customerEventTypes: Record<CustomerEventType, string> = {
  [CustomerEventType.Comment]: "CustomerCommentEvent",
  [CustomerEventType.Reminder]: "CustomerReminderEvent",
  [CustomerEventType.MetInPerson]: "CustomerMetInPersonEvent"
};

const consultantQueryBookingStatus: Record<ConsultantQueryBookingStatus, string> = {
  [ConsultantQueryBookingStatus.NoBookings]: "Har aldrig varit bokad",
  [ConsultantQueryBookingStatus.NoBookingsFromDate]: "Ej bokad efter..",
  [ConsultantQueryBookingStatus.NoBookingsFromDateBookedPreviously]:
    "Tidigare bokad men obokad efter..",
  [ConsultantQueryBookingStatus.NoBookingsInPeriod]: "Ej bokad i period..",
  [ConsultantQueryBookingStatus.HasBookingsFromDate]: "Bokad efter..",
  [ConsultantQueryBookingStatus.NoBookingsBeforeDate]: "Ej bokad innan..",
  [ConsultantQueryBookingStatus.BookingsInPeriodButNotAfter]:
    "Bokad i period, obokad efter..",
  [ConsultantQueryBookingStatus.BookingsInPeriod]: "Bokad i period.."
};

const consultantQueryBookingStatusStr: Record<
  ConsultantQueryBookingStatus,
  (...args) => string
> = {
  [ConsultantQueryBookingStatus.NoBookings]: () => "Har aldrig varit bokad",
  [ConsultantQueryBookingStatus.NoBookingsFromDate]: (from) => `Ej bokad efter ${from}`,
  [ConsultantQueryBookingStatus.NoBookingsFromDateBookedPreviously]: (from) =>
    `Tidigare bokad men obokad efter ${from}`,
  [ConsultantQueryBookingStatus.NoBookingsInPeriod]: () => "Ej bokad i period..",
  [ConsultantQueryBookingStatus.HasBookingsFromDate]: (from) => `Bokad efter ${from}`,
  [ConsultantQueryBookingStatus.NoBookingsBeforeDate]: (from) => `Ej bokad innan ${from}`,
  [ConsultantQueryBookingStatus.BookingsInPeriodButNotAfter]: (from) =>
    `Bokad i period, obokad efter ${from}`,
  [ConsultantQueryBookingStatus.BookingsInPeriod]: (from, to) =>
    `Bokad i period fra ${from} till ${to}`
};

const initialContact: Record<InitialContact, string> = {
  [InitialContact.CreatedByBonliva]: "Skapad av Bonliva",
  [InitialContact.CreatedByWCC]: "Skapad av WCC",
  [InitialContact.SelfRegister]: "Intresseanmälan",
  [InitialContact.Recommendation]: "Rekommendation"
};

const selfRegisterOption: Record<SelfRegisterOption, string> = {
  [SelfRegisterOption.SelfRegisterNotSpecified]: "Ej angett",
  [SelfRegisterOption.Bemlo]: "Bemlo",
  [SelfRegisterOption.HomepageChat]: "Chattfunktionen på hemsidan",
  [SelfRegisterOption.EmploymentAgency]: "Arbetsförmedlingen",
  [SelfRegisterOption.Lead]: "Lead",
  [SelfRegisterOption.PhoneCall]: "Telefon/mail",
  [SelfRegisterOption.SearchEngine]: "Sökmotor",
  [SelfRegisterOption.SelfRegisterHemsida]: "Intresseanmälan via Hemsida",
  [SelfRegisterOption.SelfRegisterPaper]: "Intresseanmälan via pappersblankett",
  [SelfRegisterOption.SelfRegisterPhone]: "Intresseanmälan via telefon",
  [SelfRegisterOption.SocialMedia]: "Sociala medier",
  [SelfRegisterOption.LinkedIn]: "LinkedIn",
  [SelfRegisterOption.Newsletter]: "Nyhetsbrev",
  [SelfRegisterOption.BonlivaApp]: "Bonliva-appen"
};

const customerType: Record<CustomerType, string> = {
  [CustomerType.Region]: "Organisation",
  [CustomerType.Hospital]: "Verksamhet",
  [CustomerType.Department]: "Avdelning"
};

const customerTypeShort: Record<CustomerType, string> = {
  [CustomerType.Region]: "Org",
  [CustomerType.Hospital]: "Verk",
  [CustomerType.Department]: "Avd"
};

const customerContractDocumentTypes: Record<CustomerContractDocumentTypes, string> = {
  [CustomerContractDocumentTypes.Agreement]: "Avtal",
  [CustomerContractDocumentTypes.ContractAttachments]: "Avtalsbilagor",
  [CustomerContractDocumentTypes.PricesOB]: "Priser + OB",
  [CustomerContractDocumentTypes.SubVersion]: "Avtal prisavvikelse, konsultspecifik",
  [CustomerContractDocumentTypes.Other]: "Övrigt"
};

const customerContractAgreementType: Record<CustomerContractAgreementType, string> = {
  [CustomerContractAgreementType.Procured]: "Upphandlad",
  [CustomerContractAgreementType.NotProcured]: "Ramavtal Kundavtal"
};

const customerContractCompany: Record<CustomerContractCompany, string> = {
  [CustomerContractCompany.Bonliva_AB]: "Bonliva AB",
  [CustomerContractCompany.Bonliva_Care]: "Bonliva Care"
};

const consultantStatus: Record<ConsultantStatus, string> = {
  [ConsultantStatus.ToBeDeleted]: "Ska tas bort",
  [ConsultantStatus.Active]: "Active",
  [ConsultantStatus.Unverified]: "Unverified",
  [ConsultantStatus.Verified]: "Verified",
  [ConsultantStatus.Deleted]: "Deleted"
};

const consultantStatusOptions: Record<NorwayConsultantStatus, string> = {
  [NorwayConsultantStatus.Committed]: "Engagerad",
  [NorwayConsultantStatus.Pending]: "Avvaktande",
  [NorwayConsultantStatus.NoAnswer]: "Vet ej",
  [NorwayConsultantStatus.NotObligated]: "Ej engagerad"
};

const priceListDay: Record<PriceListDay, string> = {
  [PriceListDay.BusinessDay]: "Vardagar",
  [PriceListDay.Weekends]: "Helger",
  [PriceListDay.SqueezedDay]: "Klämdag",
  [PriceListDay.DayAfterHoliday]: "Dag efter helgdag",
  [PriceListDay.DayBeforeHoliday]: "Dag före helgdag",
  [PriceListDay.NearestWeekdayBeforeHoliday]: "Vardag närmast före helgdag",
  [PriceListDay.DayAfterMajorHoliday]: "Dag efter storhelg",
  [PriceListDay.NearestWeekdayAfterHoliday]: "Vardag närmast efter helgdag",
  [PriceListDay.DayBeforeMajorHoliday]: "Dag före storhelg",
  [PriceListDay.NearestWeekdayAfterGreatHoliday]: "Vardag närmast efter storhelg",
  [PriceListDay.Holiday]: "Helgdag",
  [PriceListDay.MajorHoliday]: "Storhelg",
  [PriceListDay.Mon]: "mån",
  [PriceListDay.Tue]: "tis",
  [PriceListDay.Wed]: "ons",
  [PriceListDay.Thu]: "tors",
  [PriceListDay.Fri]: "fre",
  [PriceListDay.Sat]: "lör",
  [PriceListDay.Sun]: "sön",

  [PriceListDay.AnyTime]: "Alla dagar",

  [PriceListDay.DayBeforeNewYearsEve]: "Dagen före Nyårsafton",
  [PriceListDay.NewYearsEve]: "Dagen före Nyårsafton",
  [PriceListDay.NewYearsDay]: "Nyårsdagen",
  [PriceListDay.WeekdayAfterNewYearsDay]: "Vardag efter Nyårsdagen",
  [PriceListDay.WeekdayBeforeTwelfthDay]: "Vardag före Trettondedag jul",
  [PriceListDay.TwelfthDay]: "Trettondedag jul",
  [PriceListDay.WeekdayAfterTwelfthDay]: "Vardag efter Trettondedag jul",
  [PriceListDay.DayBeforeEasterFriday]: "Dag före Långfredagen",
  [PriceListDay.EasterFriday]: "Långfredagen",
  [PriceListDay.EasterSaturday]: "Påskafton",
  [PriceListDay.EasterSunday]: "Påskdagen",
  [PriceListDay.EasterMonday]: "Annandag påsk",
  [PriceListDay.DayAfterEasterMonday]: "Dag efter Annandag påsk",
  [PriceListDay.WeekdayBeforeFirstOfMay]: "Vardag före Första Maj",
  [PriceListDay.FirstOfMay]: "Första Maj",
  [PriceListDay.WeekdayAfterFirstOfMay]: "Vardag efter Första Maj",
  [PriceListDay.WeekdayBeforeAscensionDay]: "Vardag före Kristi himmelfärdsdag",
  [PriceListDay.AscensionDay]: "Kristi himmelsfärdsdag",
  [PriceListDay.WeekdayAfterAscensionDay]: "Vardag efter Kristi himmelfärdsdag",
  [PriceListDay.DayBeforeWhitesunDay]: "Dag före Pingsafton",
  [PriceListDay.WhitsunEve]: "Pingstafton",
  [PriceListDay.WhitSunday]: "Pingstdagen",
  [PriceListDay.WhitMonday]: "Annandag pingst",
  [PriceListDay.DayAfterWhitsunMonday]: "Dag efter Annandag pingst",
  [PriceListDay.WeekdayBeforeSwedensNationalDay]: "Vardag före Sveriges nationaldag",
  [PriceListDay.SwedensNationalDay]: "Sveriges nationaldag",
  [PriceListDay.WeekdayAfterSwedensNationalDay]: "Vardag efter Sveriges nationaldag",
  [PriceListDay.DayBeforeMidsummerEve]: "Dag före Midsommarafton",
  [PriceListDay.MidsummerEve]: "Midsommarafton",
  [PriceListDay.MidsummerDay]: "Midsommardagen",
  [PriceListDay.WeekdayAfterMidsummerDay]: "Vardag efter Midsommardagen",
  [PriceListDay.WeekdayBeforeAllSaintsDay]: "Vardag före Alla helgons dag",
  [PriceListDay.AllSaintsDay]: "Alla helgons dag",
  [PriceListDay.DayBeforeChristmasEve]: "Dag före Julafton",
  [PriceListDay.ChristmasEve]: "Julafton",
  [PriceListDay.ChristmasDay]: "Juldagen",
  [PriceListDay.BoxingDay]: "Annandag jul",
  [PriceListDay.WeekdayAfterBoxingDay]: "Vardag efter Annandag jul"
};

const priceListWorkType: Record<PriceListWorkType, string> = {
  [PriceListWorkType.Active]: "Aktiv tid",
  [PriceListWorkType.Normal]: "Ordinarie arbetstid",
  [PriceListWorkType.OnCall]: "Aktiv tid, Jour/Beredskap",
  [PriceListWorkType.OnCallJour]: "Aktiv tid, Jour",
  [PriceListWorkType.OnCallBer]: "Aktiv tid, Beredskap",
  [PriceListWorkType.OnCallA]: "Beredskap A",
  [PriceListWorkType.OnCallB]: "Beredskap B",
  [PriceListWorkType.OnCallPassive]: "Passiv tid, Jour",
  [PriceListWorkType.Overtime]: "Övertid",
  [PriceListWorkType.ChildAndYouthInvestigation]: "Barn- och ungdomsutredning",
  [PriceListWorkType.InvestigationWithinSchool]: "Utredning inom skola",
  [PriceListWorkType.AdultInvestigation]: "Vuxenutredning",
  [PriceListWorkType.BasicInvestigation]: "Basutredning",
  [PriceListWorkType.AutismInvestigation]: "Autimsutredning",
  [PriceListWorkType.ChildAndAdolescentInvestigationADHDADD]:
    "Barn- och ungdomsutredning, ADHD/ADD",
  [PriceListWorkType.ChildAndAdolescentInquiryAutism]:
    "Barn- och ungdomsutredning, Autism",
  [PriceListWorkType.AdultEducationADHDADD]: "Vuxenutredning, ADHD/ADD",
  [PriceListWorkType.AdultStudiesAutism]: "Vuxenutredning, Autism",
  [PriceListWorkType.PsychologicalInvestigation]: "Psykologutredning",
  [PriceListWorkType.NeuropsychiatricExaminationChildrenAndYoungPeople]:
    "Neuropsykiatrisk utredning Barn och Unga",
  [PriceListWorkType.NeuropsychiatricExaminationAdults]:
    "Neuropsykiatrisk utredning vuxna",
  [PriceListWorkType.NeuropsychiatricExaminationChildrenAndYoungPeopleStep3]:
    "NP-utr. Barn och Unga steg 3",
  [PriceListWorkType.MedicalExamination]: "Läkarutredning",
  [PriceListWorkType.NeuropsychologicalExaminationAdults]:
    "Neuropsykologisk utredning Vuxna",
  [PriceListWorkType.NeuropsychologicalExaminationAdultsStep3]: "NP-utr. Vuxna steg 3",
  [PriceListWorkType.PsychologicalInvestigationAdults]: "Psykologutredning Vuxna",
  [PriceListWorkType.PsychologicalInvestigationChildrenAndYoungPeople]:
    "Psykologutredning Barn och Unga",
  [PriceListWorkType.MedicalExaminationAdults]: "Läkarutredning Vuxna",
  [PriceListWorkType.MedicalExaminationChildrenAndYoungPeople]:
    "Läkarutredning Barn och Unga",
  [PriceListWorkType.PrivateAssessmentCall]: "Privat bedömningssamtal",
  [PriceListWorkType.PrivateNPAssessmentChildrenAndYoung]: "Privat NP-utr. Barn och Unga",
  [PriceListWorkType.PrivateNPAssessmentAdults]: "Privat NP-utr. Vuxna"
};

const payUnit: Record<PayUnit, string> = {
  [PayUnit.Hour]: "Tim",
  [PayUnit.Period]: "Period",
  [PayUnit.Quantity]: "Kvantitet",
  [PayUnit.EmergencyCall]: "Beredskapsjour",
  [PayUnit.WorkShift]: "Jour",
  [PayUnit.Operation]: "Operation",
  [PayUnit.Investigation]: "Utredning"
};

const payPeriod: Record<PayPeriod, string> = {
  [PayPeriod.Daily]: "Dagligen",
  [PayPeriod.FullPeriod]: "Hel period"
};

const priceTemplateType: Record<ContractsPriceTemplateType, string> = {
  [ContractsPriceTemplateType.CustomerContract]: "Kundavtal",
  [ContractsPriceTemplateType.ConsultantContract]: "Konsultavtal"
};

const contractsPrice = {
  priceListDay,
  priceListWorkType,
  payUnit,
  payPeriod,
  priceTemplateType
};

const hospitalityPaidBy: Record<HospitalityPaidBy, string> = {
  [HospitalityPaidBy.Bonliva]: "Bonliva",
  [HospitalityPaidBy.Consultant]: "Konsult"
};

const accommodationTypeOf: Record<AccommodationTypeOf, string> = {
  [AccommodationTypeOf.HBA]: "Skicka förslag till HBA",
  [AccommodationTypeOf.Consultant]: "Skicka förslag till konsult",
  [AccommodationTypeOf.Directly]: "Boka direkt"
};

const accommodationDocumentType: Record<AccommodationDocumentType, string> = {
  [AccommodationDocumentType.Sqm]: "Kvm",
  [AccommodationDocumentType.GeneralRoutines]: "Allmänna rutiner",
  [AccommodationDocumentType.Other]: "Övrigt"
};

const legalPersonType: Record<LegalPersonType, string> = {
  [LegalPersonType.Employee]: "Löntagare",
  [LegalPersonType.Subcontractor]: "Underleverantör"
};

const cooperationForm: Record<LegalPersonType, string> = {
  [LegalPersonType.Employee]: "Anställning",
  [LegalPersonType.Subcontractor]: "Konsult"
};

const legalPersonTypeWithNoOption: Record<LegalPersonTypeWithNoOption, string> = {
  [LegalPersonTypeWithNoOption.Employee]: "Löntagare",
  [LegalPersonTypeWithNoOption.Subcontractor]: "Underleverantör",
  [LegalPersonTypeWithNoOption.NoOption]: "Saknar val"
};

const languageSkillLevel: Record<LanguageSkillLevel, string> = {
  [LanguageSkillLevel.Fluent]: "Flytande",
  [LanguageSkillLevel.Good]: "Bra",
  [LanguageSkillLevel.OK]: "Grundläggande"
};

const organizationType: Record<OrganizationType, string> = {
  LLC: "Aktiebolag",
  CommercialAssociation: "Ekonomisk förening",
  NonprofitAssosiation: "Ideella föreningar",
  Commune: "Kommun",
  County: "Region",
  Norway: "Norge",
  StateUnit: "Statlig enhet",
  Partnership: "Kommanditbolag"
};

const recruitingPortalType: Record<RecruitingPortalType, string> = {
  Avropsportalen: "Avropsportalen",
  Inkopio: "Inkopio",
  Medlo: "Medlo",
  Bemlo: "Bemlo",
  Nemo: "Nemo"
};

const reportActionTypes: Record<ActionType, string> = {
  [ActionType.CompleteTodo]: "Avklarade todos",
  [ActionType.ConsultantFormSubmit]: "Redigerade konsultprofiler",
  [ActionType.ConsultantProfileAccess]: "Öppnade konsultprofiler",
  [ActionType.CreateTodo]: "Skapade todos",
  [ActionType.DownloadConsultantPresentation]: "Nedladdade konsultpresentationer",
  [ActionType.SubmitConsultantReference]: "Referenstagningar",
  [ActionType.UpdateConsultantAvailability]: "Uppdaterade tillgängligheter",
  [ActionType.UserChatAction]: "Chattaktivitet",
  [ActionType.UserCreatedBookingGroupAction]: "Skapade bokningsgrupp",
  [ActionType.UserClearedQualityItemStatus]: "Borttagna kvalitetskontroller",
  [ActionType.UserCommentAction]: "Användarkommentarer",
  [ActionType.UserCreatedContactPerson]: "Skapade kontaktpersoner på kunder",
  [ActionType.UserEmailAction]: "Emailaktivitet",
  [ActionType.UserMassEmailAction]: "Mass-mail aktivitet",
  [ActionType.UserMetInPersonAction]: "Träffats personligen",
  [ActionType.UserSetQualityItemStatusApproved]: "Godkända kvalitetskontroller",
  [ActionType.UserSMSAction]: "SMS-aktivitet",
  [ActionType.UserUpdatedCustomer]: "Uppdaterade kunder"
};

const todoStatus: Record<TodoStatus, string> = {
  [TodoStatus.New]: "Ohanterad",
  [TodoStatus.Accepted]: "Accepterad",
  [TodoStatus.InProgress]: "Påbörjad",
  [TodoStatus.Done]: "Avslutad"
};

export const searchQualityCheckStatus: Record<SearchQualityCheckStatus, string> = {
  [SearchQualityCheckStatus.Empty]: "Tom",
  [SearchQualityCheckStatus.Booked]: "Bokad",
  [SearchQualityCheckStatus.Approved]: "Godkänd",
  [SearchQualityCheckStatus.ApprovedExpired]: "Godkänd - Förfallen",
  [SearchQualityCheckStatus.RequestSent]: "Förfrågan skickad",
  [SearchQualityCheckStatus.RequestSentExpired]: "Förfrågan skickad - Förfallen",
  [SearchQualityCheckStatus.Ordered]: "Förfrågan skickad till kvk",
  [SearchQualityCheckStatus.ConsultantResponsible]: "Konsult ansvarar själv",
  [SearchQualityCheckStatus.Expired]: "Förfallen",

  [SearchQualityCheckStatus.SigningPeriodExpired]: "Signeringperiod förfallen",
  [SearchQualityCheckStatus.RequestReceivedNotProcessed]:
    "Förfrågan mottagen - åtgärd krävs",
  [SearchQualityCheckStatus.ApprovedWithComment]: "Godkänd med kommentar",

  [SearchQualityCheckStatus.FullyApproved]: "Fullt godkänd",
  [SearchQualityCheckStatus.OneApproved]: "Minst en referens godkänd",
  [SearchQualityCheckStatus.NotApproved]: "Inga godkända",
  [SearchQualityCheckStatus.RequestNotAnswered]: "Ej svar - beställ på nytt om aktuellt",
  [SearchQualityCheckStatus.Paused]: "Pausat",

  [SearchQualityCheckStatus.RequestExpired]: "",
  [SearchQualityCheckStatus.AwaitingApproval]: "",
  [SearchQualityCheckStatus.SentToRef]: "",
  [SearchQualityCheckStatus.ApprovedRef]: "",
  [SearchQualityCheckStatus.NotApprovedRef]: ""
};

export const searchQualityCheckReferenceStatus: Record<string, string> = {
  [SearchQualityCheckStatus.FullyApproved]: "Fullt godkänd referenstagning (gruppstatus)",
  [SearchQualityCheckStatus.NotApproved]: "Ej godkänd referenstagning (gruppstatus)",
  [SearchQualityCheckStatus.Empty]: "Minst ett referensformulär med status “Ej beställd”",
  [SearchQualityCheckStatus.RequestSent]:
    "Minst ett referensformulär med status “Beställd”",
  [SearchQualityCheckStatus.RequestNotAnswered]:
    "Minst ett referensformulär med status “Ej svar - beställ på nytt om aktuellt”",
  [SearchQualityCheckStatus.SentToRef]:
    "Minst ett referensformulär med status “Skickat till ref”",
  [SearchQualityCheckStatus.RequestExpired]:
    "Minst ett referensformulär med status “Referensförfrågan förfallen”",
  [SearchQualityCheckStatus.AwaitingApproval]:
    "Minst ett referensformulär med status “Inväntar godkännande”",
  [SearchQualityCheckStatus.ApprovedRef]:
    "Minst ett referensformulär med status “Godkänd”",
  [SearchQualityCheckStatus.NotApprovedRef]:
    "Minst ett referensformulär med status “Ej Godkänd”"
};

export const searchQualityCheckReferenceNationalStatus: Record<string, string> = {
  [SearchQualityCheckStatus.FullyApproved]:
    "Fullt godkänd referenstagning för Nationella (gruppstatus)",
  [SearchQualityCheckStatus.NotApproved]:
    "Ej godkänd referenstagning Nationella (gruppstatus)",
  [SearchQualityCheckStatus.Empty]: "Minst ett referensformulär med status “Ej beställd”",
  [SearchQualityCheckStatus.RequestSent]:
    "Minst ett referensformulär med status “Beställd”",
  [SearchQualityCheckStatus.RequestNotAnswered]:
    "Minst ett referensformulär med status “Ej svar - beställ på nytt om aktuellt”",
  [SearchQualityCheckStatus.SentToRef]:
    "Minst ett referensformulär med status “Skickat till ref”",
  [SearchQualityCheckStatus.RequestExpired]:
    "Minst ett referensformulär med status “Referensförfrågan förfallen”",
  [SearchQualityCheckStatus.AwaitingApproval]:
    "Minst ett referensformulär med status “Inväntar godkännande”",
  [SearchQualityCheckStatus.ApprovedRef]:
    "Minst ett referensformulär med status “Godkänd”",
  [SearchQualityCheckStatus.NotApprovedRef]:
    "Minst ett referensformulär med status “Ej Godkänd”"
};

export const searchQualityCheckName: Record<string, string> = {
  [SearchSAHLRGroup.name]: "S/A HLR som grupp"
};

const travelRequestTime: Record<TravelRequestTime, string> = {
  [TravelRequestTime.Morning]: "Morgon",
  [TravelRequestTime.Day]: "Förmiddag",
  [TravelRequestTime.Noon]: "Eftermiddag",
  [TravelRequestTime.Evening]: "Kväll"
};

const accommodationType: Record<AccommodationType, string> = {
  [AccommodationType.Hotel]: "Hotell",
  [AccommodationType.Apartment]: "Lägenhet",
  [AccommodationType.CorporateApartment]: "Företagslägenhet",
  [AccommodationType.BedAndBreakfast]: "Bed and Breakfast",
  [AccommodationType.Hostels]: "Vandrarhem",
  [AccommodationType.Guest]: "Pensionat",
  [AccommodationType.StaffAccommodation]: "Personalbostad"
};

const accommodationRequestStatusType: Record<AccommodationRequestStatusType, string> = {
  [AccommodationRequestStatusType.Open]: "Öppen",
  [AccommodationRequestStatusType.Closed]: "Avslutad"
};

const travelRequestStatusType: Record<TravelRequestStatusType, string> = {
  [TravelRequestStatusType.Open]: "Öppen",
  [TravelRequestStatusType.Closed]: "Avslutad"
};

const accommodationPriceType: Record<AccommodationPriceType, string> = {
  [AccommodationPriceType.PerNight]: "kr/natt",
  [AccommodationPriceType.PerMonth]: "kr/månad",
  [AccommodationPriceType.PerWeek]: "kr/vecka"
};

const accommodationPeriodLabel: Record<AccommodationPriceType, string> = {
  [AccommodationPriceType.PerNight]: "Natt",
  [AccommodationPriceType.PerMonth]: "Månad",
  [AccommodationPriceType.PerWeek]: "Vecka"
};

const accommodationTravelStatus: Record<AccommodationTravelStatus, string> = {
  [AccommodationTravelStatus.NotRequired]: "Krävs inte",
  [AccommodationTravelStatus.Required]: "Nödvändig",
  [AccommodationTravelStatus.ApprovedManually]: "Godkänd manuellt",
  [AccommodationTravelStatus.Blank]: "Tom",
  [AccommodationTravelStatus.Requested]: "Beställd",
  [AccommodationTravelStatus.Confirmed]: "Bekräftad"
};

const accommodationOfferOptions: Record<AccommodationOfferOptions, string> = {
  [AccommodationOfferOptions.NoKitchen]: "Inget kök",
  [AccommodationOfferOptions.OwnKitchen]: "Eget kök",
  [AccommodationOfferOptions.SeparateBathroom]: "Eget badrum",
  [AccommodationOfferOptions.Breakfast]: "Frukost",
  [AccommodationOfferOptions.CleanOut]: "Slutstädning",
  [AccommodationOfferOptions.SharedKitchen]: "Delat kök",
  [AccommodationOfferOptions.SheetAndTowelsIncluded]: "Lakan och handdukar ingår",
  [AccommodationOfferOptions.Parking]: "Parkering",
  [AccommodationOfferOptions.Internet]: "Internet",
  [AccommodationOfferOptions.Pets]: "Husdjur",
  [AccommodationOfferOptions.Gym]: "Gym"
};

const accommodationSwitchesOptions: Record<AccommodationSwitches, string> = {
  [AccommodationSwitches.EnvCertification]: "Miljöcertifiering",
  [AccommodationSwitches.PreferredChoice]: "Förstahandsval"
};

const qualityStatuses = {
  Approved: "Godkänd",
  ApprovedWithRequestSent: "Godkänd",
  ManuallyApproved: "Godkänt undantag",
  RequestSent: "Förfrågan skickad",
  SendRequestIVO: "Skicka förfrågan till IVO",
  ApprovedWithComment: "Godkänd med kommentar",
  Answered: "Besvarad",
  AwaitingApproval: "Väntar på godkännande",
  RequestReceivedNotProcessed: "Förfrågan mottagen - åtgärd krävs",
  Ordered: "Förfrågan skickad till kvk",
  Booked: "Bokad",
  ConsultantResponsible: "Konsult ansvarar själv",
  SigningPeriodExpired: "Signeringperiod förfallen",
  NotApproved: "Ej godkänd",
  RequestNotAnswered: "Ej svar - beställ på nytt om aktuellt",
  Paused: "Pausat"
} as const;

const consultantContractCurrency: Record<ConsultantContractCurrency, string> = {
  [ConsultantContractCurrency.SEK]: "kr",
  // [ConsultantContractCurrency.NOK]: "nkr",
  [ConsultantContractCurrency.Percentage]: "%"
};

const consultantContractPensionSolution: {
  getLabel: (years: number) => string;
  getValues: (years: number) => Record<ConsultantContractPensionSolution, string>;
  warningText: string;
} = {
  getLabel: (years: number) =>
    `Konsulten fyller ${years} år under avtalstiden. Vill du erbjuda tjänstepension enligt nationella avtalet under hela avtalsperioden eller vill du dela avtalet i två?`,
  getValues: (years: number) => ({
    [ConsultantContractPensionSolution.Full]:
      "Jag vill erbjuda tjänstepension enligt nationella avtalet under hela avtalsperioden",
    [ConsultantContractPensionSolution.Split]: `Jag vill skapa två avtal (det innebär att konsulten för den period som hen är ${years} år får välja mellan våra löneval)`
  }),
  warningText:
    "Slutdatum för avtalet kan senast vara dagen innan konsulten fyller år. Glöm inte skapa ett nytt avtal för den efterföljande perioden."
};

const consultantGetsPaidPer: Record<ConsultantGetsPaidPer, string> = {
  [ConsultantGetsPaidPer.Investigation]: "Utredning",
  [ConsultantGetsPaidPer.Hour]: "Timme"
};

const accommodationPaymentMethods: Record<AccommodationPaymentMethod, string> = {
  [AccommodationPaymentMethod.BonlivaPays]: "Bonliva betalar",
  [AccommodationPaymentMethod.PayOnArrival]: "Betalas på plats",
  [AccommodationPaymentMethod.DeductFromSalary]: "Löneavdrag",
  [AccommodationPaymentMethod.InvoiceSubcontractor]: "Fakturera underkonsult"
};

const consultantContractTypes: Record<ConsultantContractType, string> = {
  [ConsultantContractType.Outsource]: "Uppdragsavtal",
  [ConsultantContractType.Outsource_National]: "Uppdragsavtal Nationella",
  [ConsultantContractType.Framework]: "Ramavtal",
  [ConsultantContractType.Framework_National]: "Ramavtal Nationella",
  [ConsultantContractType.BC_Framework]: "Ramavtal - BC",
  [ConsultantContractType.Confirmation]: "Bokningsbekräftelse",
  [ConsultantContractType.Confirmation_National]: "Bokningsbekräftelse Nationella",
  [ConsultantContractType.Employment]: "Anställningsavtal",
  [ConsultantContractType.Employment_National]: "Anställningsavtal Nationella",
  [ConsultantContractType.Intermittent]: "Intermittent",
  [ConsultantContractType.Intermittent_National]: "Intermittent Nationella",
  [ConsultantContractType.BC_Intermittent]: "Intermittent - BC",
  [ConsultantContractType.SelfInvoicing]: "Självfakturering",
  [ConsultantContractType.ConfirmationForEmployees]: "Bokningsbekräftelse löntagare",
  [ConsultantContractType.ConfirmationForEmployees_National]:
    "Bokningsbekräftelse löntagare Nationella",
  [ConsultantContractType.BC_Intermittent_Confirmation]:
    "Bokningsbekräftelse löntagare - BC",
  [ConsultantContractType.BC_Framework_Confirmation]: "Bokningsbekräftelse - BC"
};

const consultantReferenceStatus: Record<ConsultantReferenceStatus, string> = {
  [ConsultantReferenceStatus.Empty]: "Tömma",
  [ConsultantReferenceStatus.NotRequested]: "Ej beställd",
  [ConsultantReferenceStatus.Approved]: "Godkänd",
  [ConsultantReferenceStatus.ApprovedWithException]: "Godkänd undantag",
  [ConsultantReferenceStatus.NotApproved]: "Ej godkänd",
  [ConsultantReferenceStatus.RequestSentToKVK]: "Förfrågan skickad till KVK",
  [ConsultantReferenceStatus.RequestSentToReference]: "Förfrågan skickad till ref",
  [ConsultantReferenceStatus.RequestExpired]: "Begäran har löpt ut",
  [ConsultantReferenceStatus.AwaitingApproval]: "Väntar på godkännande",
  [ConsultantReferenceStatus.RequestNotAnswered]: "Ej svar - beställ på nytt om aktuellt"
};

const consultantReferenceType: Record<ConsultantReferenceType, string> = {
  [ConsultantReferenceType.Reference]: "Referens",
  [ConsultantReferenceType.Evaluation]: "Uppdragsutvärdering",
  [ConsultantReferenceType.EvaluationToThreeMonths]: "Uppdragsutvärdering 3 mån",
  [ConsultantReferenceType.UUCrossQuestions]: "UU Nationella Kryssfrågor"
};

const consultantReferenceRelation: Record<ConsultantReferenceRelation, string> = {
  [ConsultantReferenceRelation.CurrentBoss]: "Nuvarande chef",
  [ConsultantReferenceRelation.LatestBoss]: "Senaste chef",
  [ConsultantReferenceRelation.FormerBoss]: "Tidigare chef",
  [ConsultantReferenceRelation.CurrentColleague]: "Nuvarande kollega",
  [ConsultantReferenceRelation.FormerColleague]: "Tidigare kollega"
};

const workedInNorwayOption: Record<WorkedInNorwayOption, string> = {
  [WorkedInNorwayOption.All]: "Alla",
  [WorkedInNorwayOption.Yes]: "Ja",
  [WorkedInNorwayOption.YesWithDNumber]: "Ja (D-nummer i CRM)",
  [WorkedInNorwayOption.YesWithoutDNumber]: "Ja (Inget D-nummer i CRM)",
  [WorkedInNorwayOption.No]: "Nej"
};

const approvedAuthorizationOption: Record<ApprovedAuthorizationOption, string> = {
  [ApprovedAuthorizationOption.All]: "Alla",
  [ApprovedAuthorizationOption.Yes]: "Ja",
  // [ApprovedAuthorizationOption.YesWithDNumber]: "Ja (D-nummer i CRM)",
  // [ApprovedAuthorizationOption.YesWithoutDNumber]: "Ja (Inget D-nummer i CRM)",
  [ApprovedAuthorizationOption.No]: "Nej"
};

const consultantGDPROption: Record<GDPROptionType, string> = {
  [GDPROptionType.Yes]: "Godkänt",
  [GDPROptionType.No]: "Ej godkänt",
  [GDPROptionType.NotSpecified]: "Ej angett"
};

const consultantGDPRChannelShort: Record<
  Extract<
    GDPRCommunicationChannel,
    GDPRCommunicationChannel.Verbal | GDPRCommunicationChannel.Email
  >,
  string
> = {
  [GDPRCommunicationChannel.Verbal]: "Muntligen",
  [GDPRCommunicationChannel.Email]: "Email"
};

const consultantGDPRChannel: Record<GDPRCommunicationChannel, string> = {
  [GDPRCommunicationChannel.Verbal]: "Muntligen",
  [GDPRCommunicationChannel.Email]: "Email",
  [GDPRCommunicationChannel.Self]: "Jag själv",
  [GDPRCommunicationChannel.MobileApp]: "Mobilapp",
  [GDPRCommunicationChannel.InterestForm]: "Intresseformulär",
  [GDPRCommunicationChannel.UnfitAction]: "Olämplig",
  [GDPRCommunicationChannel.NotDefined]: "Vet ej"
};

const consultantUnfitType: Record<ConsultantUnfitType, string> = {
  [ConsultantUnfitType.Unfit]: "Olämplig",
  [ConsultantUnfitType.NotContactable]: "Vill ej bli kontaktad"
};

const currency: Record<Currency, string> = {
  [Currency.SEK]: "kr",
  [Currency.NOK]: "nkr"
};

const consultantContractPeriod: Record<ConsultantContractPeriod, string> = {
  [ConsultantContractPeriod.Day]: "dag",
  [ConsultantContractPeriod.Week]: "vecka",
  [ConsultantContractPeriod.Month]: "månad",
  [ConsultantContractPeriod.WorkShift]: "pass"
};

const consultantContractTravelsPeriod: Record<ConsultantContractTravelsPeriod, string> = {
  [ConsultantContractTravelsPeriod.Day]: "dag",
  [ConsultantContractTravelsPeriod.Week]: "vecka",
  [ConsultantContractTravelsPeriod.Month]: "månad",
  [ConsultantContractTravelsPeriod.Mile]: "mil",
  [ConsultantContractTravelsPeriod.WorkShift]: "pass",
  [ConsultantContractTravelsPeriod.PerTravel]: "resa"
};

const consultantContractSalaryType: Record<ConsultantContractSalaryType, string> = {
  [ConsultantContractSalaryType.GrossSalary]: "bruttolön",
  [ConsultantContractSalaryType.NetSalary]: "nettolön"
};

const consultantContractPayoutType: Record<ConsultantContractPayoutType, string> = {
  [ConsultantContractPayoutType.Weekly]: "Veckovis utbetalning",
  [ConsultantContractPayoutType.Monthly]: "Månadsvis utbetalning"
};

const bookingKitPayoutType: Record<ConsultantContractPayoutType, string> = {
  [ConsultantContractPayoutType.Weekly]: "Veckolön",
  [ConsultantContractPayoutType.Monthly]: "Månadslön"
};

const consultantContractOvertimeOpt: Record<ConsultantContractOvertimeOpt, string> = {
  [ConsultantContractOvertimeOpt.NoSpecialOvertime]:
    "Särskild ersättning för obekväm arbetstid utgår inte utan har beaktats vid bestämmande av ersättningarna ovan.",
  [ConsultantContractOvertimeOpt.SKLBasedOvertime]:
    "OB-ersättning matchar faktorer vid var tid gällande SKR:s Allmänna Bestämmelser (AB)."
};

const consultantContractAccommodationAlt: Record<
  ConsultantContractAccommodationAlt,
  string
> = {
  [ConsultantContractAccommodationAlt.BonlivaPayAccommodation]: "Bonliva bekostar boende",
  [ConsultantContractAccommodationAlt.BonlivaPayAccommodationAllowance]:
    "Bonliva utbetalar boendeersättning",
  [ConsultantContractAccommodationAlt.BonlivaPayAccommodationAllowanceAgainstReceipt]:
    "Bonliva utbetalar boendeersättning mot kvitto",
  [ConsultantContractAccommodationAlt.SupplierPayAccommodation]:
    "Leverantören står själv för boendekostnader"
};

const consultantContractBonusType: Record<ConsultantContractBonusType, string> = {
  [ConsultantContractBonusType.GrossSalaryBonus]: "Bonus kopplad till tjänstgöring",
  [ConsultantContractBonusType.OtherBonus]: "Bonus ej kopplad tilltjänstgöring"
};

const consultantContractTravelsAlt: Record<ConsultantContractTravelsAlt, string> = {
  [ConsultantContractTravelsAlt.BonlivaPayTravel]: "Bonliva bokar/bekostar resor",
  [ConsultantContractTravelsAlt.BonlivaPayTravelAllowance]:
    "Bonliva utbetalar reseersättning",
  [ConsultantContractTravelsAlt.BonlivaPayTravelAllowanceAgainstReceipt]:
    "Bonliva utbetalar reseersättning mot kvitto",
  [ConsultantContractTravelsAlt.SupplierPayTravel]:
    "Leverantören står själv för resekostnader",
  [ConsultantContractTravelsAlt.ConsultantTaxCompensationAllowance]:
    "Konsult har rätt till skattepliktig milersättning",
  [ConsultantContractTravelsAlt.TravelTemplate]: "Reseschablon"
};

const consultantContractPriceType: Record<ConsultantContractPriceType, string> = {
  [ConsultantContractPriceType.PerHour]: "/h",
  [ConsultantContractPriceType.PerShift]: "per jour",
  [ConsultantContractPriceType.PerEmergencyCall]: "per beredskapsjour",
  [ConsultantContractPriceType.PerSurgery]: "per operation",
  [ConsultantContractPriceType.PerInquiry]: "per utredning",
  [ConsultantContractPriceType.PerScreening]: "per undersökning",
  [ConsultantContractPriceType.PerDay]: "per dag",
  [ConsultantContractPriceType.PerWeek]: "per vecka",
  [ConsultantContractPriceType.PerMonth]: "per månad",
  [ConsultantContractPriceType.OfRevenue]: "av intäkter"
};

const consultantContractSelfInvoicingCheck: Record<
  ConsultantContractSelfInvoicingCheck,
  string
> = {
  [ConsultantContractSelfInvoicingCheck.MustBeSubcontractor]:
    "Måste vara underleverantör",
  [ConsultantContractSelfInvoicingCheck.MustHaveResponsiblePerson]:
    "Måste ha en ansvarig person",
  [ConsultantContractSelfInvoicingCheck.MustHaveEmail]: "Måste ha e-postadress",
  [ConsultantContractSelfInvoicingCheck.MustHaveSSN]: "Måste ha personnummer",
  [ConsultantContractSelfInvoicingCheck.MustHaveBusinessName]: "Måste ha företagsnamn",
  [ConsultantContractSelfInvoicingCheck.MustHaveBusinessSSN]:
    "Måste ha företagspersonnummer",
  [ConsultantContractSelfInvoicingCheck.MustHaveBusinessAddress]:
    "Måste ha företagsadress",
  [ConsultantContractSelfInvoicingCheck.MustBeSwedishResident]:
    "Måste vara bosatt i Sverige",
  [ConsultantContractSelfInvoicingCheck.MustHaveFTaxChecked]: "Måste ha f-skatt godkänd",
  [ConsultantContractSelfInvoicingCheck.MustHaveVATChecked]:
    "Måste ha momsregistrering godkänd"
};

const rideType: Record<RideType, string> = {
  [RideType.OneWay]: "Enkel",
  [RideType.RoundTrip]: "Tur och Retur"
};

const luggageType: Record<LuggageType, string> = {
  [LuggageType.Handluggage]: "Handbagage",
  [LuggageType.HandluggagePlusChecked]: "Handbagage + incheckat bagage"
};

const transportationType: Record<TransportationType, string> = {
  [TransportationType.Train]: "Tåg",
  [TransportationType.Flight]: "Flyg",
  [TransportationType.Bus]: "Buss",
  [TransportationType.Boat]: "Båt",
  [TransportationType.Taxi]: "Taxi",
  [TransportationType.Any]: "Valfri"
};

const timeReportType: Record<TimeReportType, string> = {
  [TimeReportType.Common]: "Ordinarie tid",
  [TimeReportType.OnCall]: "Jour",
  [TimeReportType.PassiveOnCall]: "Passiv tid jour",
  [TimeReportType.PreparednessA]: "Beredskap A",
  [TimeReportType.ActivePreparednessAPeriod]: "Aktiv tid, Beredskap A",
  [TimeReportType.PreparednessB]: "Beredskap B",
  [TimeReportType.ActivePreparednessBPeriod]: "Aktiv tid, Beredskap B",
  [TimeReportType.SickDay]: "Sjuk",
  [TimeReportType.VabDay]: "VAB"
};

const lastContactType: Record<
  Exclude<LastContactType, LastContactType.Reminder>,
  string
> = {
  [LastContactType.Comment]: "Anteckning",
  [LastContactType.Email]: "Email",
  [LastContactType.MetInPerson]: "Personligt möte",
  [LastContactType.Registered]: "Registrering",
  [LastContactType.SMS]: "SMS",
  [LastContactType.Noanswer]: "Inget Svar",
  [LastContactType.NoContactAttempts]: "Inga kontaktförsök",
  [LastContactType.NoCallAttempts]: "Inga samtalsförsök",
  [LastContactType.NoComment]: "Ingen Anteckning",
  [LastContactType.Interest]: "Intresseanmälan",
  [LastContactType.Call]: "Samtal",
  [LastContactType.NoCall]: "Inga samtal"
};

const legalBasisType: Record<LegalBasis, string> = {
  [LegalBasis.Consent]: "Samtycke",
  [LegalBasis.InterestAssessment]: "Intresseavvägning",
  [LegalBasis.Agreement]: "Avtal"
};

const gdprConsentType: Record<GDPRConsentType, string> = {
  [GDPRConsentType.IntegrationPolicy]: "Integritetspolicy",
  [GDPRConsentType.Marketing]: "Marknadsföring",
  [GDPRConsentType.JobMailing]: "Behovsutskick",
  [GDPRConsentType.JobMailingEmail]: "Behovsutskick via Email",
  [GDPRConsentType.JobMailingSms]: "Behovsutskick via SMS",
  [GDPRConsentType.JobMailingCampaignEmail]: "Behovskampanj via Email"
};

const todoPriorityType: Record<string, string> = {
  NoStatus: "Ingen prioritet",
  Low: "Låg prioritet",
  High: "Hög prioritet"
};

const smsDeliveryStatusType: Record<SMSDeliveryStatus, string> = {
  [SMSDeliveryStatus.Pending]: "Skickas",
  [SMSDeliveryStatus.Delivered]: "Levererad",
  [SMSDeliveryStatus.Expired]: "Kunde ej skickas",
  [SMSDeliveryStatus.Error]: "Kunde ej skickas",
  [SMSDeliveryStatus.Rejected]: "Kunde ej skickas",
  [SMSDeliveryStatus.Unknown]: "Kunde ej skickas"
};

const consultantTableColumnType: Record<ConsultantTableColumns, string> = {
  [ConsultantTableColumns.Address]: "Ort",
  [ConsultantTableColumns.AreasOfExpertise]: "Område",
  [ConsultantTableColumns.Availability]: "Tillgänglighet",
  [ConsultantTableColumns.AvailableForWorkIn]: "Kan arbeta i",
  [ConsultantTableColumns.Comments]: "Kommentarer",
  [ConsultantTableColumns.Email]: "Epost",
  [ConsultantTableColumns.GDPRIntegrationPolicy]: "GDPR: Integritetspolicy",
  [ConsultantTableColumns.GDPRJobMailing]: "GDPR: Behovsutskick",
  [ConsultantTableColumns.GDPRLegalBasis]: "GDPR: Laglig Grund",
  [ConsultantTableColumns.GDPRMarketing]: "GDPR: Marknadsföring",
  [ConsultantTableColumns.HOSP]: "Sverige: HOSP",
  [ConsultantTableColumns.HasDrivingLicense]: "Har körkort",
  [ConsultantTableColumns.IVOErrands]: "Sverige: IVO-ärenden",
  [ConsultantTableColumns.IVO]: "Sverige: IVO",
  [ConsultantTableColumns.InterestedToWorkIn]: "Intresserad av arbete i",
  [ConsultantTableColumns.IsNationalQualityApproved]: "Kvalitetssäkring Nationella",
  [ConsultantTableColumns.IsQualityApproved]: "Kvalitetssäkring",
  [ConsultantTableColumns.LastContactDate]: "Senaste kontakt",
  [ConsultantTableColumns.Machines]: "Maskinfabrikat",
  [ConsultantTableColumns.MethodsAndInstruments]: "Metoder/Instrument",
  [ConsultantTableColumns.Name]: "Namn",
  [ConsultantTableColumns.OtherEducations]: "Övriga Utbildningar",
  [ConsultantTableColumns.Phone]: "Telefon",
  [ConsultantTableColumns.ResponsiblePerson]: "Ansvarig HBA",
  [ConsultantTableColumns.SocialSecurityNo]: "Personnummer",
  [ConsultantTableColumns.Specializations]: "Nisch",
  [ConsultantTableColumns.SwedenBusinessIntelligence]: "Omvärldsbevakning",
  [ConsultantTableColumns.SwedenCVAudit]: "CV-granskning",
  [ConsultantTableColumns.SwedenCriminalRecord]: "Belastningsregister",
  [ConsultantTableColumns.SwedenHLR]: "HLR",
  [ConsultantTableColumns.SwedenHSAIDSITHS]: "HSAID/SITHS",
  [ConsultantTableColumns.SwedenHealthDeclaration]: "Hälsodeklaration",
  [ConsultantTableColumns.SwedenIdCheck]: "ID-kontroll",
  [ConsultantTableColumns.SwedenInterviewDone]: "Intervju genomförd",
  [ConsultantTableColumns.SwedenLanguageCheck]: "Språkkontroll",
  [ConsultantTableColumns.SwedenNationalReferences]: "Referenser Nationella",
  [ConsultantTableColumns.SwedenNpCriminalRecord]: "Belastningsregister Nationella",
  [ConsultantTableColumns.SwedenProofOfEducation]: "Utbildningsbevis",
  [ConsultantTableColumns.SwedenProofOfWork]: "Yrkesbevis",
  [ConsultantTableColumns.SwedenReferences]: "Referenser",
  [ConsultantTableColumns.Systems]: "Systemkunskaper",
  [ConsultantTableColumns.WorksForCompetitors]: "Arbetat via konkurrent",
  [ConsultantTableColumns.SwedenSAHLR]: "S/A HLR",
  [ConsultantTableColumns.CallListStats]: "Ringlista",
  [ConsultantTableColumns.Tags]: "Taggar",
  [ConsultantTableColumns.NextContactDate]: "Nästa kontakt",
  [ConsultantTableColumns.PipelineStatus]: "Pipeline status",
  [ConsultantTableColumns.BookingStatus]: "Bokningsstatus",
  [ConsultantTableColumns.QualityNote]: "Kvalitetsanteckningar",
  [ConsultantTableColumns.CreatedAt]: "Skapad",
  [ConsultantTableColumns.UpdatedAt]: "Uppdaterad"
};

const kitTableColumnType: Record<BookingKitTableColumns, string> = {
  [BookingKitTableColumns.Week]: "Vecka",
  [BookingKitTableColumns.Assigned]: "Tilldelad",
  [BookingKitTableColumns.Consultant]: "Konsult",
  [BookingKitTableColumns.Customer]: "Kund",
  [BookingKitTableColumns.IsNationalProcurement]: "Nationella",
  [BookingKitTableColumns.Aoe]: "Område",
  [BookingKitTableColumns.CostCenter]: "Kst",
  [BookingKitTableColumns.Project]: "Projekt",
  [BookingKitTableColumns.Alt]: "Alt",
  [BookingKitTableColumns.KitStatus]: "Underlagsstatus",
  [BookingKitTableColumns.LastEvent]: "Senaste händelse",
  [BookingKitTableColumns.ManagementStatus]: "Hanteringsstatus",
  [BookingKitTableColumns.Type]: "Typ",
  [BookingKitTableColumns.ConsultantPayoutType]: "Utbetalning",
  [BookingKitTableColumns.TimereportApprovedOn]: "Godkänd av kunden"
};

const accommodationRequestTableColumnType: Record<
  AccommodationRequestTableColumns,
  string
> = {
  [AccommodationRequestTableColumns.Week]: "Vecka",
  [AccommodationRequestTableColumns.Consultant]: "Konsult",
  [AccommodationRequestTableColumns.City]: "Stad",
  [AccommodationRequestTableColumns.ArrivalDate]: "Första incheckning",
  [AccommodationRequestTableColumns.Sender]: "Beställare",
  [AccommodationRequestTableColumns.Status]: "Status",
  [AccommodationRequestTableColumns.CreatedOn]: "Skapad"
};

const travelRequestTableColumns: Record<TravelRequestTableColumns, string> = {
  [TravelRequestTableColumns.Week]: "Vecka",
  [TravelRequestTableColumns.Consultant]: "Konsult",
  [TravelRequestTableColumns.City]: "Resväg",
  [TravelRequestTableColumns.DepartureDate]: "Avresedag",
  [TravelRequestTableColumns.Sender]: "Beställare",
  [TravelRequestTableColumns.Status]: "Status",
  [TravelRequestTableColumns.CreatedOn]: "Skapad"
};

const bulkActionSteps = {
  selection: (entity: string) => `Välj ${entity.toLowerCase()}`,
  action: {
    [BulkActionType.ConsultantEmail]: "Skriv Email",
    [BulkActionType.ConsultantReassignBA]: "Uppdatera ansvarig HBA",
    [BulkActionType.ConsultantSMS]: "Skriv SMS",
    [BulkActionType.ConsultantAssignCallList]: "Välj ring- och kontaktlistor",
    [BulkActionType.ConsultantAssignTags]: "Välj taggar",
    [BulkActionType.ConsultantDelete]: "Radera konsulter",
    [BulkActionType.ConsultantOrderQualityDocuments]: "Välj kvalitetsdokument"
  },
  confirmation: "Bekräfta"
};

const bulkActionType: Record<BulkActionType, string> = {
  [BulkActionType.ConsultantEmail]: "Email",
  [BulkActionType.ConsultantReassignBA]: "ansvarig HBA",
  [BulkActionType.ConsultantSMS]: "SMS",
  [BulkActionType.ConsultantAssignCallList]: "ring- och kontaktlistor",
  [BulkActionType.ConsultantAssignTags]: "taggar",
  [BulkActionType.ConsultantDelete]: "radering av konsulter",
  [BulkActionType.ConsultantExport]: "",
  [BulkActionType.ConsultantExportSsn]: "",
  [BulkActionType.ConsultantOrderQualityDocuments]: "kvalitetsdokument"
};

const bulkActionSubType: Record<BulkActionCommunicationSubType, string> = {
  Information: "Informationsutskick",
  JobMailing: "Behovsutskick",
  Marketing: "Marknadsföringsutskick"
};

const qualityDocumentOrderType: Record<QualityDocumentOrderType, string> = {
  [QualityDocumentOrderType.HOSP]: "HOSP",
  [QualityDocumentOrderType.IVO]: "IVO",
  [QualityDocumentOrderType.BelastningsregisterNationella]:
    "Belastningsregister Nationella"
};

const nextContactDateReason: Record<NextContactDateReason, string> = {
  WorksForCompetitor: "Arbetar för konkurrent",
  PermanentEmployment: "Fast tjänst",
  Other: "Övrig"
};

const cardTypes: Record<CardType, string> = {
  [CardType.OrdinaryCardBonliva]: "Ordinarie kort via Bonliva",
  [CardType.ReserveCardBonliva]: "Reservkort via Bonliva",
  [CardType.OrdinaryCardWCC]: "Ordinarie kort via WCC",
  [CardType.ReserveCardWCC]: "Reservkort via WCC",
  [CardType.OtherCard]: "Annat kort"
};

const svLocale = {
  General: "Allmänt",
  PriceList: "AvtalsArtiklar",
  PriceListDiffFromCustomerTemplate:
    "För information: Helgdagar eller avtalskategorier skiljer sig mellan konsultavtal och kundavtal",
  NormalHoliday: "Helgdagar",
  NormalHoliday2: "Helgdag",
  SpecificHighHoliday: "Specifika helgdagar",
  SpecificDaily: "Jag vill specifficera veckodagar",
  SpecificFullPeriod: "Jag vill lägga in en period",
  GreatHoliday: "Storhelg",
  NewRow: "NY RAD",
  Detail: "Detalj",
  AddPriceBlock: "LÄGG TILL PRISBLOCK",
  PriceBlock: "PRISBLOCK",
  Prices: "Priser",
  PriceListItem: "Avtalsartikel",
  Template: "Bolagsmall",
  FavTemplate: "Favoritmall",
  CustomerTemplate: "Kundavtalsmall",
  ProfessionsExpertise: "Yrkestitel & Område",
  ConnectedCustomer: "Kopplade kunder",
  Customer: "Kund",
  SearchableCustomer: "Sökbar kund + ev avdelning",
  StatusDateBooking: "Datum för bokningsstatus",
  Sticky: "Fäst",
  AddRemoveFromPool: "Bearbetas",
  Pool: "Bearbetas",
  AvailableConsultants: "Tillgängliga Konsulter",
  Year: "År",
  Month: "Månad",
  Day: "Dagtid", // move these six into a separate Record
  Evening: "Kväll",
  Holiday: "Storhelg",
  Night: "Natt",
  Work: "Ordinarie tid",
  OnCall: "Jour",
  Weekend: "Helg",
  From: "Från",
  FromAcc: "Incheckning",
  FromTravel: "Utresa",
  by: "av",
  To: "Till",
  On: "På",
  In: "I",
  ToAcc: "Utcheckning",
  ToTravel: "Hemresa",
  Type: "Typ",
  SendEmail: "Skicka Email",
  Configure: "Sammanställ",
  RepeatOnWeekDays: "Samma arbetstid flera dagar",
  RepeatOnWeekDaysAccForm: "Välj de dagar konsulten kommer behöva boendet",
  RepeatOnWeekDaysExample:
    "Exempel: om konsulten ska checka in på söndagen och checka ut på fredagen, välj samtliga dagar i spannet sön-fre",
  AddTimeRecord: "Lägg till tidsrekord",
  Confirm: "Bekräfta",
  SendToFortnox: "Skicka till Fortnox?",
  Send: "Skicka",
  SendToKvkWithoutEmail: "Skicka till kvk",
  SendToKvkWithEmail: "Skicka till konsult och kvk",
  SendAs: "Skicka som",
  Subject: "Ämne",
  UnlockForms: "Lås upp formulär",
  NoSearchResults: "Denna sökning gav inget resultat",
  Results: "Resultat",
  NoContracts: "Inga avtal",
  NoMoreSearchResults: "Inga fler sökresultat",
  MakeSearchable: "Gör sökbar",
  MakeExperienceSearchable:
    "Lägg till verksamhet och avdelning för att göra erfarenheten sökbar i konsultsöket.",
  municipal: "kommunal",
  municipalities: "Kommun",
  county: "landsting",
  private: "privat aktör",
  privateTemplate: "privat mall",
  government: "statlig myndighet",
  CONTRACT_OUT: "avtal kund",
  CONTRACT_IN: "avtal konsult",
  TRAVEL_BOOKINGS: "boende",
  HOTEL_BOOKINGS: "resa",
  TYPE_1: "typ 1",
  TYPE_2: "typ 2",
  TYPE_3: "typ 3",
  Days: "Dagar",
  Week: "Vecka",
  WeekFrom: "Från vecka",
  WeekTo: "Till vecka",
  Weeks: "Veckor",
  Consultant: "Konsult",
  ConsultantName: "Konsult namn",
  TheConsultant: "Konsulten",
  NewConsultant: "Ny konsult",
  Consultants: "Konsulter",
  ExpertiseWithDots: "Område:",
  ConsultantImport: "Konsult import",
  Consultation: "Konsultation",
  Expertise: "Område",
  Senior: "Pensionär",
  Specialization: "Nisch",
  Specializations: "Nischer",
  SpecializationsRequirements:
    "Krav på erfarenhet (Om det finns krav på specifik erfarenhet, välj dem här)",
  WeekDays: {
    Monday: "Måndag",
    Tuesday: "Tisdag",
    Wednesday: "Onsdag",
    Thursday: "Torsdag",
    Friday: "Fredag",
    Saturday: "Lördag",
    Sunday: "Söndag"
  },
  Description: "Beskrivning",
  invoiceMarking: "Fakturamärkning",
  UnbookedInvoicesCreatedJob:
    "Vi skapar upp din verifikation. Du kommer bli meddelad via email när det är klart. Du kan stänga detta fönster.",
  Competitors: "Konkurrenter",
  ConsultingCompanyLabel: "Konsultbolag",
  Country: "Land",
  CountryInWhichTheConsultantWillWork: "Land som konsulten ska arbeta i",
  CanWorkInRegion: "Intresserad av arbete i",
  Region: "Region",
  Municipality: "Kommun",
  "Region customer": "Organisation kund",
  Organisation: "Organisation",
  ExpDate: "Utgår, datum",
  Exp: "Utgår",
  RegionCustomer: "Region/Kund",
  HighlyRecommended: "Rekommenderas verkligen!",
  Recommended: "Rekommenderas",
  DoubtfulRecommended: "Tveksamt om personen rekommenderas",
  NotRecommended: "Rekommenderas inte",
  HighlyNotRecommended: "Rekommenderas starkt inte!",
  VeryRecommended: "Rekommenderas verkligen!",
  Doubtful: "Tveksamt om personen rekommenderas",
  NotImplemented: "Ej implementerad",
  City: "Ort",
  city2: "Stad",
  Name: "Namn",
  Relationship: "Relation",
  Team: "Team",
  Title: "Titel",
  Mobile: "Mobil",
  Roles: "Roller",
  Vacancies: "Vakanser",
  Vacancies2: "Antal Platser",
  Optional: "Valfritt",
  Economy: "Ekonomi",
  Information: "Information",
  Duration: "Varaktighet",
  Continuous: "Kontinuerligt behov",
  Agreement: "Enligt ö.k",
  CustomText: "Fritext",
  CompanyName: "Företagsnamn",
  "1st social secretary": "1e socialsekreterare",
  Reception: "Mottagning",
  "Socionom then": "Socionom sedan",

  "Interested in": "Generellt intresse",
  InterestedIn: "Intresserad av",
  AddContactPerson: (to?) =>
    to ? `Lägg till ny kontaktperson: ${to}` : "Lägg till ny kontaktperson",
  AddMoreCustomers: "Mer kunder",
  "Select correct type": "Välj korrekt typ",
  Select: "Välj",
  Selected: "Vald",
  notSelected: "Ej valt",
  "Value should be in Date format": "Behöver vara i datum-format",
  "Value should be in Money format": "Behöver vara i valuta-format",
  "Team and expertise": "Team och område",
  "Contract type": "Anställningsavtal",
  "Invoice prices": "Fakturapriser",
  "only number": "endast nummer",
  "This field is required": "Detta fält är obligatoriskt",
  "Application deadline": "Sista ansökningsdag",
  "Personal information": "Personlig information",
  "Personal number": "Personnummer",
  "Personal number format": "ÅÅÅÅMMDD-NNNN",
  "Birth Date": "Födelsedatum",
  "Can work in": "Kan arbeta i",
  "First comment": "Första kommentar",
  "ZIP code": "Postnummer",
  "Street address": "Gatuadress",
  "Phone number": "Telefonnummer",
  "CellPhone number": "Mobilnummer",
  "Preferable communication": "Föredragen kommunikation",
  InitialContact: "Kom i kontakt via",
  fillSelfRegisterOption: "Fyll i intresseanmälan",
  NoContacts: "Inga kontakter",
  "Add contact": "Lägg till kontakt",
  "Responsible BA": "Ansvarig BA",
  "Responsible HBA": "Ansvarig HBA",
  resetResponsibleHBA: "Rensa Ansvarig HBA",
  AllProjectsFor: "Samtliga projekt för",
  ResponsibleHousingAgent: "Ansvarig boendeagent",
  Responsible: "Ansvarig",
  ResponsibleBAisMissingSendDocs:
    "Ansvarig BA saknas. Lägg till för att kunna skicka underlag",
  SelectContacts: "Välj kontakter",
  Profitability: "TB-uträkning",
  Profession: "Yrkestitel",
  Psychologist: "Psykolog",
  SalaryChoice: "Löneval",
  ExpectedSalary: "Förväntad lön",
  Salary: "Lön",
  UploadedFilesShownHere:
    "Uppladdade filer är de filer som laddats upp här. Intresserad av kvalitetssäkringsfiler? Du hittar dem",
  Here: "här",
  ProfessionCategory: "Yrkeskategori",
  Project: "Projekt",
  AllChecksPass: "Alla test godkända",
  "Known systems": "Systemkunskaper",
  Machines: "Maskinfabrikat",
  Matching: "Matchningar",
  Educations: "Övriga utbildningar",
  Methods: "Metoder/Instrument",
  pipelineStatus: "Pipeline status",
  changePipelineStatus: "Ändra pipeline status",
  "Works for competitors": "Arbetar för konkurrenter",
  "Works via competitors": "Arbetat via konkurrent",
  "Organisation number": "Organisationsnummer",
  "Organisation number short": "Organisationsnr",
  FTaxChecked: "F-skatt godkänd?",
  VATChecked: "Momsregistrering godkänd?",
  "Address details": "Adressuppgifter",
  "Contact details": "Kontaktuppgifter",
  "Customer type": "Kundtyp",
  "Customer name": "Kundens namn",
  "Primary contact": "Primär Kontakt",
  "Other number": "Annat nummer",
  "Invoice price": "Fakturapris",
  LastApplicationDate: "Sista ansökningsdatum",
  ASAP: "Först till kvarn",
  "Description of the assignment": "Beskrivning av uppdraget",
  Qualifications: "Kvalifikationer",
  "Assigned to": "Tilldelad till",
  "Social security numbers do not match. Do you want to continue anyway?":
    "Personnummer matchar inte. Vill du fortsätta ändå?",
  "Surnames do not match. Do you want to continue anyway?":
    "Efternamn matchar inte. Vill du fortsätta ändå?",
  AssignTo: "Tilldela till",
  ScheduleReschedule: "Boka/boka om",
  NoPermissions: "Saknar behörighet",
  LinkTo: "Koppla till",
  "End date": "Slutdatum",
  "Update todo": "Uppdatera påminnelse",
  "Delete todo?": "Radera påminnelse",
  "Create a new todo": "Skapa ny påminnelse",
  "Team information": "Teamuppgifter",
  "Customer details": "Kunduppgifter",
  "Contact persons": "Kontaktpersoner",
  "Contact person": "Kontaktperson",
  ContactPersonName: "Kontaktperson Namn",
  ContactPersonTitle: "Kontaktperson Titel",
  ContactPersonPhone: "Kontaktperson Tfn",
  ContactPersonEmail: "Kontaktperson Mail",
  ContactPersonDeassigned: "Contact person deassigned",
  DeleteContactPersonDesc: (name) =>
    `Du tar nu bort kontaktperson '${name}' från alla kunder. Kontaktpersonen kommer då raderas`,
  ContactPersonDeleted: `Kontaktperson raderad`,
  ConfirmLeaveTitle: "Vill du avsluta?",
  ConfirmLeaveText: "Avsluta kommer ångra alla osparade ändringar, vill du avsluta?",
  "Can contain only numbers": "Kan endast innehålla siffror",
  "should match +xxxxxxxxxx": "ska matcha +xxxxxxxxxx",
  "Can contain only numbers, XXXXXX-XXXX": "Kan endast innehålla siffror, XXXXXX-XXXX",
  "Can contain only letters": "Kan endast innehålla bokstäver",
  "Can contain only numbers or letters": "Kan endast innehålla siffror eller bokstäver",
  "Can contain only numbers or letters and hyphen character":
    "Kan endast innehålla siffror, bokstäver eller bindestreck.",
  "Invalid email address": "Ogiltig mailadress",
  "Is not private email": "Vänligen ange en privat mailadress till konsulten.",
  "Valid email address": "Giltig mailadress",
  "Invalid cellphone": "Ogiltigt mobilnummer",
  "Valid cellphone": "Giltigt mobilnummer",
  entityHasMissingOrInvalidCellphone: (entity: string) =>
    `${entity} saknar eller har ogiltigt mobilnummer`,

  "To should be greater than From": "Behöver vara större än från värdet",
  FillSomething: (something: string): string => `Fyll i ${something}`,
  FillCorrect: (something: string): string => `Fyll i korrekt ${something}`,
  SelectSomething: (something: string): string => `${something}`,
  "Can / Do not want to work with": (something: string): string =>
    `Kan/Vill ej arbeta med ${something}`,
  "Call your consultants!": "Ring dina konsulter!",
  "Employment type": "Samarbetsform",
  "ICE number": "Nödnummer till anhörig",
  NationalProcurement: "Nationella upphandlingen",
  hasBookingKit: "Har kit",
  hasBookingKitMissingCompleteAndAccruedCost: "Upplupen kostnad",
  hasBookingKitMissingCompleteAndAccruedIncome: "Upplupen intäkt",
  hasAccommodationBenefitTax: "Har Förmån boende",
  NationalProcurementShort: "Nationella",
  GroupBy: "Gruppera på",
  Staffing: "Bemanning",
  None: "Inget",
  Customers: "Kunder",
  momentLocaleName: "sv",
  dateFnsLocale: sv,
  User: "Användare",
  TheUser: "Användaren",
  ConsultantSalary: "Lön",
  Approved: "Godkänd",
  ApprovedBy: "Godkänd av",
  ApprovedWithComment: "Godkänd med kommentar",
  Approve: "Godkänn",
  ApproveOC: "Slutattest OC",
  ApproveHBA: "Förattest HBA",
  Rate: "Generell Skattesats",
  ApproveRecruitmentFees: "Godkänn rekryteringsavgifter",
  TotalExcludeVAT: "UL - lägg in summa ex moms",
  noNeedApproval: " Stående attest finns från kund",
  noApprovalMonthly: " Attest behövs ej, Heroma-rapport månadsvis",
  noApprovalShortening: " Attest behövs ej, kökortningsuppdrag",
  noApprovalSpecial: " Attest behövs ej, särskilt upplägg från kund",
  needSignature: " Kräver attest på papper",
  needSignatureTROkInApp: "Attest på papper, rapportering ok i appen",
  Expires: "Förfaller",
  HasExpired: "Förfallen",
  Expired: "Förföll",
  ExpiresFromNow: "Löper ut från nu",
  DateMissing: "datum saknas",
  UnfitConsultant: "Ska ej kontaktas",
  UnfitConsultantObs: "OBS! Konsulten ska ej kontaktas",
  UnfitConsultantError:
    "Konsulten är markerad som “Ska ej kontaktas” och kan inte läggas till i ring- eller kontaktlista.",
  MarkedConsultantAsUnfit: (comment) => `Markerade konsulten som olämplig: ${comment}`,
  ChangedUserSettings: (setting: string, value: boolean) =>
    `Ändrad användarinställning "${setting}" till "${value ? "Ja" : "Nej"}"`,
  ChangedWelcomeEmail: (value: boolean) =>
    `har slagit ${value ? "på" : "av"} Välkomstmail`,
  MarkedConsultantAsFit: "Markerade konsulten som lämplig",
  UnfitConsultants: "Olämpliga för arbete",
  NotContactableConsultants: "Vill ej bli kontaktad",
  FitConsultants: "Kan kontaktas",
  AllConsultants: "Oavsett lämplighet",
  NoSSNText: "Personnummer krävs för uppladdning av filer",
  missingSSNForOrdering: "Lägg till personnummer för att kunna beställa.",
  NorwaySnnIsWorked:
    "Klicka i denna checkbox om konsulten har arbetat i Norge och redan har Norskt personnummer/D-nummer",
  NorwayCommentAuthorization: "Klicka i denna checkbox om konsulten har godkänd turnus",
  WorkedInNorway: "Jobbat i Norge",
  Fitness: "Kontaktstatus",
  Comment: "Kommentar",
  Save: "Spara",
  SaveSearchProfile: "Spara sökprofil",
  UpdateSearchProfile: "Uppdatera sökprofil",
  deleteProfile: "Radera profil",
  deleteProfileConfirm: "Vill du radera profil?",
  SaveAndCreateTicket: "Spara och skapa ticket",
  SaveAndCreateEmail: "Spara och skapa email",
  AddTicket: "Skapa ticket",
  AddEmail: "Skapa email",
  Reset: "Återställ",
  Saving: "Sparar...",
  Saved: "Sparat",
  Unsaved: "Ej sparat",
  Merge: "Slå samman",
  Order: "Beställ",
  OrderUpd: "Beställ uppd.",
  Ordered: "Beställd",
  Error: "Fel",
  Cancel: "Avbryt",
  Restore: "Återställ",
  Restored: "Återställd",
  CancelWeek: "Avboka vecka",
  CancelAllWeeks: "Avboka hela bokningsperioden",
  Canceled: "Avbokad",
  ShowAll: "Visa alla",
  ShowQualified: "Visa kvalitetssäkrade",
  PresentationReady: "Presentationsredo",
  ShowUnqualified: "Visa ej kvalitetssäkrade",
  ShowExpired: "Tidigare kvalitetssäkrad, nu förfallen",
  IsUnqualified: "Ej kvalitetssäkrad",
  IsQualified: "Kvalitetssäkrad",
  IsQualifiedAndExpired: "Tidigare kvalitetssäkrad, nu förfallen",
  IsNotQualified: "Ej kvalitetssäkrad",
  QualificationFilter: "Kvalitetssäkring Sverige",
  NationalQualificationFilter: "Kvalitetssäkring Nationella",
  Bookings: "Bokningar",
  Finance: "Ekonomi",
  FinanceOK: "Ekonomi OK",
  FinanceStatus: "Status",
  CanWorkWithVaccination: "Vill arbeta med vaccinering?",
  CovidTitle: "Covid-19",
  HaveConsultantReceivedDose1: "Har konsulten fått Dos 1?",
  HaveConsultantReceivedDose2: "Har konsulten fått Dos 2?",
  HaveConsultantBeenOfferedVaccine: "Har konsulten blivit erbjuden vaccin?",
  DoesConsultantWantToGetTheVaccine: "Vill konsulten vaccineras?",
  WhatWasTheConsultantVaccinated: "Vilket vaccin fick konsulten?",
  ShowOnlyMyActivity: "Visa endast min aktivitet",
  Schedule: "Schema",
  SeeAttachedSchedule: "Se bifogat schema",
  eventActivity: "Händelselogg - aktivitet",
  LastContact: "Senaste kontakt",
  LastContactDate: "Senaste kontakt",
  LastContactType: "Senaste kontaktsättet",
  ExternalTaxNumber: "Samordningsnummer",
  MiscFilters: "Övriga filter",
  resetFilters: "Rensa filter",
  forceRefresh: "Tvinga fram uppdatering sida",
  ClearEverything: "Rensa allt",
  AllFilters: "Alla filter",
  Filters: "Filter",
  Date: "Datum",
  ValidFrom: "Gäller från",
  EditedOn: "Redigerad den",
  StartDateWhenDueShouldCount: "Datum från när förfallodatum ska börja räknas",
  StartDate: "Startdatum",
  PostingDate: "Bokföringsdatum",
  SignedOn: "Signerad på",
  EndDate: "Slutdatum",
  EndDateAfterStartDate: "Slutdatum ska vara efter Startdatumet",
  FormUnSaveMessage:
    "Du har osparade ändringar, är du säker på att du vill stänga formen? Ändringarna kommer gå förlorade.",
  OneOfDatesAreReq: "Välj minst en av dem",
  Actions: "Actions",
  Total: "Totalt",
  TotalCost: "Verklig kostnad",
  TotalCost2: "Kostnad",
  TotalNights: "Antal nätter",
  TotalGr: "Intäkter",
  TotalIncome: "Intäkt",
  NewCustomerLink: "Beställ ny kund/kundnummer",
  NewCustomerLinkAction: "här",
  Email: "Email",
  SMS: "SMS",
  Excel: "Excel",
  CreateList: "Skapa lista",
  MassActions: "Masshantering",
  History: "Historik",
  "Additional message": "Additional message",
  ResponsiblePersonEmpty: "[Ansvarig saknas]",
  ResponsiblePersonAny: "[Ansvarig finns]",
  Upload: "Ladda upp",
  UploadFile: "Ladda upp fil",
  RemoveFile: "Radera fil?",
  Uploaded: "Uploaded",
  CreateReminder: "Skapa påminnelse",
  CreatedReminder: "Påminnelse skapad",
  CreateReminderFailed: "Misslyckades att skapa påminnelse, försök igen.",
  Only: (something: string) => `Endast ${something}`,
  OnlyMy: "Endast mina",
  Competence: "Kompetens",
  Competences: "Kompetenser",
  Interested: "Vill få jobbförslag",
  NotInterested: "Ej intresserad",
  Experienced: "Erfarenhet",
  IsExperienced: "Har erfarenhet",
  IsNotExperienced: "Ingen erfarenhet",
  ExperienceFrom: "Erfarenhet från",
  ExperienceYearFrom: "Erfarenhet sedan",
  VUBYearFrom: "Utbildad sedan",
  CertifiedFrom: "Certifierad från",
  LastUsed: "Senast använd",
  HasDrivingLicence: "Har körkort",
  hasEmail: "Har email",
  hasPhone: "Har telefonnummer",
  NorwegianSsn: "Norskt personnnummer/D-nummer",
  CommentAuthorization: "Godkjent turnus",
  HPRNumber: "HPR-nummer",
  AboutYouTitle: "Om dig",
  SummaryAmbitionSection: "Kortfattad kompetensbeskrivning",
  AboutYouLabel:
    "Kort om konsulten, intressen, vad driver och inspirerar, varför arbetar du inom yrket?",
  IntroTitle: "Intro",
  IntroLabel: "Kort introduktionstext om konsult",
  ImportantToYouTitle: "Vad är viktigt för dig?",
  ImportantToYouLabel: "Vad är viktigt för att du ska trivas, önskemål eller behov?",
  Position: "Position",
  ReferencePosition: "Position",
  ReferenceWorkplace: "Arbetsplats",
  ReferencesAssignmentEvaluations: "Referenser/Uppdragsutvärderingar",
  QualityStatusesWithoutForms: "Kvalitetsstatusar utan formulär",
  newForm: "Nytt formulär",
  editForm: "Redigera formulär",
  NotGood: "Ej bra",
  LessGood: "Mindre bra",
  AcceptedEvaluate: "Godkänt",
  Good: "Bra",
  VeryGood: "Mycket bra",
  PrescriptionCode: "Förskrivarkod",
  Exists: "Intresserad",
  Qualification: "Kvalifikation",
  Pass: "Godkänd",
  NotPass: "Ej godkänd",
  CourseName: "Namn på utbildning",
  CourseLocation: "Namn på skola/universitet",
  Degree: "Hel Utbildning",
  Course: "Kurs",
  Education: "Utbildning",
  EducationClarification: "Skriv vad konsulten ska arbeta som",
  DriverLicense: "Körkort",
  NotRelevantMonthlyWeeklyPayouts: "Vecko-/månadsutbetalning ej relevant",
  NotRelevantMonthlyWeeklyPayoutsForPsy: "Konsult får betalt per utredning",
  EducationType: "Utbildningstyp",
  AdjustWorkRelationHistoryAutomatically: "Justera arbetsrelationshistoriken automatiskt",
  IncludeComments: "Inkludera kommentarer",
  IncludeGenCV: "Vill du inkludera det CRM-genererade CV:t?",
  IncludeGenCVInfo:
    "CRM-CVt genereras av den informationen som läggs in direkt på konsultens profil under “Bakgrund”. Om konsultens profil är ifylld och du väljer “Ja” då behöver du inte inkludera orginalfilen som du har fått av konsulten.",
  Yes: "Ja",
  No: "Nej",
  Why: "Varför",
  Workplace: "Arbetsställe",
  WorkplaceInTheApp: "Arbetsplats i appen",
  DepartmentInTheApp: "Avdelning i appen",
  WorkplaceAndDeps: "Arbetsplats + ev avdelning",
  FromYear: "Från år",
  ToYear: "Till år",
  Worktype: "Arbetstyp",
  WorkExperience: "Erfarenhet",
  ExtraCurriculum: "Ytterligare Erfarenhet",
  AddNewWorkExperience: "Lägg till enfarenhet",
  New: "Ny",
  Back: "Tillbaka",
  Top: (parentName) => `Go to ${parentName}`,
  Active: "Aktiva",
  Completed: "Avslutade",
  MyReminders: "Mina påminnelser",
  CalendarView: "Kalendarvy",
  AddNew: "Lägg till ny",
  AddSubVersion: "Skapa Prisavvikelse",
  AddSubVersionInfo:
    "Prisavvikelse kan skapas för de avtal där man anger pris vid presentation. Om du skapar en prisavvikelse här så anger du grundpriset. Vi kommer sedan lägga på eventuella faktorer enligt grundavtalet i uträkningen för underlaget.",
  AddSubVersionDisableInfo: "Prisavvikelser är inte möjliga på det nationella avtalet.",
  Subversion: "Prisavvikelse",
  NewPeriod: "Ny period",
  Period: "Period",
  Period2: "Vald period",
  Periods: "Perioder",
  PeriodBooked: "Inbokad period",
  PeriodDesc: "Period (fyll i de datum/veckor som konsulten ska arbeta)",
  PeriodDesc2: "Period (Skriv tidsperioden/veckorna/datumen konsulten ska arbeta)",
  Add: "Lägg till",
  AddToFav: "Lägg till favorit",
  onlyFavourites: "Endast favoriter",
  AddToTimeReport: "Lägg till Tidrapport från appen",
  MyActions: "Mina actions",
  Appointments: "Beställningar",
  DueDate: "Förfaller",
  Empty: "Saknas uppgifter",
  NoItems: "Inga poster",
  Blank: "Tom",
  View: "Visa",
  Copy: "Kopiera",
  Edit: "Redigera",
  SendToOneflow: "Skicka till OneFlow",
  SendToRef: "Skicka till Ref",
  ReSendToRef: "Skicka in igen till Ref",
  ReSentToRef: "Skickad in igen till Ref",
  RepublishToOneflow: "Återpublicera till OneFlow",
  SendToConsultant: "Skicka till konsult",
  Generate: "Generera",
  Reference: "Referens",
  References: "Referenser",
  ReferencesNational: "Referenser Nationella",
  newReference: "Ny referens",
  WorkRelationship: "Arbetsrelation",
  EditReferences: "Redigera referenser",
  EditEvalutes: "Redigera Uppdragsutvärdering",
  EditEvalutesToThreeMonths: "Redigera UU Nationella Kryssfrågor",
  Evaluates: "Uppdragsutvärdering",
  EvaluatesThreeMonths: "Uppdragsutvärdering 3 mån",
  UUCrossQuestions: "UU Nationella Kryssfrågor",
  Download: "Ladda ner",
  Downloading: "Laddar ner…",
  BookingReportsMenu: "Rapporter",
  BookingReportMaxMonthsDatePeriod: "Tillåten tidsperiod max 18 månader.",
  DownloadPresentation: "Ladda ner presentation",
  "Download options": "Nerladdnings val",
  DownloadInvoiceForm: "Ladda ner fakturaformulär",
  FillInvoiceForm: "Redigera fakturaformulär",
  CreateInvoiceFormFortnox: "Skicka utkast på kundfaktura till Fortnox",
  CreateInvoiceVoucherFortnoxMenu: "Bokföringsverifikation ej bokförda fakturor",
  FortnoxCreateVouchersForm:
    "Du skapar nu upp en bokföringsverifikation för ej bokförda fakturor i fortnox.",
  FortnoxCreateVouchers: "Bokföringsverifikationen skapas",
  SickForm: "Sjuklönemall",
  ManageSickForm: "Redigera sjuklönemall",
  NoteForSickForm: "Anteckningar för sjuklönemall",
  NoteForSubcontractorForm: "Anteckningar för underleverantörsfaktura",
  Note: "Anteckning",
  editNote: "Redigera anteckning",
  PreviewInvoiceFormFortnox: "Öppna kundfakturan i Fortnox",
  AssignToBooking: "Koppla kundfaktura från Fortnox till bokningsvecka",
  ConsultantPaymentForm: "Konsult betalningsformulär",
  SubcontractorPaymentForm: "Fakturautkast underleverantör",
  SubcontractorPaymentForm2: "Kontroll av underleverantörsfaktura",
  SickPaymentForm: "Sjuklöneformulär",
  EditSubcontractorForm: "Redigera beräkning för underleverantörsfaktura",
  SendSubcontractorFormDraftToConsultant:
    "Skicka utkast för underleverantörsfaktura till konsult",
  FillConsultantPaymentForm: "Redigera lönemall",
  DownloadPaymentForm: " Ladda ner lönemall",
  SendConsultantPaymentToFortnox: "Skicka utkast på lönemall till Fortnox",
  SendConsultantSickFormToFortnox: "Skicka utkast på sjuklön till Fortnox",
  "No text": "Utan text",
  "About me": "Om mig",
  "Intro text": "Intro-text",
  "Select Method": "Välj framsida",
  TimeReported: "Tidrapporterad",
  TimeReport: "Tidrapport",
  DoubleCheckAtCreationTime: "Dubbelkontroll vid skapelsetillfället",
  WarningTRBeforeAttachDraft:
    "Det ligger redan en påbörjad tidrapport på bokningen. Är du säker att du vill skriva över konsultens fria tidrapport från appen?",
  WarningTRBeforeAttachBlank:
    "Du skriver nu över en fri tidrapport som konsulten har skapat i appen. Innan du går vidare, dubbelkolla så att den verksamhet och avdelning som konsulten har noterat stämmer med bokningen. Är du säker på att du vill skriva över tidrapporten?",
  TimeReportBookingInfoFailed:
    "Bokningen kunde inte läsas. Tidrapporten visas skrivskyddad tills den går att läsa igen.",
  MonthBreakDayOwnedByOtherBooking:
    "Dagen tillhör månadsbrytets andra bokning. Du kan tidrapportera den här, men konsulten ser inte tiden i appen – den läser dagen från bokningen som äger den.",
  TimeReportAbbr: "T/R",
  Time: "Tid",
  AddWorkHours: "Arbetad tid",
  AddSickHours: "Sjukfrånvaro",
  AddVABHours: "VAB",
  AddOnCallHours: "Jour",
  AddOnCallAHours: "Beredskap A",
  AddOnCallBHours: "Beredskap B",
  LunchAndOther: "Rast och övrigt",
  AddLanguageSkill: "Lägg till språk",
  SendFeedback: "Skicka feedback",
  FeedbackAndReport: "Feedback & Rapporter",
  LogOut: "Logga ut",
  LogInAs: "Logga in som",
  Deassign: "Deassign",
  Delete: "Radera",
  Deleted: "Raderad",
  ShowDeleted: "Visa raderad",
  Created: "Skapad",
  Updated: "Uppdaterad",
  WorkPlace: "Arbetsplats",
  ScheduleName: "Namn bemanningspersonal:",
  Evaluate: "Bedömning av",
  Affirmation: "Sammanvägt utlåtande",
  Comments: "Kommentarer",
  TakingReferencesOf: "Referenstagning av",
  AddCompetence: "Lägg till ny",
  ArrivalDate: "Första incheckning",
  DepartureDate: "Avresedag",
  AddReference: "Lägg till ny",
  AddEvaluate: "Lägg till ny",
  AddExpertise: "Lägg till ny",
  ReferenceName: "Namn på referent",
  Personality: "Personlighet",
  Cooperation: "Samarbete",
  Adaptation: "Anpassning",
  Administration: "Administration",
  administrativeFee: "Administrativ avgift",
  PatientTreatment: "Patientbemötande",
  OtherExtra: "Övrigt - Tillägg",
  OtherCustomers: "Övrigt",
  Language: "Språk",
  Payout: "Utbetalning",
  ConsultantGetsPaidPer: "Konsult får betalt per",
  BankCounselling: "Rådgivningssamtal SEB",
  BankCounsellingOptions: {
    yes: "Ja, vill ha rådgivningssamtal",
    no: "Nej, vill inte ha rådgivningssamtal"
  },
  EmptyHere: "Saknas uppgifter",
  UploadedFiles: "Uppladdade filer",
  NoFiles: "Inga filer att bifoga",
  LookUp: "Slå upp",
  NumberInUse: "Numret används redan",
  AddressInUse: "Adressen används redan",
  PersonNotFound: "Personen hittades inte",
  LookupApplied: "Data om personen fylldes i",
  LookupFailed: "Sökning misslyckades: ",
  NoCellphoneNumberRegistered: "Ingen träff på telefonnummer",
  NoAnswerInitialText: "Försök komma i kontakt med",
  NoAnswer: "Inget Svar",
  NoAccommodation: "Boende ej angett",
  NoConsultantContracts: "Det finns inga konsultavtal",
  NoCustomerContracts: "Det finns inga kundavtal",
  DoComment: "Kommentera",
  UploadCertificate: "Ladda upp attestbevis",
  Reminder: "Påminnelse",
  SendSMS: "Skicka SMS",
  SendMail: "Skicka mail",
  updateQualityNote: "Uppdatera kvalitetsanteckning",
  ReminderCreated: "Påminnelse skapad",
  Assigned: "Tilldelad",
  AssignedBy: "Tilldelad utav",
  AssignedTo: "Tilldelad till",
  Un_Assigned: "Avtaggad från hantering",
  RemoveTag: "tog bort hanteringsstatus",
  Un_Assign: "Ta bort tilldelning",
  managedBy: "Hanteras av",
  Retry: "Försök igen",
  Status: "Status",
  MetInPerson: "Träffat personligen",
  LastMetInPerson: "Senaste personliga kontakten",
  ConsultantMetBy: "Konsulten träffades utav",
  consultantAssigned: "Konsulten är nu tilldelad",
  CustomerMetBy: "Kunden träffades utav",
  Never: "Aldrig",
  WriteComment: "Skriv kommentar...",
  BookingSearchPageTitle: "Bokningar",
  AvailConsultantSearchPageTitle: "Tillgängliga konsulter",
  ConsultantSearchPageTitle: "Konsult",
  ConsultantSearchPageDesc: "Konsultvård, massutskick, etc..",
  consultantCare: "Konsultvård",
  PotentialWeeksToStaff: "Potentiella veckor att bemanna",
  ExpiresToday: "Löper ut idag",
  BirthdayMessage: (date: string) => `Gratulera på deras födelsedag (${date})`,
  NotContactedMessage: "Konsulten har inte fått uppmärksamhet senaste 14 dagarna",
  WillLoseConsultantAfterDays: (days) =>
    `Konsult kommer avtaggas om 4 dagar pga ingen kontakt senaste ${days} mån`,
  ActivityYesterday: "Aktiviteter igår",
  ActivityRecord: "Personligt rekord i aktiviteter",
  ActivityToday: "Aktiviteter idag",
  ChooseField: (text: string) => `Välj ${text}`,
  PersonalTab: "Personlig info",
  BackgroundTab: "Bakgrund",
  Misc: "Övrigt",
  RecommendedBy: "Rekommenderad utav",
  FortnoxNumber: "Fortnox Kundnummer",
  BusyRatio: "Tjänstgöringsgrad",
  Availability: "Tillgänglighet",
  AccommodationRequested: "Finns beställning",
  FailedGettingAvailability: "Misslyckades att hämta tillgänglighet",
  FailedUpdatingAvailability: "Misslyckades att uppdatera tillgänglighet",
  AvailabilityArea: "Tillgänglighetsområde",
  PreferredSchedule: "Föredraget schema",
  PreferredChoice: "Förstahandsval",
  GeneralInterestRegion: "Geografisk preferens län",
  EnvCertification: "Miljöcertifiering",
  EnvCertificationName: "Namn på miljöcertifiering",
  Delegated: "Delegerade",
  NotAvailable: "Ej tillgänglig",
  NotAvailableShort: "n/a",
  Required: "Krävs",
  NothingFound: "Inget hittades",
  NextContact: "Nästa kontakt",
  NextContactReason: "Anledning",
  notSpecified: "Ej angett",
  EightDigitsRequired: "8-siffor krävs",
  YYYY: "ÅÅÅÅ",
  YYYYorX: "ÅÅÅÅ eller `x`",
  SortBy: "Sortera på:",
  SortBy1: "Sortera efter:",
  SearchFailed: "Sökning misslyckades",
  ErrorOccurred: "Ett fel uppstod",
  ReloadPage: "Ladda om sidan",
  ClickForDetails: "Klicka här för detaljer",
  UploadToApprove: "Ladda upp fil för att godkänna",
  ActionLog: "Händelselogg",
  Files: "Filer",
  matching: "Matchning",
  ChooseFiles: "Välj en eller flera filer i listan",
  File: "Fil",
  ConsultantBookingFiles: "Filer underlag",
  dropFilesToUpload: "Släpp filer för att ladda upp",
  getFileUploadError: (allowedExts: string) =>
    `Ej tillåten filtyp. Använd ${allowedExts}`,
  Kit: "Kit",
  KitAutomation: "Kit Automation",
  CVIsRequired: "Du måste ladda upp ett CV för att kunna beställa referens/UU.",
  GDPR: "GDPR",
  "Skip for now": "Hoppa över",
  "Set a reminder?": "Skapa påminnelse?",
  ContactPersonRenameConfirm:
    "Kontaktpersonen har döpts om. Vill du skapa en ny kontaktperson eller uppdatera den befintliga?",
  UpdateContactPerson: "Uppdatera",
  CreateNewContactPerson: "Skapa en ny",
  created: "Skapad",
  updated: "Uppdaterad",
  deleted: "Raderad",
  restored: "Återställd",
  "Leave now?": "Avsluta nu?",
  priceLeaveNow: "Du har osparade ändringar, är du säker på att du vill avsluta?",
  Leave: "Avsluta",
  Stay: "Stanna",
  ErrorIn: " ",
  Linked: "Länkad",
  Accepted: "Accepterad",
  Declined: "Avböjd",
  DeclineTodoAction: "Svara",
  DeclineTodoReason: "Orsak",
  WaitingForAnswer: "Väntar på svar",
  NoAnswerTodo: (name: string, lastContactDate: Date) =>
    `Försök komma i kontakt med ${name}. Senaste kontakten ${moment
      .utc(lastContactDate)
      .format("L")} skedde utan svar`,
  CellPhone: "Mobilnummer",
  StreetName: "Gatuadress",
  SocialSecurityNo: "Personnummer",
  SocialSecurityNoShort: "Personnr",
  FirstName: "Förnamn",
  LastName: "Efternamn",
  ZipCode: "Postnummer",
  hoursWorked: "Utförda arbetstimmar",
  consultantMatchingInfoText:
    "Tabellen nedan visar antalet arbetade timmar de <strong>senaste 12 månaderna</strong>. OBS - vi inte kan ta hänsyn till regeln om att kontinuitet enbart kan tillräknas 18 månader från det att den först åberopas.",
  recruitmentFees: "Rekryteringsavgifter",
  "is not allowed to be empty": "Detta fält får ej vara tomt",
  "is required": "Detta fält är obligatoriskt",
  missing: "Saknas",
  ConsultantContractMissingInfo: "Du behöver uppdatera konsultens profil. Detta saknas:",
  ConsultantMissingField: "Konsulten saknar angivet",
  PleaseFillBeforeContinue: "Vänligen ange detta innan du fortsätter.",
  Nobody: "Ingen",
  SsnAssigned: "Personnummret finns redan registrerat",
  IsAssignedToConsultant: "är redan registrerat till konsult",
  Hospital: "Sjukhus",
  AreaOfExpertise: "Område",
  AreasOfExpertise: "Områden",
  Department: "Avdelning",
  Departments: "Avdelningar",
  Professions: "Yrken",
  ProfessionInformation: "Yrkesinformation",
  Address: "Adress",
  AddressToWorkplace: "Adress till arbetsplatsen",
  ExcludeAddress: "Uteslut adress",
  excludeFromPresentation: "Exkludera från presentation?",
  ShowOrgName: "Visa organisationsnamn som arbetsplats",
  hideDeptName: "Göm avdelningsnamn i avtalet",
  hideHospitalName: "Använd Region som uppdragsplats",
  NeedAccommodation: "Boende behövs/inget boende behövs",
  NeedTravel: "Resa behövs/ingen resa behövs",
  NeedConsultantSignature: " kräver attest på papper från konsult",
  BookingHasDuplicates:
    "För denna vecka finns en dubblett - vänligen kolla bemanningsportal för att fördela passen rätt.",
  "View duplicate": "Visa dubblett",
  NeedSignatureTROkInAppCustomers: " Attest på papper, rapportering i app ok",
  NoCustomerApproval: " kräver ej attest",
  "must contain at least 1 items": "får inte vara tomt",
  "must be a valid email": "måste vara en giltig email",
  BusinessName: "Organisationsnamn",
  BusinessSocialSecurityNo: "Organisationsnummer",
  OrganizationNumber: "Organisationsnummer",
  "fails to match the required pattern: /^([0-9]{6})+-[0-9]{4}$/":
    "måste ha formatet ######+-####",
  "fails to match the required pattern: /^([-0-9()+ ]){0,}$/":
    "måste vara ett giltigt telefonnummer",
  "fails to match the required pattern: /^([0-9]{8})+-[0-9]{4}$/":
    "måste vara ett giltigt personnummer",
  Phone: "Telefon",
  MissingComment: "Kommentar saknas",
  Registration: "Registrering",
  MarkAsDeleted: "Markera som borttagen",
  MarkAsDeletedConfirmation: "Är du säker att du vill markera denna som borttagen?",
  DeleteConfirmation: "Är du säker på att du vill ta bort denna?",
  EventLog: "Kommentarer",
  AreYouSure: "Är du säker?",
  AreYouSureRemoveConfirmation:
    "Är du säker? Detta kommer ta bort bekräftelsen för följande veckor:",
  AreYouSureChangeContractInvoicing:
    "Du håller nu på att byta faktureringsstrategi, vill du fortsätta?",
  AreYouSureDeleteConsultantReference:
    "Du är på väg att helt radera den här referensen. Den kan inte återskapas. Vill du fortsätta?",
  EditMigratedConsultantReferenceTooltip:
    "Du kan inte redigera den här referensen du den migrerades som pdf från förra implementeringen.",
  OrderConsultantReferenceTooltip:
    "Du måste lägga till formulärtyp för att kunna beställa. Klicka “Redigera” för att lägga till formulärtyp.",
  SendToKvkConsultantReferenceTooltip:
    "Mail och/eller formulärtyp är inte angivet. Klicka “Redigera” och lägg till detta för att kunna skicka.",
  OK: "OK",
  "Email sent successfully": "Email skickad framgångsrikt",
  "SMS messages in progress": "SMS-meddelanden skickas",
  Close: "Avbryt", // cancel??
  Close1: "Stäng",
  AllTemplates: "Alla mallar...",
  AboutUs: "Om oss",
  ChangeBA: "Ändra ansvarig person",
  TruncatedRecordSet: (limit, total) => `Endast ${limit} av ${total} visas`,
  CopyValue: (value: string) => `Kopiera ${value}`,
  Validity: "Giltighet",
  Valid: "OK",
  Invalid: "Ej OK",
  InvalidValue: "Ogiltigt värde",
  Unassigned: "Saknar ansvarig BA",
  MyUnassigned: "Mina + saknar ansvarig BA",
  ValidationError: "Valideringsfel",
  All: "Alla",
  Everything: "Allt",
  SelectAll: "Välj alla",
  And: "Och",
  BusinessAddress: "Företagsadress",
  "must be a string": "måste vara text",
  "Press Enter for a new line": "Tryck Enter för ny rad",
  "Press Shift+Enter for a new line": "Tryck Shift+Enter för ny rad",
  "Click to upload a new image": "Klicka för att ladda upp en bild",
  "Click to delete an avatar": "Klicka för att radera en avatar",
  "Are you sure you want to update email with the new information?": (
    oldEmail,
    newEmail
  ) =>
    `Är du säker på att du vill uppdatera e-mail ${oldEmail} med nya informationen ${newEmail}?`,
  "Are you sure you want to update your mobile number with the new information?": (
    oldMobile,
    newMobile
  ) =>
    `Är du säker på att du vill uppdatera mobilnumret ${oldMobile} med nya informationen ${newMobile}?`,
  "Are you sure you want to update the phone number with the new information?": (
    oldPhone,
    newPhone
  ) =>
    `Är du säker på att du vill uppdatera telefonnumret ${oldPhone} med nya informationen ${newPhone}?`,
  CustomerCreateEventCondition: "Välj kontaktperson för att skapa kommentar",
  SubCustomers: "Enheter",
  NameAndTitle: "Namn och titel",
  CallLists: "Ringlistor",
  ForPipeline: "För pipeline",
  CallList: "Ringlista",
  ContactLists: "Kontaktlistor",
  ContactList: "Kontaktlista",
  Contacts: "Kontakter",
  CustomerFine: "Vite (läggs in som +) och intäkt (läggs in som -)",
  AdministrativeFee: "Förmedlingskostnad",
  CallListRemoveWithoutMembers: "Är du säker på att du vill radera listan?",
  CallListRemoveWithMembers: "Är du säker på att du vill lämna gruppen?",
  CallListCount: (count: number) => `${count} st`,
  AddToNewCallList: "Lägg till ny ringlista",
  NewCallList: "Ny ringlista",
  AddToNewContactList: "Lägg till ny kontaktlista",
  NewContactList: "Ny kontaktlista",
  addMoreInfo: "Lägg till mer information",
  MissingContactPerson: "Kontaktperson saknas",
  ConsultantGDPRDescription: "När har konsulten godkänt GDPR och vilka uppgifter?",
  ProcessingDataLegalBasis: "Laglig grund för hantering av personuppgifter",
  ApprovedPrivacyPolicy: "Integritetspolicy samtycke",
  ApprovedMarketing: "Marknadsföring samtycke",
  ApprovedMailing: "Behovsutskick samtycke",
  SendAsCampaign: "Skicka som behovskampanj",
  ApprovedMailingVia: (channel: string) => `Behovsutskick samtycke via ${channel}`,
  ApprovedCampaignMailingVia: (channel: string) =>
    `Behovskampanj samtycke via ${channel}`,
  SignedContract: "Avtal signerat",
  EnteredBy: "Angett av",
  AcceptedVia: "Accepterad via",
  InformationCameVia: "Informerade via",
  EditGdprConsent: (consent: string) => `Redigera ${consent.toLowerCase()}`,
  GrantedConsent: (consent: string) => `Samtycke till ${consent.toLowerCase()}`,
  Granted: "Godkänt",
  NotGranted: "Ej godkänt",
  optedOutOfJobMailing: "Nej till behovsutskick",
  Signed: "Signerat",
  NotSigned: "Ej signerat",
  GDPRHelpText1:
    'Observera att valen är konsultens! Om ni inte har fått info från konsulten kring vilket val de önskar väljer ni "Ej angett".',
  GDPRHelpText2:
    "Marknadsföring: Ej angett innebär att konsulten inte kommer få marknadsföringsutskick iom att konsulten inte aktivt gett sitt godkännande.\n\nBehovsutskick: Konsulten kommer få behovsutskick även om “Ej angett” är valt eftersom vi behöver kunna skicka behov till en konsult för att erbjuda vår tjänst. Bara om “Nej” är angett kommer konsultens mailadress inte komma med när man gör ett mass-utskick gällande behov från systemet.",
  LegalBasis: "Laglig grund",
  WagesType: "Lön/ersättningsanspråk per timme",
  FromWagesType: "Från lön/h (min 0 kr/h)",
  ToWagesType: "Till lön/h (max 3000 kr/h)",
  AgeType: "Ålder",
  FromAgeType: "Från ålder (min 21 år)",
  ToAgeType: "Till ålder (max 90 år)",
  OrganizationType: "Organisationstyp",
  RecruitingPortalType: "Bemanningsportal",
  CriminalRecord: "Belastningsregister",
  CriminalRecordHelperTextNational:
    'Ska din konsult arbeta inom psykiatrisk vård, vård av barn/ungdom, vård av funktionshindrade, tvångsvård- välj "HVBhem". I övriga fall, välj "Övrigt"',
  sendEmailToConsultant: "Skicka mail till konsult",
  letterFirstName: "Bokstav förnamn",
  letterLastName: "Bokstav efternamn",
  WelcomeEmail: "Välkomstmail",
  ActionType: "Händelsetyp",
  EmailExists: `Email finns redan registrerad`,
  CellPhoneExists: `Mobiltelefon finns redan registrerad`,
  SocialSecurityNoExists: `Personnummer finns redan registrerad`,
  EmailExistsInBlacklist: `Email finns registrerad på borttagen konsult.`,
  CellPhoneExistsInBlacklist: `Mobiltelefon finns registrerad på borttagen konsult.`,
  Priority: "Prioritet",
  SocialSecurityNoExistsInBlacklist: `Personnummer finns registrerad på borttagen konsult.`,
  FormCannotBeSaved: `Formuläret kan inte sparas eftersom:`,
  FormFixErrors: "Formuläret kan inte sparas, var god åtgärda",
  PleaseConsider: `Observera:`,
  ConsultantDoesNotExist: "Konsulten hittades inte, försök gärna söka igen.",
  nodeValidationErrors: {
    CustomerInsufficientRoles: (params: string[]) =>
      `Kund saknar kontakt med följande roller: ${params.join(", ")}`
  },
  Less: "Mindre",
  More: "Mer",
  greaterThan: (text: string) => `måste vara större än ${text}`,
  MyTasks: "Mina uppgifter",
  KitAttestation: "Underlagsattest",
  DelegatedToMe: "Delegerat till mig",
  DelegatedByMe: "Delegerad av mig",
  NonAnswers: "Ej svar",
  Birthday: "Födelsedag",
  ContactPersonWithSamePhoneExists: (name) =>
    `Detta telefonnummer finns redan registrerad på ${name}`,
  ContactPersonWithSameEmailExists: (name) =>
    `Denna mailadressen finns redan registrerad på  ${name}`,
  CustomerWithSameNameExists: "En kund med samma namn existerar redan",
  Parent: "Parent",
  BisnodeLookup: "Välj fält att fylla i",
  CurrentValue: "Nuvarande information",
  Current: "Nuvarande",
  BisnodeValue: "Bisnodes information",
  LanguageProficiencyLevel: "Språkfärdighet",
  Main: "Main",
  Overview: "Överblick avrop",
  Candidates: "Kandidater",
  description: "Beskrivning",
  DescriptionSchedule: "Schemat för uppdraget",
  DescriptionsQualifications: "Kvalifikationer för uppdraget",
  AboutCustomer: "Om kunden",
  Available: "Tillgänglig",
  Available2: "Tillgänglighet",
  Matches: "Matchar",
  QualityRate: "Kvalitetssäkring",
  "WW-YYYY": "VV-ÅÅÅÅ",
  NewConsultantApplication: "Ny intresseanmälan",
  NewConsultantApplicationFrom: (
    name: string,
    professions: string,
    aoe: string,
    source: string
  ) => `[${source}] Intresseanmälan ${name} - ${professions}, ${aoe || ""}.`,
  NewConsultantLoginRequest: "Konsult försöker logga in i appen, men är inte aktiverad.",
  MyActivity: "Min aktivitet",
  InvoicingData: "Fakturainformation",
  InvoicingStrategy: "Faktureringsstrategi",
  Workshift: "Arbetstider",
  Workspans: "Arbetsintervall",
  Nevermind: "Glöm det",
  "Upload Original": "Ladda upp original",
  "Crop and Upload": "Beskära och ladda upp",
  "Crop the photo": "Beskära bilden",
  Search: "Sök",
  NumberBetween: (start: number, end: number) => `Måste vara mellan ${start} - ${end}`,
  SearchProfiles: "Sökprofiler",
  SearchProfile: "Sökprofil",
  SearchProfileSaved: "Sökprofilen har sparats",
  SearchProfileDeleted: "Sökprofilen har raderats",
  TimeReportSwitchLabel: "Tidrapportering",
  Documents: "Dokument",
  NotContacted: "Uppföljningar",
  interest: "Intresseanmälan",
  ConsultantReapplied: "Konsulten har ansökt igen",
  AddList: "Ny Lista",
  EditList: "Redigera Listan",
  NoanswerTodoAdded: "En påminnelse har skapats",
  ConsultantStatusChangeSuccess: (status: ConsultantStatus) =>
    `Konsultens status har ändrats till ${consultantStatus[status]}`,
  InvoiceRef: "Faktura referens",
  OrderId: "Beställar-ID",
  "Please save the form first": "Var god spara formuläret först",
  "Only departments support workshifts": "Bara avdelningar stödjer arbetsskift",
  AddContactPersonText: "Kontaktperson saknas for ",
  AddContactPersonSubText: "Lägg till kontakt för ",
  "No files found": "Det finns inga filer ännu",
  "Document type": "Dokumenttyp",
  DocumentToCertify: "Underlag att attestera",
  Uploading: "Laddar upp...",
  FileUpload: "Filuppladdning",
  FileUploadSuccess: "Filen har sparats",
  FileUploadFail: "Kan inte ladda up filen",
  FileDragNDrop: "Dra och släpp filer här eller",
  ChooseFile: "Välj fil",
  EditFile: "Redigera fil",
  Updating: "Uppdaterande...",
  FileUpdateSuccess: "Filen har uppdaterats framgångsrikt",
  FileUploadSucceeded: "Uppladdningen lyckades",
  FileUpdateFail: "Filen har inte uppdaterats",
  Prev: "Tidigare",
  Next: "Nästa",
  RefreshListing: "Uppdatera listan",
  ConsultantResponsiblePerson: "Konsult BA",
  CustomerResponsiblePerson: "Kund BA",
  "Add new booking": "Lägg till ny bokning",
  "Add new ticket": "Skapa ny ticket",
  newAccommodationRequest: "Boendebeställning där bokningsvecka saknas",
  newTravelRequest: "Resebeställning där bokningsvecka saknas",
  newTravelOrAccommodationRequest: "Ny beställning till Resa och Boende",
  "Consultant accepted": "Konsult accepterad",
  "Consultant rejected": "Konsult avvisad",
  "Accept has been sent": "Acceptera har skickats",
  "Reject has been sent": "Avvisa har skickats",
  "Yes, cancel booking": "Ja, avboka bokning",
  "Yes, cancel and find replacement": "Ja, avboka och hitta ersättare",
  "Booking has been canceled": "Bokningen har avbrutits",
  "Are you sure you want to cancel this booking?":
    "Är du säker att du vill avbryta denna bokning?",
  "Are you sure you want to cancel booking group?":
    "Är du säker att du vill avbryta denna bokningsperioden?",
  BookingsCreated: "Bokningar skapade",
  BookingsUpdated: "Bokningar uppdaterade",
  MemoriseMyChoice: "Spara mitt val",
  SourceAOESpecs: "Källdata - Område och nischer",
  SourceCustomerName: "Källdata - Kund",
  ChangeDisplayNameInTheApp: "byt visningsnamn i appen",
  UserSettingsDescription:
    "Här kan du ställla in om konsulten ska kunna logga in appen mha sin mailaddress.",
  CanLogin: "Kan registrera sig / logga in",
  Chat: "Chatta",
  ShowInApp: "Visa i BonlivApp",
  Published: "Publicerad",
  Closed: "Stängd",
  Default: "Standard",
  Details: "Detaljer",
  Until: "Fram tills",
  "Edit file": "Redigera fil",
  ErrorEdit: "Redigering av fel",
  Accessed: "Nås",
  AccessedOn: "Åtkomst till",
  SharedBy: "Delad av",
  FileName: "Filnamn",
  "Open in a new tab": "Öppna i en ny flik",
  OpenInNewWindow: "Öppna i nytt fönster",
  CopyUrl: "Kopiera url",
  CopyToClipboard: "Kopiera till utklipp",
  Copied: "Kopierat",
  fileHasBeenRemoved: `File har tagits bort`,
  fileHasBeenModified: "Filen har ändrats",
  uploadHasFinished: "Uppladdningen är klar",
  IgnoreQualityChecks: "Ignorera kvalitetssäkring",
  "Select to renew": "Välj för att förnya",
  "Expiration date": "Utgångsdatum",
  Sent: "Skickat",
  SendInformationOnWr: "Skicka behov till konsult",
  SendInformationOnWrDescription: "Skicka information till konsulten om valt behov.",
  InformationOnConsultantSent: "Skicka konsultprofil till kund",
  InformationOnConsultantSentDescription: `Välj epost dit en länk till konsultprofilen kommer skickas. Detta kan vara tex bemanningsansvarig eller liknande. Snart kommer man här istället kunna välja en kontaktperson.`,
  AdditionalInformationOnConsultantSentDescription: `BA har diskuterat lön och förväntningar med konsulten innan presenterat detta till kund.`,
  ConfirmBookingCallNumber:
    "Du har förlängt en bokningsgrupp med inlagt avropsnummer. Är du säker på att de nya bokningarna har samma avropsnummer som de tidigare? Om ja så kan du gå vidare. Om nej så behöver du skapa upp en ny bokning för de bokningar med nytt avropsnummer.",
  ConfirmNewBookingCallNumber:
    "Du har lagt på ett avropsnummer på bokningsgruppen. Är du säker på att samtliga veckor ska ha samma avropsnummer? Om någon av veckorna ska ha ett annat avropsnummer, skapa en ny bokningsgrupp för dessa veckor.",
  ConfirmConsultantPresentation: "Godkänn konsultpresentation",
  Accept: "Acceptera",
  Archive: "Arkivera",
  Archived: "Arkiv",
  Reject: "Neka",
  ContractSigned: "Kontrakt signerat",
  ContractNumber: "Avtal Nr",
  "Approved/Rejected": "Godkänd/ej godkänd",
  CustomerApprovePresentationMessage:
    "Tack för ditt svar. Vi återkommer snart till dig inför nästa steg.",
  BeRepresentedIn: "Ska presenteras i",
  Presented: "Presenterad",
  ShouldCRMGeneratedCVBeIncluded: "Ska det CRM-genererade CV:t inkluderas?",
  AddedBy: "Tillagd av",
  ByUser: "Av användare",
  AddToPool: "Börja bearbeta",
  RemoveFromPool: "Sluta bearbeta",
  AddRemoveToPool: "Börja/sluta bearbeta",
  ConsultantPool: "Konsultpool",
  CandidatesUnderReview: "Följande kandidater utvärderas för detta behov",
  ConsultantPresentationWasSentByEmail: "Konsultens presentation blev skickad med Epost",
  Recipients: "Mottagare",
  noRecipients:
    "Det finns inga kontaktpersoner inlagda på kunden. Skriv in email manuellt för att kunna skicka tidrapporten. Lägg sedan gärna till kontaktpersonen på kundprofilen.",
  RecipientsTooltip1:
    "Kontaktpersonen är den person som kommer stå som kontaktperson på bokningsbekräftelsen. Om mottagare av bokningsbekräftelsen är annan än kontaktpersonen, lägg till ny mottagare nedan.",
  RecipientsTooltip2:
    "Om mottagaren av bokningsbekräftelsen skiljer sig från kontaktpersonen som ska stå på bokningsbekräftelsen lägg till annan mailadress nedan.",
  PressEnterToAddValue: 'Klicka "enter" för att lägga till fler än en mailadress',
  LostServerConnection: "Vi har tappat anslutningen till servern",
  WeWillReconnectAutomatically: "Vi återansluter dig automatiskt",
  DontCloseBrowserToContinue: "Om du har osparade ändringar, stäng inte detta fönster",
  Disabled: "Inaktiverad",
  FirstBookingInGroupWarning:
    "Avtalet innehåller en introduktionslön/ersättning och detta är den första bokningen i bokningsgruppen. Vänligen kontrollera om konsulten ska ha annan lön/ersättning för introduktionstimmar. OBS: det kan vara så att det finns en tidigare bokningsgrupp, separat från denna, där introduktion redan dragits.",
  MonthlyBrakeWarning:
    "Kund kräver månadsfakturering enligt Kalendermånad så vid månadsbryt krävs dubbla veckor i CRM",
  "Add a New Workplace": "Lägg till ny arbetsplats",
  "Add a New Contact": "Lägg till ny kontaktperson",
  MemoCreated: "Memo skapat",
  EmployeeNumber: "Anställningsnummer",
  EmployeeNumber2: "Anställningsnummer Val 2+3",
  EmployeeNumber3: "Antällningsnummer Nationella Upphandlingen",
  PartialSalary: "Delvis lön",
  InvoiceNumber: "FakturaNr",
  PaymentNumber: "LöpNr",
  InvoiceNumberFull: "Fakturanummer",
  Leaderboard: "Ledartavla",
  Todo: "Att göra",
  Users: "Användare",
  Unit: "Enhet",
  ChartUnits: {
    profit: "TB (total)",
    bookingCount: "Antal veckor",
    invoiced: "Intäkt"
  },
  Groups: "Grupper",
  Teams: "Teams",
  GroupByTeam: "Gruppera",
  ConsultantDataPopulatedViaBisnode: "Konsultdata inläst",
  BisnodeReturned: "Hittade data:",
  BisnodeNothingFound: "Hittade ingen information om konsulten",
  BisnodeErrorOccurred: "Misslyckades med att läsa in konsultdata",
  Values: "Värden",
  Stacked: "Stapla",
  NotBilledNotPaidNotPassed: "Kommande",
  NotBilledNotPaid: "Öppen",
  Billed: "Fakturerad",
  Paid: "Utbetald",
  BilledAndPaid: "Stängd",
  ChartType: "Diagramtyp",
  ToManyResults: (found: number, limit: number) =>
    `Hittade ${found} men visar endast ${limit}, var god filtrera resultatet.`,
  Absence: "Frånvaro",
  Table: "Tabell",
  Cards: "Kort",
  CardNumber: "Kortnummer",
  CartType: "Typ av kort",
  Draft: "Utkast",
  Submitted: "Lämnats",
  SentToCustomer: "Skickas till kunden",
  ApprovedByCustomer: "Godkänd av kunden",
  PaidByFinanceDept: "Betald av finansavdelning",
  WaitingApproval: "Väntar på OK",
  Rejected: "Ej godkänd",
  CustomerNumberShort: "Kundnr",
  CustomerNumber: "Kundnummer",
  lastEvent: "Senaste händelse",
  fixme: "FIXME",
  Alt: "Alt.",
  Open: "Öppna",
  ContractOut: "Avtal ut",
  ContractIn: "Avtal in",
  TravelBooked: "Resa",
  ConsultantContractBooked: "Avtal konsult",
  CustomerContractBooked: "Avtal kund",
  NoteUnSaveBlockMessage:
    "Du har osparade ändringar - är du säker på att du vill stänga sidan?",
  Accommodation: "Boende",
  NPTravel: "NU Resa",
  RequestedOn: "Beställd",
  AccommodationTravel: "Resa/Boende",
  NationalProcurementTravelQuestion:
    "Kan vi fakturera kund för resa enligt reseschablon för nationella?",
  Travel: "Resa",
  ReactionTime: "Reaktionstid",
  Live: "Live", // as in live feed, or live view
  TravelExpenses: "Resekostnader",
  AccommodationExpenses: "Boendekostnader",
  OtherExpenses: "Andra kostnader",
  InvoiceTotal: "Fakturabelopp",
  SalaryType: "Typ av lön",
  BookingUpdated: "Bokningen har uppdaterats",
  SkipTimeReportReminder: "Ingen påminnelse",
  Member: "Användare",
  TravelOk: "Resa OK/ej relevant",
  AccommodationOk: "Bostad OK/ej relevant",
  SubContractorInvoicePayed: "Underleverantörsfaktura betald",
  SickPayed: "Sjuklön betald",
  UploadForSubContractor: "Ladda upp underleverantörfaktura",
  IsSubcontractorPayed: "Faktura betald",
  IsEmployeePayed: "Lön betald",
  receiveWorkBreakdownByEmail:
    "Vill få automatiskt mail med tidrapport bifogad efter att tidrapport blivit godkänd.",
  IsCustomerBilled: "Kund Fakturerad",
  CustomerInvoice: "Fakturaformulär",
  ConsultantInvoice: "Lönemall",
  ContractType: "Kontrakttyp",
  ChoseCompany: "Genom vilket företag bokar du konsulten",
  IsNationalProcurement: "Sker uppdraget inom ramen för den Nationella upphandlingen?",
  IsNationalProcurementLabel: "Nationella upphandlingen",
  wantToCreateNationalProcurement:
    "Är avtalet du vill skapa för den Nationella Upphandlingen?",
  AB: "Bonliva AB",
  BC: "Bonliva Care AB",
  ContractTypeAB: "Bonliva AB Kontrakttyp",
  ContractTypeBC: "Bonliva Care Kontrakttyp",
  "Type of contract is a requirement for employees": "Kontrakttyp är krav för löntagare",
  AccommodationType: "Boendetyp",
  TravelStatusUpdateLog: `har uppdaterat Resa-status till`,
  ShowSelected: "Visa valda",
  Milage: "Milersättning, i km",
  Milage2: "Milersättning",
  PlannedFrom: "Plan. från",
  PlannedTo: "Plan. till",
  BreakMinutes: "Rast, minuter",
  Notes: "Anteckningar",
  PatientId: "Patient-ID",
  NotesForInvoiceForm: "Anteckningar för fakturaformulär",
  NotesForPayForm: "Anteckningar för löneförmulär",
  AdditionalPayoutAmount: "Tilläggsersättning (belopp)",
  AdditionalPayoutDescription: "Tilläggsersättning (beskrivning)",
  AdditionalPayoutSaved: "Tilläggsersättning har sparats",
  AdditionalWork: "Övertid",
  PossibleInvoiceForAccommodation: "Möjligt att fakturera för boende",
  PossibleInvoiceForTravel: "Möjligt att fakturera för resa",
  OvertimeOptionQuestion: "OB enligt SKR?",
  OvertimeOptionOBNote: "Procentuell påslag på OB",
  AdditionalWorkNotes: "Anledning till övertid",
  WorkedHoursTimeReport:
    "Tidrapport - siffran visar det totala antalet timmar i tidrapporten.",
  WorkedHoursTimeReportInfo:
    "Siffran visar det totala antalet timmar som är registrerat i tidrapporten. Siffran används enbart för kontinuitetsrapporten. Siffran används EJ för kundfakturering eller konsultutbetalning (dessa tar sin info från Fakturaunderlaget och Lön-/Underleverantörsunderlaget). Om det inte finns någon siffra innebär det att underlaget inte har hanterats i CRM och det går då bra att uppdatera siffran manuellt.",
  BookingStatus: "Bokningsstatus",
  NationalBookingStatus: "Bokningsstatus Nationella",
  BookingTagStatus: "Status underlag",
  // Var uppdraget kom ifrån — plattformen, mejlen eller den manuellt skapade
  // förfrågan. Inte samma sak som TimereportRowSource, som säger hur timmarna
  // rapporterades: ett uppdrag från Bemlo kan mycket väl få sina timmar på papper.
  DemandSource: "Uppdragskälla",
  DemandSourceEmpty: "Utan källa",
  TimereportRowSource: "Rapporteringskanal",
  TimereportRowSourceNotRecorded: "Ej registrerad",
  // timereport-api vägrar timmar på en rapport som lämnat utkast (ADR-0008, CRMDEV-7485).
  TimereportRowsLocked:
    "Timmarna kan inte ändras eftersom tidrapporten inte längre är ett utkast. Återställ tidrapporten till utkast för att ändra timmarna.",
  // Varningen innan veckan byter status eller skickas vidare medan en dag har osparade
  // ändringar: de sparas inte och försvinner när veckan laddas om (CRMDEV-7588).
  UnsavedTimesLost: "Osparade tider försvinner om du går vidare.",
  // Var timmarna rapporterades. Leverantörsnamn är egennamn och översätts inte;
  // de övriga är beskrivningar av var någon skrev in tiden.
  timereportRowSource: {
    Paper: "Papper",
    MobileApp: "Konsultappen",
    Crm: "Inskrivet i CRM",
    Manual: "Inskrivet i ERP",
    Unknown: "Kanal ej registrerad",
    OpenOrigin: "öppna källan"
  },
  BookingClosed: "Stängd",
  BookingInvoiced: "Fakturerad",
  BookingPayed: "Lön Betald",
  BookingOpen: "Öppen",
  AddConsultant: "Lägg till konsult",
  Paste: "Klistra in",
  OnCallFrom: "Jour från",
  OnCallTo: "Jour till",
  Disruptions: "Störningar",
  SetAvailablePeriods: "Välj veckor att presentera",
  ConsultAvailability: "Konsultens tillgänglighet",
  Booked: "Bokat",
  CustomerBA: "Kundansvarig",
  ConsultantBA: "Konsultansvarig",
  HospitalOverwriteWarning: (hospitalName) =>
    `Sjukhuset "${hospitalName}" har egna områdes-inställningar som kommer bli överskrivna`,
  ConfirmConsultantPresentationGuarantee:
    "Är du säker på att du läst igenom presentationen?",
  ConsultantPresentationGuarantee: "Jag har läst igenom presentationen",
  LanguageLevelFluent: "Talar flytande och kommuicerar väl",
  LanguageLevelGood: "Talar och kommunicerar bra",
  LanguageLevelOK: "Förstår",
  NoContractsFoundWarning: "Inga kontrakt hittade",
  NoContractsFoundWarningDesc:
    "Var vänlig se till att kontrakt existerar för att kunna boka konsulter.",
  "Consultant must accept this work": "Konsulten måste acceptera",
  "Customer must accept consultant": "Kunden måste acceptera",
  "Consultant must have areas of expertise on the contract":
    "Konsulten måste ha område på kontraktet",
  NewBookingAddedTodo: (workplace) => `Konsulten bokad för ${workplace} `,
  AddPeriod: "Lägg till period",
  Message: "Meddelande",
  BookingGroup: "Bokningar",
  BookingGroupUpdated: "Bokningar uppdaterade",
  Profile: "Profil",
  Avatar: "Avatar",
  Repeat: "Återkommande period",
  RepeatWeekly: "Per vecka",
  RepeatCustom: "Välj själv",
  Other: "Annat",
  OtherCost: "Övrig",
  OtherInfo: "Notera",
  SimilarEmailAlreadyAssigned: (email: string) =>
    `En liknande email, ${email}, finns redan på annan konsult.`,
  EmailAlreadyAssigned: (email: string) => `(email ${email}), finns redan.`,
  SsnAlreadyAssigned: "Detta personnumret finns redan registrerat på en annan profil.",
  BlacklistExist: (date: Date) =>
    `Den här har tidigare existerat men blivit borttagen ${toLocale.dateTime(date)}.`,
  RemoveWarning: `ok, Ta bort varning`,
  BlacklistExistedHeader: "Tidigare borttagen",
  UploadTravelDocument: "Ladda upp resedokument",
  UploadAccommodationDocument: "Ladda upp boendedokument",
  ChooseDocumentType: "Välj dokumenttyp",
  DropOrClickToUpload: "Dra filer hit eller klicka för att ladda upp",
  CountryDoesntMatchOrgNum: "Land matchar inte organisationsnummer",
  PleaseFillRequiredFields: "Var vänlig fyll i alla fält.",
  QualityCheckSucessfullySet: "Kvalitetskontroll sparad",
  QualityCheckSucessfullyRemoved: "Kvalitetskontroll borttagen",
  QualityCheckDocument: "Kvalitetssäkringsdokument",
  selectQCDocument: "Välj dokument",
  selectQCStatus: "Välj status",
  AccountNumber: "Kontonummer",
  ClearingNumber: "Clearing-nummer",
  SendToCustomer: "Skicka till kund",
  SendTo: "Skicka till",
  ToCustomer: "Till kund",
  SendToHBA: "Skicka för attest",
  RunAutoCreateBookingKit: "Auto",
  FixKit: "Skicka korrigering",
  KitForFix: "Kit kräver korrigering",
  MarkCustomerApproved: "Markera som godkänd",
  MarkSubmitted: "Markera som inskickad",
  Submit: "Skicka in",
  SendToOC: "Skicka för attest till ",
  DownloadConsultantContract: "Ladda ner kontrakt",
  HasAddConsultantContract: (
    user: string,
    type: string,
    customer: string,
    date: string
  ) => `${user} har skapat ett ${type}${customer ? ` för ${customer},` : ", "} ${date}`,
  HasAddSelfInvoicingConsultantContract: (user: string, type: string) =>
    `${user} har skapat ett avtal för ${type}`,
  UploadSignedConsultantContract: (
    user: string,
    type: string,
    customer: string,
    date: string
  ) =>
    `${user} har laddat upp ett signerat ${type}${
      customer ? ` för ${customer},` : ", "
    } ${date}`,
  UploadSignedSelfInvoicingConsultantContract: (user: string, type: string) =>
    `${user} har laddat upp ett signerat avtal för ${type}`,
  Preview: "Förhandsvisning",
  WeekDayStart: "Från dag",
  WeekDayEnd: "Till dag",
  AddContractOptions: "Ställ in avtalsalternativ",
  SelectOptionsBelow: "Klicka på valen nedan för att lägga till på kontraktet",
  SelectedOptions: "Valda alternativ",
  InvalidEmail: "Ogiltig email",
  ContractArticle: "Avtalsartikel",
  Editable: "Redigerbar",
  Price: "Pris",
  PriceMultiplier: "Faktor",
  OriginalEmail: "Originalmail & Bilagor",
  Addition: "Tillägg",
  AdditionNationalProcurementPriceStrategy:
    "Tillägg Total Ersättning (lön ink tjänstepension)",
  AddAdditionPeriodPrice: "Specifik period",
  FixedPrice: "Tillägg",
  Rooms: "Rum",
  RoomsType: "Rumstyp",
  Beds: "Sängar",
  Area: "Area",
  Options: "Faciliteter",
  ManagementStatus: "Hanteringsstatus",
  SelectWeeks: "Välj veckor",
  ClickToOpen: "Klicka för att öppna",
  OnlyOne: "Bara en",
  SaveToPersist: "Spara formuläret först för att kunna bifoga filen",
  SetNameAndExpiration: "Ställ in filnamn samt tidsperiod för delningen av filerna",
  ApproveTimereport: "Godkänn tidrapport",
  ConfirmBookingGuarantee: "Är du säker på att tidsrapporten är korrekt?",
  Owner: "Ägare",
  Admin: "Admin",
  AddRecipient: "Lägg till mottagare",
  AddInvoiceNumber: "Lägg till faktura",
  ChooseWorkTypesForContract:
    "Välj vilken typ av arbete du vill skapa avtalsartiklar för:",
  Hide: "Dölj",
  Duplicates: "Dubletter",
  FoundDuplicates: "Hittade dubbletter",
  ClickToDedupe: "Klicka på ett kort nedan för att ta bort dubbletter.",
  TypeHere: "Skriv här...",
  Attachments: "Bifogade filer",
  AttachmentsCorrupted:
    "Du har bifogat en korrupt fil, se över denna innan du går vidare.",
  Contract: "Avtal",
  FixAllTheErrors: "Åtgärda markerade fel för att kunna publicera behovet",
  AddCustomerNumberTodoTitle: (aoe) => `Lägg till kundnummer till för område ${aoe}`,
  AddCustomerNumberTodoDesc: `
Varning: En bokningar skapats på den här kunden, men kundnummret är inte satt.
Var god sätt kundnummer för att kunna fakturering ska kunna utföras.
Detta är automatiskt genererat.`,
  ChangeProjectConfirm: "Bekräfta ändring av projekt",
  SelectedProjectIs: (desc) => `Projekt kommer vara ${desc}`,
  BooksProfessions: "Bokar yrken",
  BookingInvoiceType: "Fakturatyp",
  CheckControlBtn: "Kontroll",
  WhereProjectsWithSingleMember: "Varav projekt med endast 1 medlem",
  WhereProjectsWithMultipleMembers: "Varav projekt med flera medlemmar",
  TotalWeeks: "Totalt antal veckor",
  NumApproved: "Godkända",
  NumNotApproved: "Ej godkända",
  NumNotAudited: "Ej granskade",
  CallNumber: "Avropsnummer",
  CallNumberLabel: "Avropsnummer (se inforutan för vad som efterfrågas i vilken region)",
  CallNumberLabelForRegionBleking: "Avropsnummer för Blekinge",
  CallNumberTooltip:
    "Varje avrop har ett unikt Avrops-id (ordernummer/orderreferens) som behöver läggas till på bokningen för att det ska kunna skickas med på fakturan, vilket är ett krav.",
  CallNumberTooltipForRegionBleking:
    "Varje vecka har ett eget avropsnummer. Du kan lägga till dessa specifikt på varje bokningsvecka efter att bokningsgruppen har skapats.",
  CallNumberTooltipForRegionGavleborg:
    "LGX-nummer kommer läggas till av kundfakturering senare.",
  ForceCallNumber: "Kräver avropsnummer på faktura",
  UpdateConsultantWithSnapshotData:
    "Vill du uppdatera konsulten med den nya anställningsinformationen?",
  ConsultantCurrentFinanceInformation: (
    type: string,
    organizationName: string,
    organizationNumber: string
  ) => `På konsulten finns följande information: Anställningstyp: ${type ? type : ""},
  Företagsnamn: ${organizationName}, Organisationsnummer: ${organizationNumber}`,
  Sync: "Synk",
  Audit: "Granskning",
  KitSupport: "Underlag",
  CostCenter: "Kostnadsställe",
  CostCenterId: "Kostnadsställe ID",
  CostCenterName: "Kostnadsställe namn",
  ProjectId: "Projekt ID",
  ProjectName: "Projektnamn",
  TravelTemplateLevel: "Nivå, reseschablon",
  MissingCostCenter: "Saknar kostnadsställe",
  OpenConsultantSelector: "Välj konsult",
  TitleIsMissing: "Titel saknas",
  PhoneNumberMissing: "Telefonummer saknas",
  CellPhoneNumberMissing: "Mobilnummer saknas",
  SelectBA: "Var god välj HBA först",
  NumberOfBookings: "# bokningar",
  AuditRecordByOn: (record, by, on) => `${record} av ${by}, ${on}`,
  ListProjectsWithMembers: "Visa projekt med dessa medlemmar:",
  IsNotDirectBooking: "Detta är inte en direktbokning.",
  ContactPersonEditorTitle: "Kontaktperson",
  ClickAgainToSelectAllContacts: "Click the checkbox to select all contacts",
  Clear: "Rensa",
  SentOn: "Skickad",
  ReceivedOn: "Mottagen den",
  SetOn: "Set on",
  ClearStatus: "Rensa",
  SelectValue: "Välj värde",
  SelectFile: "Välj fil",
  SelectAnotherFile: "Välj andra fil",
  DownloadTemplateFile: "Ladda ner en excelmall",
  SelectResume: "Ladda upp CV",
  ImportFile: (name = "") => `Import ${name}`.trim(),
  DragAndDropOrSelect: "Drag and dropa en fil hit eller klicka på välj fil",
  DragAndDrop: "Dra och släpp en fil här, eller klicka för att välja fil",
  DropHere: "Släpp filen här",
  MaxFileSize: (size: string) => `Max filstorlek: ${size}`,
  PreviewImportResults: "Förhandsgranska",
  PreviewTimeReport: "Förhandsgranska tidsrapport",
  ReuploadFile: "Återupladda filen",
  FixErrorsAndReupload: (total) =>
    `Filen har ${total} fel. Var god och åtgärda dem och försök ladda upp på nytt`,
  PressSaveToImport: (total) => `Klicka på spara för att importera ${total} stycken`,
  ImportSuccess: "Importen är färdig",
  consultantExportSuccess:
    "Vi hanterar din rapport, den skickas till dig på mail när den är klar.",
  consultantExportSsnSuccess:
    "Vi hanterar din rapport, den skickas till dig på mail när den är klar.",
  ImportFormatError: "Fel dokument. Du behöver använda angiven excel-mall.",
  StartAgain: "Starta om",
  CheckForDupes: "Kontrollera dubbletter",
  BiznodeLookup: "Bisnode sökning",
  RunDedupeToProceed: "Klicka på 'Kontrollera dubbletter' för att fortsätta",
  BookingIntro: "Avdrag för introduktion på avdelning",
  TenPercentDiscountQuestion:
    'Konsulten är inte taggad med området som är angett på bokningen - ska vi dra av 10% i enlighet med kraven i Nationella för "Annan specialitet"?',
  AuthorizedOrderer: "Behörig beställare (för- och efternamn enbart)",
  AtLeastOneTravelInvoicingWeekRequired:
    "Du måste välja minst en vecka att fakturera resa för.",
  NationalProcurementOption: "Ska bokningen utföras på den nationella upphandlingen?",
  NationalProcurementAlert1:
    "Du har lagt till nya bokningsveckor - ska dessa utföras på nationella upphandlingen?",
  NationalProcurementAlert2:
    "Bokningen kan inte skapas då alternativet inte matchar det som är angett på bokningsgruppen. Du behöver skapa en ny bokning för dessa veckor.",
  NationalProcurementAlert3:
    "Du har lagt till nya bokningsveckor - är behörig beställare densamma som är angivet på bokningen du förlänger?",
  NothingToImport: "Det finns ingenting att importera, vad god försök igen",
  ImportWithBiznode: "Bisnode sökning",
  ImportWithTodos: "Importera och skapa todos",
  AssignContactPersonToCustomer: (contactPerson: string, customer: string) =>
    `Vill du tilldela ${contactPerson} som en kontaktperson till kunden ${customer}?`,
  CustomerCantBeDeleted: "Kunden kan inte tas bort eftersom:",
  CantBeDeletedReasons: {
    "no-access": "Du har inte behörighet att ta bort en kund",
    "has-children": "Kunden har underkategorier",
    "has-contact-persons": "Kunden har kontaktpersoner"
  },
  CustomerCantBeMoved: "Kunden kan ej flyttas",
  DeleteCustomerPersonConfirm: (name) => `Radera klienten ${name}?`,
  CustomerDeleted: `Kunden har tagits bort`,
  CustomerGroups: "Kundgrupper",
  CustomerGroup: "Kundgrupp",
  ClickToExpand: "Klicka för att expandera",
  CustomerMoved: "Kunden flyttades",
  MoveCustomer: "Flytta kunden",
  SeeMore: "Se fler",
  SeeLess: "Se färre",
  PromoteCustomerToOrganisation: "Flytta upp till organisation",
  NoAccess: `Saknar behörighet`,
  ZipOrPlace: "Postnummer/Ort",
  Klm: "Klm",
  RadiusFromZip: "Radie från postnumret",
  RemoveContactPerson: (name) => `Välj kund att ta bort kontaktpersonen '${name}' från:`,
  SchoolPlatforms: "Skolplatformer",
  DigitalCompetence: "Digital erfarenhet",
  Qualified: "Behörig",
  Qualification1: "Behörighet",
  ExperienceSince: "Erfarenhet sedan",
  SocionomFrom: "Socionom sedan",
  AddExp: "Lägg till erfarenhet",
  AddVUB: "Lägg till VUB",
  AddVUBDoc: "Lägg till Specialisering",
  SearchAny: "Alla",
  SearchAnd: "Och",
  SearchOnly: "Enbart",
  ExperienceYears: "Erfarenhet, antal år",
  SocionomOptions: "Alternativ",
  SelectPresentationType: "Välj presentationstyp",
  SelectOneProfessionFilter: "Välj max ett yrke för att kunna använda underkategorier",
  SelectOneQualityCheckNameFilter: "Välj max ett filter för att kunna använda statusen",
  WithIntroText: "Med introduktionstext",
  WithoutIntroText: "Utan introduktionstext",
  SelectContactPerson: "Välj kontaktperson",
  OCTeam: "OC-attest",
  SelectContactPerson2: "Välj kontaktperson för Bonliva",
  SelectFiles: "Välj filer",
  CannotFindTodoItem: "(saknar innehåll)",
  AddCustomerNumberToCreateBooking: "Lägg till kundnummer för att genomföra bokning",
  staffingPortalCustomerBookingBlocked: (portal: string) =>
    `Kundens organisation har ${portal} som bemanningsportal. Bokningar för ${portal}-kunder skapas via ${portal} och kan inte skapas i CRM.`,
  fieldIsMandatory: (name: string) => `${name} är obligatoriskt`,
  NotInContractInterval: "Datum ej innanför avtalets intervall",
  maxCount: (count) => `max ${count}`,
  maxSymbols: (count) => `Max antal tecken är ${count}`,
  Outsource: "Kundavtal (konsultspecifik)",
  missingLang: "Saknar språk",
  Framework: "Ramavtal",
  Confirmation: "Bokningsbekräftelse",
  SpecificRequirementsForTheCompetence: "Ytterligare krav som gäller kompetens",
  SpecificRequirementsForTheCompetenceDesc:
    "(Fyll i eventuella särskilda villkor för uppdrag, t. ex. beträffande journalsystem, belastningsregisterutdrag etc.)",
  SpecificRequirementsForBookingConfirmationEmp:
    "Eventuella avtalsändringar som gäller enbart för bokningsbekräftelsen",
  interestEventConfirmation: "Du lägger till en intresseanmälan, vill du fortsätta?",
  OtherSpecialRequirements: "Övriga krav",
  OtherSpecialRequirementsDesc:
    "(exempel: Konsulten ska inneha SITHS-kort innan Uppdraget påbörjas, om inte annat överenskommes mellan Leverantören och Bonliva senast fjorton (14) dagar innan Uppdraget påbörjas)",
  InvestigationsNumber: "Antal utredningar",
  NumberOfVisits: "Antal patientbesök",
  NumberOfVisitsDesc:
    "Antal patientbesök (specificerar antal överenskomna patientbesök per vecka)",
  SalaryCompensation: "Lön/ersättning",
  SalaryCompensationBookingContract: "Pris",
  SalaryCompensationHourly: "Timlön inklusive semesterersättning",
  SalaryCompensationDesc: '(glöm inte skriva ut valuta ex "kr" eller "nkr")',
  WorkHours: "Tidsspecifikation",
  SalaryAddDisabled:
    "Bilaga med SKRs OB-ersättning kommer bifogas i avtalet så enbart grundersättning behöver skrivas in i avtalet",
  Currency: "Valuta",
  Compensation: "Ersättning",
  BasicPriceNationalProcurementPriceStrategy:
    "Total ersättning till konsult inklusive pension",
  BasicPriceUnderSalaryLimit: "Timlön under 7,5 * Inkomstbasbelopp",
  BasicPriceOverSalaryLimit: "Timlön över 7,5 * Inkomsbasbelopp",
  NationalProcurementPriceStrategyQuestion:
    "När en konsults bruttolön överstiger 7,5 inkomsbasbelopp på innevarande år för uppdrag som bokas på nationella upphandlingen ökar den pensionsavsättning som vi behöver göra från 4,5% till 30%. Välj nedan om du vill använda samma lön till konsult oavsett hur mycket bruttolön en konsult har tjänat på innevarande år, eller om du vill fördela om andelen lön och tjänstepension så att den totala ersättningen alltid är samma oavsett hur mycket en konsult har tjänat.",
  NationalProcurementPriceStrategyYes:
    "Jag vill erbjuda två olika löner till konsulten beroende på om hen har tjänat mer eller mindre än 7,5 inkomstbasbelopp på innevarande år. Det innebär att den totala kostnaden för Bonliva alltid är samma men att andelen lön/pension som konsulten erhåller förändras.",
  NationalProcurementPriceStrategyNo:
    "Jag vill erbjuda enbart en lön till konsult vilket innebär att den totala ersättningen till konsult ökar när hen har tjänat mer än 7,5 inkomsbasbelopp på innevarande år.",
  BasicPrice: "Ersättning",
  CompensationType: "Ersättningstyp",
  Sum: "Summa",
  Bonus: "Bonus",
  Introduction: "Introduktion",
  AddBonus: "Lägg till bonus",
  BonusType: "Bonustyp",
  Bonuses: "Bonusar",
  AddIntroduction: "Lägg till introduktion",
  BonusRequirements: "Krav för bonus",
  Travels: "Resor",
  PossibleSupplement: "Eventuellt tillägg",
  MaxCost: "Max kostnad",
  BonusAmount: "Bonusbelopp",
  RatePerHour: "Ersättning per timme",
  DateFrameworkAgreementIsSigned: "Datum då associerat ramavtal signerats",
  DateIntermittentAgreementIsSigned: "Datum för när intermittent avtal signerades",
  DateIntermittentNationalAgreementIsSigned:
    "Datum för när intermittent nationella avtal signerades",
  DownloadSuccessful: "Nedladdning genomförd",
  DownloadFailed: "Nedladdning misslyckades",
  AddContractSuccess: "Avtalet har lagt till",
  EditContractSuccess: "Avtalet har redigerats",
  deleteContractSuccess: "Avtalet har raderats",
  consultantContractInUse:
    "Avtalet används av bokningar och kan inte raderas. Välj ett annat avtal på bokningarna först.",
  selfInvoicingContractInUse:
    "Självfaktureringsavtalet används av konsultens bokningar som underkonsult och kan inte raderas. Markera det som uppsagt i stället.",
  consultantContractDeleted:
    "Konsultavtalet på bokningen har raderats. Välj ett annat avtal på bokningen och försök igen.",
  deleteContract: "Radera avtal?",
  YoEx: (y) => `${y} år`,
  Overtime: "Obekväm arbetstid",
  ConsultantContracts: "Konsultavtal",
  NewConsultantContract: "Nytt konsultavtal",
  NewConsultantSelfInvoicingContract: "Nytt självfakturering",
  NewConsultantSelfInvoicingContractAlert:
    "Du skapar nu ett avtal för självfakturering och kommer bli dirigerad till OneFlow, vill du fortsätta?",
  CustomerContracts: "Kundavtal",
  NewCustomerContract: "Nytt kundavtal",
  AgreementType: "Avtalstyp",
  Duplicate: "Duplicera",
  MonthlyBrake: "Månadsdsbryt",
  Nullify: "Markera som uppsagt",
  Nullified: "Uppsagt",
  UnNullify: "Att återställa",
  UnNullified: "Återställd",
  NullificationReason: "Orsak till makulering",
  AddBookingConfirm: "Lägg till bokningsbekräftelse löntagare",
  UploadSignedDocument: "Ladda upp signerat avtal",
  AddSalaryChoice: "Lägg till avtalsval",
  ErrorSalaryChoice: "Avtalet saknar val 1-3",
  MarkAsSigned: "Markera som signerat",
  ApprovedByConsultant: "Godkänd av konsult",
  DisableWeek: "Stäng vecka",
  Quantity: "Antal",
  seeQuantity: "Se antal",
  Increase: "Öka",
  Decrease: "Minska",
  AddNewAgreement: "Skapa nytt avtal",
  EconomyRequired: "Redigera samarbetsform för att kunna skapa avtal",
  ChooseSignedContract: "Välj signerat avtal att ladda upp",
  FileSize: "File size",
  AccordingtoContract: "Enligt upphandlat avtal",
  AccordingToCustomerContract: "Enligt kundavtal",
  SeeAttachedPriceList: "Se bifogad prislista",
  IsContactPersonInheritable:
    "Låt kontaktperson flöda neråt i kundträdet i enlighet med område",
  ShowOnMap: "Visa på karta",
  GroupInvoicing: "Grupp fakturering",
  GroupInvoicingShort: "G/f",
  CheckIn: "Incheckningstid",
  CheckOut: "Utcheckningstid",
  Instruction: "Nyckelrutiner",
  CancellationPolicy: "Avbokningsregler",
  Site: "Hemsida",
  NoKitchen: "Inget kök",
  OwnKitchen: "Eget kök",
  SharedKitchen: "Delat kök",
  SheetAndTowelsIncluded: "Lakan och handdukar ingår",
  Parking: "Parkering",
  Internet: "Internet",
  Pets: "Husdjur",
  Gym: "Gym",
  Custom: (name) => `Custom ${name}`,
  OtherReason: "Annat skäl",
  Lunch: "Rast",
  Expense: "Utlägg",
  GiveCancelingReason: "Ange i så fall skäl till avbokning",
  CannotSaveBooking: `Misslyckades med att skapa bokning.`,
  CannotCreateBookingsInPlaceOfCanceled: (weeks) =>
    `kan inte återskapa bokning på vecka ${weeks}. Förmodligen har de avbokats tidigare.`,
  ProfessionRequired: "Yrke är obligatoriskt",
  NoanswerEventText: (name) => `${name} svarade ej`,
  interestEventText: "Konsulten har skickat in en intresseanmälan",
  legalBasisEventText: (option) =>
    `Laglig grund för hantering av personuppgifter ändrades till ${option}`,
  marketingConsentEventText: (option, channel) =>
    `ändrade valet av Marknadsföring till ${option} / ${channel}`,
  mailingConsentEventText: (option, channel) =>
    `ändrade valet av Behovsutskick till ${option} / ${channel}`,
  mailingEmailConsentEventText: (option, channel) =>
    `ändrade valet av Behovsutskick (E-post) till ${option} / ${channel}`,
  mailingSmsConsentEventText: (option, channel) =>
    `ändrade valet av Behovsutskick (SMS) till ${option} / ${channel}`,
  mailingCampaignEmailConsentEventText: (option, channel) =>
    `ändrade valet av Behovskampanj (E-post) till ${option} / ${channel}`,
  privacyPolicyConsentEventText: (option, channel) =>
    `ändrade valet av Integritetspolicy till ${option} / ${channel}`,
  CancelBookingWithAccConfirmation:
    "Bokningen avbröts, men det finns bekräftelse på boende",
  CancelBookingWithTravelConfirmation:
    "Bokningen avbröts, men det finns bekräftelse på resan",
  "The phone number is already entered": "Telefonnumret är redan inlagt",
  EditTime: "Redigera tid",
  ActivePeriods: "Active periods",
  PassivePeriods: "Passiv tid",
  PreparednessA: "Beredskap A",
  PreparednessB: "Beredskap B",
  WeightContinuity: "Kontinuitet",
  WeightFulfillment: "Uppfyllelse",

  RegisterKarens: "Registrera karens",
  Karens: "Karens",
  DateKarensExp: "Datum karensen utgår",
  AddKarens: "Lägg till karens",

  estimatedResponseDate: "Beräknat svarsdatum",

  system: "System",
  journalSystem: "Journalsystem",
  journalSystemInfo:
    "Informationen du registrerar här kommer flyta neråt i kundträdet till samtliga kopplade kundprofiler.",
  consultantContractTaxCompens:
    "Konsulten har rätt till skattepliktig ersättning per mil som uppgår till 18,5 kr. Efter den 5/6 2023 kommer ersättningen öka till 25kr per mil.",
  consultantContractLangOpt: {
    v1: "motsvarande lägst C1-nivå enligt Europarådets nivåskala för språk",
    v2: "i sådan grad att god kommunikation med patienter och övrig personal hos Vårdgivaren är möjlig"
  },
  consultantSecondaryEmail: "Sekundär email (skickas ej till Globus)",
  squareFootageAttached: "Kvm finns bifogad i skrift",
  sickPaySumInfo:
    "Summan för sjuklön kan vara positiv ifall sjukperioden är fler än en vecka och karensen i den första veckan överstiger sjuklönen som ska betalas ut i samma vecka. På totalen kommer det vara korrekt iom att de justeras i nästkommande veckor som helt saknar karens.",
  basicPriceCheckboxInfo:
    "När checkbox är klickad innebär det att vi justerar fakturan för denna raden så att den matchar kraven för nationella. Men andra ord lägger vi till en rad för grundpris.",
  bemloOrgId: "Bemlo Org-ID",
  bemloUnitId: "Bemlo Enhets-ID",
  BemloOrgIdSetOnRegion: "Anges på regionen och ärvs av alla enheter under den",
  billingInstructions: "Faktureringsanvisningar",
  payoutPeriod: "Utbetalningsperiod",
  selfBilling: "Självfakturering",
  emailForSelfInvoicing: "Mail för självfakturering",
  filterOnRange: (value: number | string, length: number | string) =>
    `Filtrera på (${value}/${length})`,
  customerContractCard,
  customerContractProfile,
  customerContractPriceLists,
  turnedOnBy: "Påslagen av",
  creationDate: "Skapelsedatum",
  workHistory: "Arbetslivshistorik",
  hospQualityCheckInfo:
    "HOSP-listor genereras till kvalitetsavdelning varje dag runt lunch (+/- någon timme). Är switchen påslagen innan dagens lista genereras kommer konsulten med i dagens lista. Annars kommer den med i morgondagens lista.",
  createPresentation: "Skapa presentation",
  sendPresentation: "Skicka presentation",
  presentation: "Presentation",
  openCard: "Öppna kort",
  activeContract: "Aktivt avtal",
  lastBookedWeek: "Sista bokad vecka",
  companyInformation: "Bolagsinformation",
  company: "Bolag",
  cannotPerformAction: "Åtgärden gick inte att slutföra",
  welcomeEmailSent: "Välkomstmail utskickat",
  canSignInUsingBankId: "Konsulten kan logga in i appen via personnummer och BankID",
  addTags: "Lägg till taggar",
  tags: "Taggar",
  tag: "Tagg",
  alreadyAddedTags: "Redan skapade taggar",
  addNewTag: "Skapa ny tagg",
  newTag: "Ny tagg",
  noTag: "Saknar tagg",
  untag: "Avtagga",
  enabledBy: "Påslagen av: ",
  disabledBy: "Avslagen av: ",
  addError: (value: string, item?: string) =>
    `Kunde inte lägga till ${value} ${item ? `till ${item}` : ""}`,
  removeError: (value: string, item?: string) =>
    `Kunde inte ta bort ${value} ${item ? `från ${item}` : ""}`,
  deleteError: (value: string, item?: string) =>
    `Kunde inte radera ${value} ${item ? `från ${item}` : ""}`,
  updateError: (value: string, item?: string) =>
    `Kunde inte uppdatera ${value} ${item ? `för ${item}` : ""}`,
  callAndContactLists: "Ring- och kontaktlistor",
  addPersons: "Lägg till personer",
  doYouWantToContinue: "Vill du gå vidare?",
  somethingWentWrong: "Något gick fel",
  previous: "Föregående",
  numberOfChosenObjects: (
    amount: number,
    objectSingular: string,
    objectPlural: string
  ) => {
    return `${amount} ${(amount === 1
      ? objectSingular
      : objectPlural
    ).toLowerCase()} valda`;
  },
  use: "Använd",
  noEventsFound: "Inga händelser hittades",
  eventAddedToEventLog: "Händelsen har lagts till i händelseloggen",
  pinned: "Pinnad",
  loadMore: "Ladda fler",
  continue: "Fortsätt",
  sendAsSmsAlertText: "Du kommer skicka meddelande som sms, vill du fortsätta?",
  smsCostAlertText: (threshold: number, totalCost: string) =>
    `Du passerar nu ${threshold} tecken ink mellanslag vilket innebär att totalkostnaden för utskicket nu är ${totalCost} kr.`,
  smsTotalCostText: (total: string) => `Kostnad för utskick ${total} kr.`,
  smsInvalidCharacters: (chars: string) =>
    `Meddelandet innehåller tecken som inte kan skickas som SMS (t.ex. emojis): ${chars}`,
  smsInvalidText: "Meddelandet kan inte skickas som SMS.",
  smsUnsubscribeLinkReserved: (reserved: number) =>
    `${reserved} tecken är reserverade för avregistreringslänken som läggs till i varje SMS.`,
  smsTooLong: (length: number, max: number) =>
    `Meddelandet är för långt: ${length} av max ${max} tecken. Tecknen ^ { } \\ [ ~ ] | € räknas som två.`,
  workExperienceAdded: "Arbetslivserfarenhet tillagd",
  noWorkExperienceAdded: "Just nu finns det ingen arbetslivshistorik tillagd.",
  searchForCustomerName: "Sök efter kundnamn",
  customerEmptySearchText:
    "Vänligen skriv något i sökfältet eller lägg till kundnummer för att se resultat.",
  writeSomething: "Skriv någonting",
  otherInformation: "Övrig information",
  notRequired: "Ej obligatoriskt",
  areYouSureYouWantToDelete: (entity: string) =>
    `Är du säker på att du vill radera ${entity}?`,
  columns: "Kolumner",
  leadStatus: "Lead-status",
  consultantStatusText: "Konsultstatus",
  changeConsultantStatus: "Ändra konsultstatus",
  chooseEventLogAction: "Välj Samtal, SMS eller Anteckning först",
  primaryEmail: "Primär email",
  secondaryEmail: "Sekundär email",
  workStatistics: "Jobbstatistik",
  couldNotGetData: "Kunde inte hämta data",
  exportToExcel: "Exportera till Excel",
  exportToExcelWithSsn: "Exportera till Excel (med personnummer)",
  selectAll: "Markera allt",
  deselectAll: "Avmarkera allt",
  selectionRequiredToContinue: (entity: string, anOrThe: "en" | "ett") =>
    `Välj minst ${anOrThe} ${entity} för att fortsätta`,
  noValidEntities: (entityPlural: string) =>
    `Ingen av de valda ${entityPlural}na är giltiga för detta`,
  subjectAndContentsRequired: "Ämne och innehåll krävs",
  bulkActionConfirmation: (
    action: string,
    numberOfEntities: number,
    entitySingular: string,
    entityPlural: string
  ) =>
    `Du är på väg att ${action.toLowerCase()} ${numberOfEntities} ${(numberOfEntities ===
    1
      ? entitySingular
      : entityPlural
    ).toLowerCase()}. Vill du fortsätta?`,
  assignBaConfirmation: (totalEntities: number, ownEntities: number) =>
    `Du valde ${totalEntities} konsulter men du är bara ansvarig för ${ownEntities}. Du är på väg att ändra ansvarig person på ${ownEntities} konsulter. Vill du fortsätta?`,
  callAndContactListConfirmation: (
    numberOfCallLists: number,
    numberOfEntities: number,
    entitySingular: string,
    entityPlural: string
  ) =>
    `Du är på väg att lägga till ${numberOfEntities} ${(numberOfEntities === 1
      ? entitySingular
      : entityPlural
    ).toLowerCase()} i ${numberOfCallLists} ${
      numberOfCallLists === 1 ? "ring- eller kontaktlista" : "ring- och kontaktlistor"
    }. Vill du fortsätta?`,
  bulkActionDeleteNotValid: (
    total: number,
    validEntities: number,
    entitySingular: string,
    entityPlural: string
  ) =>
    `Du valde ${total} ${(total === 1
      ? entitySingular
      : entityPlural
    ).toLowerCase()}, men endast ${validEntities} av dem saknar ansvarig HBA.`,
  bulkActionEmailNotValid: (
    total: number,
    validEntities: number,
    entitySingular: string,
    entityPlural: string
  ) =>
    `Du valde ${total} ${(total === 1
      ? entitySingular
      : entityPlural
    ).toLowerCase()}, men endast ${validEntities} av dem har en giltig mailadress.`,
  bulkActionSMSNotValid: (
    total: number,
    validEntities: number,
    entitySingular: string,
    entityPlural: string
  ) =>
    `Du valde ${total} ${(total === 1
      ? entitySingular
      : entityPlural
    ).toLowerCase()}, men endast ${validEntities} av dem har ett giltigt mobilnummer.`,
  youWillBeNotifiedViaEmail:
    "Du kommer bli meddelad via e-mail när åtgärden är slutförd.",
  massActionQueuedTitle: "Massåtgärden är köad",
  selection: "Val",
  required: (requiredItem: string) => `${requiredItem} krävs`,
  assignTagsConfirmation: (
    numberOfTags: number,
    numberOfEntities: number,
    entitySingular: string,
    entityPlural: string
  ) =>
    `Du är på väg att lägga till ${numberOfTags} ${
      numberOfTags === 1 ? "tagg" : "taggar"
    } på ${numberOfEntities} ${(numberOfEntities === 1
      ? entitySingular
      : entityPlural
    ).toLowerCase()}. Vill du fortsätta?`,

  mine: "Mina",
  mineTeams: "Mitt team",
  bookingNationalProcurementStatisticsInfo:
    "Denna rapport skapar en grund för den rapportering som förväntas på det Nationella Avtalet. Välj relevant datumintervall innan du laddar ner.",
  period: "Tidsperiod",
  appliesTo: "Gäller för",
  createdBy: "Skapad av",
  sender: "Beställare",
  nullify: "Makulera",
  nullifiedBy: "Makulerad av",
  callOffStatus: "Anropsstatus",

  servicePreferences: {
    title: "Serviceinstruktion",
    createNew: "Ny instruktion",
    customerInfo:
      "Om du inte gör något val här kommer instruktionen gälla för samtliga bokningar inom vald period."
  },

  freshdeskTickets: {
    title: "Freshdesk",
    subject: "Ärende",
    status: "Status",
    created: "Skapad",
    updated: "Uppdaterad",
    empty: "Inga Freshdesk-ärenden hittades för den här konsulten."
  },

  workStatus: {
    title: "Arbetsstatus",
    createNew: "Lägg till status",
    reminderDate: "Påminnelsedatum",
    noStatuses: "Inga statusar",
    types: {
      PermanentPosition: "Fast tjänst",
      WorkingForCompetitor: "Arbetar för konkurrent",
      Retired: "Pensionerad",
      PartTimeConsultant: "Deltidskonsult",
      FullTimeConsultant: "Heltidskonsult",
      OnSickLeave: "Sjukskriven"
    }
  },

  consultantContractPensionSolution,

  sendPresentationSteps: {
    first: "Vidare till förhandsgranskning",
    second: "Skicka via mail",
    third: "Skicka"
  },

  // Tickets
  tickets: {
    name: "Namn",
    createdBy: "Skapad av",
    managedBy: "Hanteras av",
    delegatedBy: "Delegerad av",
    assignedTo: "Tilldelats",
    createdDateFrom: "Skapad datum från",
    createdDateTo: "Skapad datum till",
    criminalRecordType: "Belastningsregister",
    dueDateFrom: "Förfallodatum från",
    dueDateTo: "Förfallodatum till",
    completionStatus: "Slutförande Status",
    deletionStatus: "Radering av status",
    category: "Kategori",
    createTicket: "Skapa ticket",
    sendAs: "Skicka som ticket",
    text: "Titel",
    textRequired: "Titel krävs",
    description: "Beskrivning",
    dueDate: "Förfallodatum",
    dueDateReason:
      "Genomförandedatum är mindre än 10 dagar från nu, vänligen ange en anledning till detta som hjälp för oss att prioritera",
    dueDateInfo: "Datum som utbildning senast måste vara genomförd",
    tags: "Taggar",
    priority: "Prioritet",
    status: "Ärendestatus",
    errand: "Ärende",
    deliveryPlace: "Ort för kortaktivering",
    chargedProjects: "Projekt som ska belastas",
    whoChargedProjects: "Vem bekostar utbildningen",
    whichProjectCharge: "Vilket projekt ska belastas",
    educationType: "Typ av utbildning",
    perfPlace: "Ort för utförande",
    perfPlaceExt: "Ort för utförande (hemort kommer med i beställningen automatiskt)",
    statuses: {
      New: "Ohanterad",
      Accepted: "Accepterad",
      InProgress: "Påbörjad",
      Done: "Avslutad"
    },
    priorities: {
      High: "Hög",
      Low: "Låg"
    },
    exitSITHSCard: "Avsluta SITHS-kort",
    orderSITHSCard: "Beställning SITHS-kort",
    consultantOrderInfo:
      "Efter beställning av ordinarie kort tar det ca 10 arbetsdagar för kortet att komma. Ordinarie kort kan endast komma till konsultens folkbokföringsadress. Sätt förfallodatum till det datum konsulten senast behöver ett aktivt kort redo att tas till uppdragsplats.",
    consultantContractHighPriorityHelperText:
      "Hög prio innebär att avtalet skickas inom 2h från upprättandet av denna ticket. Vänligen välj annan status om behovet av omgående utskick inte är kritiskt.",
    commentFunctionSwitchedOffWhenTicketClosed:
      'Kommentarsfunktionen är avstängd då ticketen är avslutad. För att kommentera, ändra först status på ticket till "Accepterad".',
    workInStockholm: "Ska konsulten arbeta i Region Stockholm?",
    deadlineInfo:
      "Deadline är för tajt, kortet kommer inte hinna komma. Justera förfallodatum till mer än 10 dagar i framtiden eller kontakta KVK via mail för ett reservkort."
  },

  MustNotBeZero: "",
  MinHoursPerWeek: "Minimum timmar/vecka",
  FreeText: "Fritext",
  bonuses: {
    WeeklyHoursWorkedBonus: "Bonus för minimum antal arbetade timmar/vecka",
    WorkedHourBonus: "Bonus per arbetad timme",
    OvertimeBonus: "Övertidsbonus per arbetad timme",
    CustomBonus: "Bonus - fritext",
    TravelBonus: "TravelBonus"
  },

  travelTemplateLevels: {
    level1: "Nivå 1",
    level2: "Nivå 2",
    level3: "Nivå 3",
    level4: "Nivå 4"
  },

  EvaluatesUpToThreeMonthsLabels: {
    period: "Period",
    periodQuestion: "Vilken period var konsulten bokad?",
    personality: "Personlighet",
    personalityQuestion: "Hur skulle du beskriva VB som person?",
    competence: "Kompetens",
    //
    //
    // * Check instead ConsultantEvaluateToThreeMonthsEditorForm.tsx file for solution
    //
    //
    // competenceQuestion:
    //   "Hur ser du på VBs kompetens och arbetsutförande?" +
    //   "Har VB levt upp till arbetsförväntningarna?" +
    //   "Finns det något du tycker personen kunnat utveckla eller bli bättre på?",
    cooperation: "Samarbete",
    cooperationQuestion:
      "Hur upplever du att samarbetet har fungerat i personalgruppen samt med chefer/övrig personal?",
    adaptation: "Anpassning",
    adaptationQuestion:
      "Hur har VB anpassat sig till nya rutiner och förändringar i arbetet, t ex introduktion?",
    administration: "Administrering",
    administrationQuestion:
      "Hur har det fungerat med administrativa uppgifter så som journalföring?",
    patientTreatment: "Patientbemötande",
    patientTreatmentQuestion:
      "Hur har du uppfattat VBs bemötande mot patienter och anhöriga?",
    other: "Övrigt",
    otherQuestion: "Någonting mer jag behöver känna till? "
  },

  ticketsProfile: {
    assignedTo: "Tilldelad till",
    assignedBy: "Tilldelad utav",
    customer: "Beställare",
    editTitle: "Fyll i namn på tod",
    Info: "Beskrivning",
    Overview:
      "Övrig info; t.ex. enbart extrajobb utöver ordinarie arbete eller referent jobbar för oss idag, osv.",
    dropdown: {
      managedBy: "Hanteras utav",
      status: "Ärendestatus",
      tags: "Taggar",
      priority: "Prioritet"
    }
  },

  ticketsCard: {
    assignedOn: "Tilldelad den",
    deadline: "Deadline",
    assignedTo: "Tilldelats",
    assignedBy: "Tilldelad av",
    customer: "Beställare",
    acceptedBy: "Hanteras av",
    status: "Status",
    tags: "Taggar",
    sortCreateDesc: "Publicerad (nyast först)",
    sortCreateAsc: "Publicerad (äldst först)",
    sortDue: "Deadline (fallande)",
    sortNewMessages: "Nya meddelanden",
    sortNewTickets: "Nya ärenden",
    sortUnreadFirst: "Senaste händelse",
    categoryMyTask: "Mina uppgifter",
    categoryDelegatedByMe: "Delegerat av mig",
    categoryNotContacted: "Uppföljningar",
    categoryNonAnswers: "Ej svar",
    categoryBirthday: "Födelsedagar",
    categoryApplications: "Intresseanmälningar",
    categoryTeam: "Team",
    categoryTravel: "Resa",
    categoryAccommodation: "Boende",
    reason: "Anledning",
    priority: "Prioritet",
    lowPriority: "Låg prioritet",
    highPriority: "Hög prioritet",
    tooltip: {
      new: "Ny ticket",
      comment: "Nytt meddelande i ticket",
      deadline: "Deadline på väg att gå ut"
    },
    viewOnly: "Enbart läsvy"
  },

  configEditor: {
    costCenter: "Kostnadsställe",
    costCenters: "Kostnadsställen",
    contractTemplates: "Kontraktsmallar",
    tgRequirements: "TG-krav"
  },

  // Booking Invoice
  bookingSupportChecks: {
    requiredForm: "Nödvändiga blanketter",
    costCenter: "Kostnadsställe",
    project: "Projekt",
    customerNumber: "Kundnummer",
    orderNumber: "Avropsnummer",
    customerContractId: "Kundavtal",
    consultantContractId: "Konsultavtal",
    profitAbs: "Vinst i kronor",
    profitRel: "Vinst i %",
    totalInvoice: "Fakturabelopp",
    hours: "Antal timmar",
    mileage: "Reseersättning",
    expenses: "Konsults egna utgifter",
    accommodationSum: "Verklig kostnad för boende",
    travelSum: "Verklig kostnad för resa",
    orderId: "Beställares namn",
    // cvu: null,
    attachTimeReport: "Om tidrapport ska bifogas i faktureringen",
    groupInvoicing: "Gruppfakturering",
    contractNumber: "Avtalsnummer för kundavtal"
  },
  bookingInvoice: {
    invoices: "Fakturaunderlag",
    finalSum: "Tot till Lönemall",
    administrativeFee: "Administrativ avgift",
    sumWithTaxes: "Summa att fakturera (inklusive moms)",
    sumNoTaxes: "Summa (exklusive moms)",
    totalHours: "Summa timmar",
    week: "Vecka",
    period: "Period",
    customerDetails: "Kunduppgifter",
    customerName: "Kundens namn",
    workplace: "Arbetsplats/Sjukhus",
    section: "Avdelning",
    region: "Region / Landsting",
    order: "Order/avrop/HY../",
    cvu: "CVU/Beställning nr",
    clientName: "Beställares namn",
    invoiceContractNumber: "Avtalsnr (VG)",
    timesheet: "Tidrapport",
    aboutTheMission: "Om uppdraget",
    invoiceProfession: "Specialitet/kompetens",
    invoiceAreaOfExperience: "Specialitet/kompetens",
    kst: "KST",
    projectNo: "Projekt nr",
    projectName: "Projekt Namn",
    lowBt: "Lågt TB",
    billingCustomer: "Fakturera Kund",
    addRow: "Lägg till rad",
    autoComplete: "Kopiera tidigare underlag",
    specification: "Specifikation",
    numberOfHours: "Antal tim",
    hourlyRate: "Timpris",
    totalPrice: "Tot pris",
    note: "Övrigt",
    editInvoice: "Redigera faktura",
    createInvoice: "Skapa fakturamall",
    viewInvoice: "Visa faktura",
    trip: "Resa",
    accommodation: "Boende",
    consolidatedInvoice: "Samlingsfaktura",
    syncInvoice: "Synkronisera",
    invoiceSaved: "Faktura sparad",
    invoiceUpdated: "Fakturan uppdaterad"
  },

  bookingFinance: {
    SubcontractorInvoiceTotal: "Belopp för underleverantörsfaktura",
    EmployeeSalary: "Lön total",
    sickPay: "Sjuklön total",
    accommodationCost: "Boendekostnader",
    travelCost: "Resekostnader"
  },

  mainMenu: {
    accommodationRequests: "Boendebeställningar",
    travelRequests: "Resebeställningar",
    start: "Start",
    consultants: "Konsulter",
    consultantImport: "Konsult import",
    pipeline: "Pipeline",
    consultantPipeline: "Konsult Pipeline",
    consultantOpportunity: "Konsult Presentation",
    availableConsultants: "Tillgängliga konsulter",
    matching: "Matchning",
    customers: "Kunder",
    bookings: "Bokningar",
    timereportKitFlow: "Tidrapporter",
    kitFlow: "Underlag",
    contracts: "Kundavtal",
    history: "Historik",
    timeReports: "Tidrapporter",
    reports: "Rapporter",
    sharedDocuments: "Delade dokument",
    accommodations: "Boenderegister",
    configurationEditor: "Config",
    departure: "Rapportera Avvikelse",
    tbCalculator: "TB-kalkylator"
  },

  agGrid: {
    // See https://ag-grid.com/react-data-grid/localisation/ for additional translateable keys
    more: "Mer",
    to: "till",
    of: "av",
    page: "Sida",
    pageLastRowUnknown: "?",
    nextPage: "Nästa sida",
    lastPage: "Sista sidan",
    firstPage: "Första sidan",
    previousPage: "Föregående sida",
    pageSizeSelectorLabel: "Sidstorlek:",
    footerTotal: "Total"
  },

  pipeline: {
    noBoardFound: "Du har ingen aktiv tavla",
    switchBoard: "Byt bräda"
  },

  tenants,
  tenantsShort,
  accommodationType,
  accommodationRequestStatusType,
  accommodationPriceType,
  accommodationPeriodLabel,
  accommodationDocumentType,
  accommodationOfferOptions,
  accommodationSwitchesOptions,
  accommodationTravelStatus,
  bookingCannotBeRemovedReason,
  bookingInvoiceType,
  invoiceSplitType,
  invoiceMonth,
  invoicingStrategyType,
  bookingStatus,
  timeReportStatus,
  bookingKitType,
  bookingKitStatusType,
  timeReportTemplateType,
  bookingSystemEvents,
  bookingReportStatus,
  bookingReportType,
  bookingCSVReportType,
  bookingWeekDocumentTypes,
  bookingWeekConsultantDocumentWithOutBookingType,
  customerDocumentTypes,
  customerContractStatus,
  consultantContractAccommodationAlt,
  consultantContractTypes,
  accommodationPaymentMethods,
  consultantContractCurrency,
  consultantGetsPaidPer,
  consultantContractPriceType,
  consultantContractSelfInvoicingCheck,
  consultantContractBonusType,
  consultantContractTravelsAlt,
  consultantContractPeriod,
  consultantContractTravelsPeriod,
  consultantContractSalaryType,
  consultantContractPayoutType,
  bookingKitPayoutType,
  consultantContractOvertimeOpt,
  consultantQueryBookingStatus,
  consultantQueryBookingStatusStr,
  consultantStatus,
  consultantStatusOptions,
  contractsPrice,
  contractType,
  criminalRecordType,
  consultantReferenceStatus,
  consultantReferenceType,
  consultantReferenceRelation,
  drivingLicenceType,
  nodeType,
  workedInNorwayOption,
  approvedAuthorizationOption,
  consultantGDPROption,
  consultantGDPRChannel,
  consultantUnfitType,
  consultantGDPRChannelShort,
  currency,
  customerContractCompany,
  customerContractAgreementType,
  customerType,
  customerTypeShort,
  customerContractDocumentTypes,
  consultantEventTypes,
  consultantEventTypesNew,
  customerEventTypes,
  hospitalityPaidBy,
  accommodationTypeOf,
  initialContact,
  selfRegisterOption,
  languageSkillLevel,
  legalPersonType,
  legalPersonTypeWithNoOption,
  cooperationForm,
  organizationType,
  recruitingPortalType,
  reportActionTypes,
  rideType,
  luggageType,
  transportationType,
  travelRequestTime,
  todoStatus,
  qualityStatuses,
  timeReportType,
  lastContactType,
  legalBasisType,
  gdprConsentType,
  todoPriorityType,
  smsDeliveryStatusType,
  consultantTableColumnType,
  consultantBookingStatus,
  consultantLeadStatus,
  bulkActionSteps,
  bulkActionType,
  bulkActionSubType,
  qualityDocumentOrderType,
  searchQualityCheckStatus,
  searchQualityCheckReferenceStatus,
  searchQualityCheckReferenceNationalStatus,
  searchQualityCheckName,
  nextContactDateReason,
  cardTypes,
  kitTableColumnType,
  accommodationRequestTableColumnType,
  travelRequestTableColumns,
  travelRequestStatusType,

  ticketDifficultyLevel: {
    title: "Välj svårighetsgrad",
    subtitle:
      "Välj svårighetsgrad för att underlätta delegeringen av ärendet för Resor & Boende till rätt agent för att kunna utföra bokningen snabbare.",
    options: {
      [TicketDifficultyLevel.Easy]: "Lätt",
      [TicketDifficultyLevel.Medium]: "Mellan",
      [TicketDifficultyLevel.Hard]: "Svår"
    }
  },

  accommodationRequest: {
    sameAsLastTime: "Samma som sist",
    sameAsLastTimeInfo:
      "om du checkar denna box kommer vi kopiera in den senaste beställningen för denna konsulten och arbetsplatsens stad",
    isProlongation: "Förlängning av existerande boende",
    isReschedule: "Ombokning av existerande boende",
    rescheduleComment: "Kommentar till ombokningen",
    title: "Titel",
    deadline: "Deadline",
    city: "Ort",
    cityInfo: "Orten plockas från kundkortet.",
    customerAddress: "Adressen till arbetsplatsen",
    period: "Datum",
    budget: "Riktpris per natt",
    getBudgetLabel: (period = AccommodationPriceType.PerNight) =>
      `Riktpris per ${accommodationPeriodLabel[period].toLowerCase()}`,
    budgetInfo: "Det pris du anger här är det vi kommer försöka hålla.",
    acceptedOveragePercent: "Accepterad prisdifferens",
    acceptedOveragePercentInfo: "Ange hur mycket högre priset får vara än budget",
    paidBy: "Vem betalar?",
    hasOwnCar: "Har konsulten tillgång till egen bil?",
    hasCarAccess: "Behöver vi ordna med parkering till egen bil?",
    carAccess: "Behöver vi ordna med parkering för egen bil?",
    parkingMaxPrice: "Maxpris för parkering per natt",
    walkingDistance: "Gångavstånd till arbetsplatsen, anges i meter",
    sharedKitchen: "Accepterar delat kök",
    sharedConveniences: "Kan tänka sig delat kök och/eller badrum?",
    sharedBathroom: "Accepterar delat badrum",
    additionalInfo: "Kommentar till boendet",
    additionInfoDesc:
      "tex om konsult önskar ett speciellt boende, eller om en konsult tidigare haft problem med ett boende/varit extra känslig för något eller tex ska jobba natt och därför ska sova på dagen",
    shouldBeBookedDirectly: "Ska boendet bokas direkt eller önskar ni förslag först?",
    others: "Något övrigt att ta hänsyn till",
    zzNumber: "ZZ-nummer",
    differentCity: "Ska konsulten bo på annan ort än arbetsplatsen?",
    specifyCity: "Ange vilken ort",
    accommodationSuggestion: "Förslag på accepterat boende",
    accommodationSuggestionSubtitle: "Använd även denna checkbox vid personalboende",
    accommodationSuggestionDescription: [
      "Observera att ärendet kommer prioriteras och bokas omgående utan några kompletterande frågor så länge budget/natt möts.",
      "Detta för att säkra att det pris du har hittat är kvar när vi ska boka."
    ],
    staffAccommodation: "Gäller det personalbostäder",
    staffAccommodationDescription: "Använd denna checkbox vid personalbostäder",
    accommodationLink: "Länk till boendet",
    bookingConfirmationFile: "Ladda upp bokningsbekräftelse",
    bookingInformation: "Bokningsinformation",
    bookingInformationSubtitle:
      "Ange allt RBA kan tänkas behöva veta som inte redan framgår av formuläret för att kunna genomföra bokningen",
    commentAboutBooking: "Kommentar kring bokningen",
    cityValidationError:
      "Ingen ort är inlagd för Verksamheten eller Avdelningen. Lägg till detta på kundprofilen innan du kan fortsätta.",
    minValueError: (min: number) => `Minsta möjliga värde: ${min}`,
    budgetPeriod: "Riktpris per",
    // Tooltip texts
    budgetTooltip: "Det pris du anger här är det vi kommer försöka hålla.",
    acceptedOveragePercentTooltip:
      "Så länge prisdifferensen håller sig inom detta intervall kommer vi inte att kontakta dig. Om vi inte hittar något inom detta intervall kommer vi att kontakta dig.",
    periodTooltip:
      'Om du vill repetera in- och utcheckningsdatum så väljer du "Per vecka". Välj sedan de veckor som konsulten behöver boende, samt vilka veckodagar hen kommer bo där varje vecka. Då kommer vi skapa upp en period för varje vecka för förvalt in- och utcheckningsdatum (som sedan kan justeras manuellt vid behov). \n\nOm du själv vill justera perioden så väljer du "Välj själv". Systemet kommer sedan markera alla de bokningsveckor som spänner över dina bokninsdatum.'
  },

  // related to data/json/days.json
  highHolidaysList: {
    "New Years Eve": "Nyårsafton",
    "New Years Day": "Nyårsdagen",
    "Twelfth Day": "Trettondedag jul",
    "Easter Friday": "Långfredagen",
    "Easter Saturday": "Påskafton",
    "Easter Sunday": "Påskdagen",
    "Easter Monday": "Annandag påsk",
    "Ascension Day": "Kristi himmelsfärdsdag",
    "First of May": "Första Maj",
    "Sweden's national day": "Sveriges nationaldag",
    "Whitsun Eve": "Pingstafton",
    "Whit Sunday": "Pingstdagen",
    "Whit Monday": "Annandag pingst",
    "Midsummer Eve": "Midsommarafton",
    "Midsummer Day": "Midsommardagen",
    "All Saint's Day": "Alla helgons dag",
    "Christmas Day": "Juldagen",
    "Christmas Eve": "Julafton",
    "Boxing Day": "Annandag jul"
  },

  travelRequest: {
    isReschedule: "Ska ersätta existerande resa",
    rescheduleComment: "Kommentar till ombokningen",
    deadline: "Deadline",
    date: "Datum",
    time: "Tid",
    city: "Stad",
    country: "Land",
    transportationType: "Färdmedel",
    luggage: "Vid flyg, har konsulten resväska eller bara handbagage?",
    luggageTypeLabel: "Vid flyg, har konsulten resväska eller bara handbagage?",
    extraCostNote: "Extra kostnad tillkommer",
    departure: "Avresa",
    return: "Hemresa",
    from: "Från",
    to: "Till",
    isCancelable: "Ja, resan ska vara avbokningsbar (ofta till en extra kostnad)",
    isFlexible: "Ja, resan ska vara ombokningsbar (ofta till en extra kostnad)",
    shouldBeBookedDirectly: "Ska resan bokas direkt eller önskar ni förslag först?",
    oneWay: "Enkel",
    roundTrip: "Tur-Retur",
    checkIn: "Incheckningsdatum",
    checkOut: "Utcheckningsdatum",
    periodInput: "Kommentar till datum",
    others: "Något övrigt att ta hänsyn till"
  },

  countries: {
    Afghanistan: "Afghanistan",
    Albania: "Albanien",
    Algeria: "Algeriet",
    Andorra: "Andorra",
    Angola: "Angola",
    "Antigua & Deps": "Antigua & Deps",
    Argentina: "Argentina",
    Armenia: "Armenien",
    Australia: "Australien",
    Austria: "Österrike",
    Azerbaijan: "Azerbajdzjan",
    Bahamas: "Bahamas",
    Bahrain: "Bahrain",
    Bangladesh: "Bangladesh",
    Barbados: "Barbados",
    Belarus: "Vitryssland",
    Belgium: "Belgien",
    Belize: "Belize",
    Benin: "Benin",
    Bhutan: "Bhutan",
    Bolivia: "Bolivia",
    "Bosnia Herzegovina": "Bosnien Hercegovina",
    Botswana: "Botswana",
    Brazil: "Brasilien",
    Brunei: "Brunei",
    Bulgaria: "Bulgarien",
    Burkina: "Burkina",
    Burundi: "Burundi",
    Cambodia: "Kambodja",
    Cameroon: "Kamerun",
    Canada: "Kanada",
    "Cape Verde": "Cap Verde",
    "Central African Rep": "Centralafrikanska Rep",
    Chad: "Tchad",
    Chile: "Chile",
    China: "Kina",
    Colombia: "Colombia",
    Comoros: "Komorerna",
    Congo: "Kongo",
    "Congo {Democratic Rep}": "Kongo {Demokratiska Rep}",
    "Costa Rica": "Costa Rica",
    Croatia: "Kroatien",
    Cuba: "Kuba",
    Cyprus: "Cypern",
    "Czech Republic": "Tjeckien",
    Denmark: "Danmark",
    Djibouti: "Djibouti",
    Dominica: "Dominica",
    "Dominican Republic": "Dominikanska Republiken",
    "East Timor": "Östtimor",
    Ecuador: "Ecuador",
    Egypt: "Egypten",
    "El Salvador": "El Salvador",
    "Equatorial Guinea": "Ekvatorialguinea",
    Eritrea: "Eritrea",
    Estonia: "Estland",
    Ethiopia: "Etiopien",
    Fiji: "Fiji",
    Finland: "Finland",
    France: "Frankrike",
    Gabon: "Gabon",
    Gambia: "Gambia",
    Georgia: "Georgien",
    Germany: "Tyskland",
    Ghana: "Ghana",
    Greece: "Grekland",
    Grenada: "Grenada",
    Guatemala: "Guatemala",
    Guinea: "Guinea",
    "Guinea-Bissau": "Guinea-Bissau",
    Guyana: "Guyana",
    Haiti: "Haiti",
    Honduras: "Honduras",
    Hungary: "Ungern",
    Iceland: "Island",
    India: "Indien",
    Indonesia: "Indonesien",
    Iran: "Iran",
    Iraq: "Irak",
    "Ireland {Republic}": "Irland {Republic}",
    Israel: "Israel",
    Italy: "Italien",
    "Ivory Coast": "Elfenbenskusten",
    Jamaica: "Jamaica",
    Japan: "Japan",
    Jordan: "Jordanien",
    Kazakhstan: "Kazakstan",
    Kenya: "Kenya",
    Kiribati: "Kiribati",
    "Korea North": "Korea North",
    "Korea South": "Sydkorea",
    Kosovo: "Kosovo",
    Kuwait: "Kuwait",
    Kyrgyzstan: "Kirgizistan",
    Laos: "Laos",
    Latvia: "Lettland",
    Lebanon: "Libanon",
    Lesotho: "Lesotho",
    Liberia: "Liberia",
    Libya: "Libyen",
    Liechtenstein: "Liechtenstein",
    Lithuania: "Litauen",
    Luxembourg: "Luxemburg",
    Macedonia: "Makedonien",
    Madagascar: "Madagaskar",
    Malawi: "Malawi",
    Malaysia: "Malaysia",
    Maldives: "Maldiverna",
    Mali: "Mali",
    Malta: "Malta",
    "Marshall Islands": "Marshallöarna",
    Mauritania: "Mauretanien",
    Mauritius: "Mauritius",
    Mexico: "Mexico",
    Micronesia: "Mikronesien",
    Moldova: "Moldavien",
    Monaco: "Monaco",
    Mongolia: "Mongoliet",
    Montenegro: "Montenegro",
    Morocco: "Marocko",
    Mozambique: "Moçambique",
    "Myanmar, {Burma}": "Myanmar, {Burma}",
    Namibia: "Namibia",
    Nauru: "Nauru",
    Nepal: "Nepal",
    Netherlands: "Nederländerna",
    "New Zealand": "Nya Zeeland",
    Nicaragua: "Nicaragua",
    Niger: "Niger",
    Nigeria: "Nigeria",
    Norway: "Norge",
    Oman: "Oman",
    Pakistan: "Pakistan",
    Palau: "Palau",
    Panama: "Panama",
    "Papua New Guinea": "Papua Nya Guinea",
    Paraguay: "Paraguay",
    Peru: "Peru",
    Philippines: "Filippinerna",
    Poland: "Polen",
    Portugal: "Portugal",
    Qatar: "Qatar",
    Romania: "Rumänien",
    "Russian Federation": "Ryska Federationen",
    Rwanda: "Rwanda",
    "St Kitts & Nevis": "St Kitts Och Nevis",
    "St Lucia": "Sankta Lucia",
    "Saint Vincent & the Grenadines": "Saint Vincent Och Grenadinerna",
    Samoa: "Samoa",
    "San Marino": "San Marino",
    "Sao Tome & Principe": "Sao Tomé Och Principe",
    "Saudi Arabia": "Saudiarabien",
    Senegal: "Senegal",
    Serbia: "Serbien",
    Seychelles: "Seychellerna",
    "Sierra Leone": "Sierra Leone",
    Singapore: "Singapore",
    Slovakia: "Slovakien",
    Slovenia: "Slovenien",
    "Solomon Islands": "Salomonöarna",
    Somalia: "Somalia",
    "South Africa": "Sydafrika",
    "South Sudan": "Södra Sudan",
    Spain: "Spanien",
    "Sri Lanka": "Sri Lanka",
    Sudan: "Sudan",
    Suriname: "Surinam",
    Swaziland: "Swaziland",
    Sweden: "Sverige",
    Switzerland: "Schweiz",
    Syria: "Syrien",
    Taiwan: "Taiwan",
    Tajikistan: "Tadzjikistan",
    Tanzania: "Tanzania",
    Thailand: "Thailand",
    Togo: "Togo",
    Tonga: "Tonga",
    "Trinidad & Tobago": "Trinidad Och Tobago",
    Tunisia: "Tunisien",
    Turkey: "Kalkon",
    Turkmenistan: "Turkmenistan",
    Tuvalu: "Tuvalu",
    Uganda: "Uganda",
    Ukraine: "Ukraina",
    "United Arab Emirates": "Förenade Arabemiraten",
    "United Kingdom": "Storbritannien",
    "United States": "Förenta Staterna",
    Uruguay: "Uruguay",
    Uzbekistan: "Uzbekistan",
    Vanuatu: "Vanuatu",
    "Vatican City": "Vatikanstaten",
    Venezuela: "Venezuela",
    Vietnam: "Vietnam",
    Yemen: "Jemen",
    Zambia: "Zambia",
    Zimbabwe: "Zimbabwe",
    "Bonliva Care": "Bonliva Care"
  },

  languages: {
    albanian: "Albanska",
    arabian: "Arabiska",
    belarusian: "Belaruska",
    bengali: "Bengali",
    bosnian: "Bosniska",
    bulgarian: "Bulgariska",
    catalan: "Katalanska",
    chinese: "Kinesiska",
    croatian: "Kroatiska",
    czech: "Tjeckiska",
    danish: "Danska",
    dari: "Dari",
    dutch: "Nederländska",
    english: "Engelska",
    faroese: "Färöiska",
    finnish: "Finska",
    french: "Franska",
    frisian: "Frisiska",
    german: "Tyska",
    greek: "Grekiska",
    hindi: "Hindi",
    hungarian: "Ungerska",
    icelandic: "Isländska",
    indonesian: "Indonesiska",
    irish: "Iriska",
    italian: "Italienska",
    japanese: "Japanska",
    korean: "Koreanska",
    kurdish: "Kurdiska",
    latin: "Latin",
    latvian: "Lettiska",
    lithuanian: "Litauiska",
    norwegian: "Norska",
    persian: "Persiska",
    polish: "Polska",
    portugese: "Portugisiska",
    punjabi: "Punjabi",
    romanian: "Rumänska",
    russian: "Ryska",
    serbian: "Serbiska",
    sign: "Teckenspråk",
    slovak: "Slovakiska",
    slovenian: "Slovenska",
    spanish: "Spanish",
    swedish: "Svenska",
    thai: "Thai",
    tajik: "Tadzjikiska",
    turkish: "Turkiska",
    ukrainian: "Ukrainska",
    urdu: "Urdu",
    vietnamese: "Vietnamesiska"
  },

  workshifts: {
    Day: "Dagtid",
    Evening: "Kväll",
    Holiday: "Storhelg",
    Night: "Natt",
    OnCall: "Jour",
    Weekend: "Helg"
  },

  customerContractSorting: {
    customer_name_asc: "Kundens namn (alfabetisk a-ö)",
    customer_name_desc: "Kundens namn (alfabetisk ö-a)",
    customer_contract_number_asc: "Kontrakt nummer (äldst först)",
    contract_start_asc: "Kontrakt Start",
    contract_end_asc: "Kontrakt Slut",
    current_contract_end_asc: "Nuvarande Kontrakt Slut (äldst först)",
    contract_prolongation_asc: "Förlängningsdatum",
    contract_price_adjustment_date_asc: "Justeringsdatum",
    estimated_contract_value_asc: "Uppskattat avtalsvärde (lägst först)",
    estimated_contract_value_desc: "Uppskattat avtalsvärde (högst först)",
    customer_contract_number_desc: "Kontrakt nummer (nyast först)",
    contract_start_desc: "Kontrakt start (nyast först)",
    contract_end_desc: "Kontrakt slut (nyast först)",
    current_contract_end_desc: "Nuvarande kontrakt slut (nyast först)",
    contract_prolongation_desc: "Förlängningsdatum (nyast först)",
    contract_price_adjustment_date_desc: "Justeringsdatum (nyast först)"
  },

  customerContractEdit: {
    articleNumber: "Art.nr",
    articleNumberForOtherSpeciality: "Art.Nr AS",
    contractNumber: "Avtalsnr",
    contractName: "Avatalsnamn",
    agreementType: "Typ av avtal",
    dateStart: "Startdatum",
    dateEnd: "Slutdatum",
    paidPer: "Betald per",
    priceUnit: "Prisvaluta",
    workType: "Yrkeskategori",
    priceAdjustmentDate: "Prisjusteringsdatum",
    priceAdjustmentDateDeadline: "Påminnelsedatum prisjustering",
    priceAdjustmentDateDeadlinePlaceholder:
      "Ange det datum ticket ska skickas till Upphandlingsavdelningen kring att prisjustering snart behöver skickas in till kund.",
    prolongationDate: "Förlängningsdatum",
    prolongationDateDeadline: "Påminnelsedatum förlängning",
    prolongationDateDeadlinePlaceholder:
      "Ange det datum ticket ska skickas till Upphandlingsavdelningen kring att förlängning av avtal snart behöver skickas in till kund.",
    currentEndDate: "Max Avtalstid",
    dispenseDeadline: "Deadline för dispens, antal veckor",
    adjustmentDate: "Justeringsdatum",
    contractExtension: "Kontrakttyp",
    introductionInfo: "Introduktion",
    contactPerson: "Kontaktperson",
    customers: "Kunder",
    priceTemplates: "Price templates",
    priceTemplatesMissing: "Lägg till ett prisblock ",
    professions: "Område",
    proLongationComment: "Kommentar förlängning",
    adjustmentComment: "Kommentar justering",
    estimatedContractValue: "Uppskattat avtalsvärde",
    avtalsnummer: "Avtalsnummer",
    avropsnummer: "Avropsnummer",
    extraInvoiceLine: "Extra fakturamärkning",
    useNationalProcurementInvoicing: "Beräkna som nationell upphandling (andra fas)"
  },

  directRequestContract: {
    button: "Skapa direktförfrågansavtal",
    buttonTooltip: "Välj kund, område och veckor först",
    notInNationalProcurementTooltip:
      "Denna knapp kan enbart användas för direktupphandlingsavtal mot Nationella Avtalet.",
    title: "Skapa direktförfrågansavtal",
    basicPrice: "Grundpris",
    extraInvoiceLine: "Extra fakturamärkning",
    extraInvoiceLineHelp:
      "Dubbelkolla förfrågan för eventuellt krav på specifik fakturamärkning.",
    avropsnummer: "Avropsnummer",
    dateRange: "Avtalsperiod",
    create: "Skapa",
    cancel: "Avbryt",
    createSuccess: "Direktförfrågansavtal skapat",
    createFailed: "Kunde inte skapa direktförfrågansavtal",
    openContract: "Öppna det skapade avtalet i ny flik",
    durationNote: "Att skapa avtalet kan ta upp till cirka en minut.",
    saving: "Skapar avtal …"
  },

  yesNoOptions: [
    {
      label: "Ja",
      value: "true"
    },
    {
      label: "Nej",
      value: "false"
    }
  ],

  yesNoBoolOptions: [
    {
      label: "Ja",
      value: true
    },
    {
      label: "Nej",
      value: false
    }
  ],
  consultantOpportunityCard: {
    presentedWeeks: "Presenterad veckor",
    wonWeeks: "Bokad period",
    lostWeeks: "Avslagna veckor",
    replyDate: "Beräknat svarsdatum",
    org: "Org",
    costCenter: "Kst"
  },

  sorting: {
    last_contact_desc: "Senaste kontakt (nyast först)",
    last_contact_asc: "Senaste kontakt (äldst först)",
    oldest_contact_desc: "Senast tillagd (nyast först)",
    name_asc: "Namn (A-Ö)",
    relevance: "Relevans"
  },

  desc: (name) => `${name} (nyast först)`,
  asc: (name) => `${name} (äldst först)`,
  preferredCommunication: {
    email: "Email",
    letter: "Brev",
    linkedin: "LinkedIn",
    phone: "Telefon",
    sms: "SMS"
  },

  getCountryName(countryNameEn: string) {
    if (!countryNameEn) {
      console.error("wrong args");
      return "";
    }

    let result = this.countries[countryNameEn];

    if (!result) {
      result = this.countries[countryNameEn.toLowerCase()];
    }

    // try to lower-case all the keys:
    if (!result) {
      const key = Object.keys(this.countries)
        .map((str) => str.toLowerCase())
        .find((k) => k === countryNameEn.toLowerCase());

      result = this.countries[key];
    }

    return result || countryNameEn;
  },

  getLanguage(langEn: string) {
    if (!langEn) {
      console.error("wrong args");
      return "";
    }

    const result = this.languages[langEn.toLowerCase()];
    return result || langEn;
  },

  getBulkActionSuccessText(type: BulkActionType) {
    return `${this.MassActions} ${this.bulkActionType[type]} har lyckats.`;
  },

  getBulkActionErrorText(type: BulkActionType) {
    return `${this.MassActions} ${this.bulkActionType[type]} har misslyckats.`;
  },

  getBulkActionFailedText(type: BulkActionType, num: number) {
    const bulkActionType: Partial<Record<BulkActionType, string>> = {
      [BulkActionType.ConsultantEmail]: `${num} e-postmeddelanden kunde inte skickas`,
      [BulkActionType.ConsultantSMS]: `${num} SMS kunde inte skickas`
    };

    return bulkActionType[type] ?? `${num} konsulter misslyckades`;
  },

  getBulkActionResultText(type: BulkActionType, num: number) {
    const bulkActionType: Record<BulkActionType, string> = {
      [BulkActionType.ConsultantEmail]: `${num} e-postmeddelanden har skickats`,
      [BulkActionType.ConsultantReassignBA]: `${num} konsulter har uppdaterats`,
      [BulkActionType.ConsultantSMS]: `${num} SMS har skickats`,
      [BulkActionType.ConsultantAssignCallList]: `${num} konsulter har lagts till i kontaktlistor`,
      [BulkActionType.ConsultantDelete]: `${num} konsulter har tagits bort`,
      [BulkActionType.ConsultantExport]: `${num} konsulter har exporterats`,
      [BulkActionType.ConsultantExportSsn]: `${num} konsulter har exporterats`,
      [BulkActionType.ConsultantAssignTags]: `${num} konsulter har uppdaterats`,
      [BulkActionType.ConsultantOrderQualityDocuments]: `${num} kvalitetsdokument har beställts`
    };

    return bulkActionType[type];
  },

  personal: "Personlig",
  switchUserBoard: "Växla användare",
  presentedWeeks: "Presenterade bokningsveckor",
  wonWeeks: "Antal bokade veckor",
  lostWeeks: "Nekade bokningsveckor",
  openWeeks: "Öppna bokningsveckor",
  showOld: "Visa gamla",
  showResponseThisMonth: "Svar denna månad",

  consultantOpportunityBoardHeaders: {
    Presented: "Presenterad",
    Won: "Vunna",
    Lost: "Nekade"
  },

  consultantPipelineBoardHeaders: {
    NotPlaced: "Ej placerad",
    NotRelevant: "Ej aktuell",
    RelevantInTheFuture: "Aktuell längre fram",
    Warm: "Varm",
    RelevantNow: "Aktuell för uppdrag",
    ReadyToBePresented: "Presentationsredo",
    Presented: "Presenterad",
    Booked: "Bokad"
  } satisfies Record<ConsultantPipelineStatus, string>,

  enterCurrentFurtherForward: "Ange aktuell längre fram",
  reason: "Anledning",
  customReason: "Annat",
  selectUser: "Välj en användare",

  consultantQualityReferenceVariant: {
    Evaluate: "Uppdragsutvärdering",
    EvaluateToThreeMonths: "Uppdragsutvärdering 3 mån",
    Reference: "Referens",
    UUNationalCrossQuestions: "UU Nationell Korsfrågor"
  },

  zone: "Zon",
  name: "Namn",
  articleNumber: "Artikelnummer",

  consultantCriminalRecordApproveAlert:
    "Du ändrar nu datum vilket innebär att vi kommer skapa filen på nytt. Om det finns en fil med samma typ av belastningsregister uppladdad sedan tidigare så kommer denna raderas, vill du fortsätta?",
  consultantQualityItemRemoveAlert:
    "OBS! Du tar nu bara bort godkännandet. Referensen kommer fortfarande vara valbar när du skapar en presentation. Om du vill ta bort formuläret helt måste du radera den via konsultredigeraren → Referenser.",
  basicPrice: "Grundpris",

  bookingClonedSuccess: "Veckan har duplicerats",
  bookingClonedFailure: "Det gick inte att duplicera veckan",
  bookingCloningQuestion:
    "Du kommer duplicera hela bokningen inklusive tidrapporten samt alla dokument, vill du fortsätta?",
  NPOption: "NU",
  NoNPOption: "Ej NU",
  tooltipBookingSchemaRequiredTravelInvoicing:
    "Visar vilka bokningsveckor som är markerade för rese-fakturering i enlighet med NUs regler kring reseschablon.",
  NPTravelInvoicedIconTooltip: "Veckan är redan fakturerad",
  NPRequiredIconTooltip: "Veckan är markerad för fakturering av reseschablon.",
  travelInvoicingRequiredAlert: "Veckan har markerats för resefakturering, nationella.",
  travelInvoicingRequiredButNotMarkedAlert:
    "Det finns resebekräftelse uppladdad även om veckan inte är markerad för resefakturerning, nationella.",

  pausedUntil: "Pausat till",
  paused: "Pausat",
  activate: "Aktivera",

  hsaidApprovalConfirmation:
    "Om du godkänner denna status så kommer ett mail att gå ut till konsulten med information, vill du fortsätta?",
  hsaidCardPaused: "SITHS-kort har pausats",
  hsaidCardReactivated: "SITHS-kort har aktiverats igen",
  hsaidLabel: "HSAID",
  "Failed to load city inhabitants": "Misslyckades med att ladda stadsinvånare",
  accommodationManagedInErp: "Detta boende hanteras i ERP och kan inte redigeras här.",
  openInErp: "Öppna i ERP",
  erpWorkRequest: "ERP-uppdrag",
  erpExportUnit: "ERP-exportenhet",
  accommodationWeeksFromErp: "Boendeveckor (från ERP):",
  accommodationSyncedAt: "Senast synkad från ERP",
  accommodationReservations: "Bokningar",
  accommodationStayedNights: "Bodda nätter",
  accommodationBenefitTax: "Förmånsskatt",
  accommodationExtraCost: "Övriga kostnader",
  accommodationPaidBy: "Betalas av",
  accommodationNotPaidByBonliva: "betalas ej av Bonliva",
  accommodationNotBenefitTaxed: "ingen förmånsskatt, dras från lön",
  accommodationDeductedFromSalary: "dras från lön",
  accommodationChargedToSubcontractor: "faktureras underkonsult",
  accommodationSalaryDeduction: "Löneavdrag",
  accommodationArea: "Area (skriv 0 på underleverantör)",
  accommodationCity: "Ort",
  createBookingKitWf: {
    "A booking kit for the week already exists": "Ett bokningskit för veckan finns redan",
    "The time report has comments": "Tidrapporten har kommentarer",
    "The time report has customer comments": "Tidrapporten har kundkommentarer",
    "The booking has service preferences": "Bokningen har servicepreferenser",
    "The booking requires a travel allowance": "Bokningen kräver reseschablon",
    "The booking week may need a monthly break": "Bokningsveckan kan behöva månadsbryt",
    "The booking week may need a yearly break": "Bokningsveckan kan behöva årsbryt",
    "The booking week requires an introduction": "Bokningsveckan kräver intro",
    "The consultant contract includes a bonus": "Konsultavtalet tar upp en bonus",
    "The consultant contract includes accommodation compensation":
      "Konsultavtalet tar upp boendeersättning",
    "The consultant contract includes travel compensation":
      "Konsultavtalet tar upp reseersättning",
    "Missing a default customer contract": "Saknar ett förvalt kundavtal",
    "Missing a default consultant contract": "Saknar ett förvalt konsultavtal",
    "The invoice form could not be created": "Fakturaformuläret kunde inte skapas",
    "No matching contract option for":
      "ingen matchande avtalspost för loggad arbetstid på",
    "The pay form could not be created": "Löneunderlaget kunde inte skapas",
    "The subcontractor form could not be created":
      "Underleverantörsformuläret kunde inte skapas",
    "The sick pay form could not be created": "Sjukunderlaget kunde inte skapas",
    "The accommodation for the booking week is not distributed":
      "Boendet för bokningsveckan är inte fördelat",
    "The consultant is a subcontractor and lacks self-invoicing":
      "Konsult är underleverantör och saknar självfakturering",
    "There are sick hours, a sickness report has been created":
      "Finns sjuktimmar, sjukunderlag har skapats",
    "The time report is empty": "Tidsrapporten är tom",
    "The consultant has no responsible person to approve the kit":
      "Konsulten saknar ansvarig HBA som kan godkänna kitet. Lägg till en ansvarig på konsultprofilen och kör kitet igen.",
    "The kit has been created!": "Kit har skapats!",
    // Rejections the timereport-api returns when creating a pay / sick pay /
    // subcontractor form, keyed by its FormErrorCode. The workflow shows the
    // operator this reason instead of the generic failure. An untranslated code
    // falls back to the generic message — the raw API text never reaches the UI.
    formApiError: {
      BookingNotFound: "Bokningen hittades inte",
      TimereportNotFound: "Tidrapporten hittades inte",
      TimereportNotReady: "Tidrapporten är inte klar än, försök igen om en stund",
      ConsultantNotFound: "Konsulten hittades inte",
      // The API appends the profile field to fill in as a detail, e.g.
      // "... på konsultprofilen: Anställningsnummer Val 2+3".
      EmployeeNumberNotFound:
        "Konsulten saknar anställningsnummer, lägg till det på konsultprofilen",
      ContractTypeNotFound: "Avtalstyp saknas",
      ContractNotSigned: "Konsultavtalet är inte signerat",
      ConsultantTaxEmpty:
        "Konsultens skattesats saknas. Ange Generell Skattesats på konsultens profil.",
      InvalidContract: "Konsultavtalet är ogiltigt",
      TimereportDisabled: "Tidrapporten är markerad som borttagen och kan inte faktureras"
    }
  },

  "Automation process started. Kit is being created":
    "Automatiseringsprocess startad. Kit håller på att skapas",
  "Kit created!": "Underlag skapade!",
  "Kit creation failed!": "Misslyckades att skapa underlag.",
  "Updating live": "Uppdaterar i realtid",
  "Processing pending": "Bearbetning pågår",
  "Your request is being handled. Please wait until it's complete.":
    "Din förfrågan hanteras just nu. Vänta tills processen är klar.",
  "Processing failed": "Bearbetningen misslyckades",
  "Kit Automation History": "Historik för kit-automatisering",
  "Error loading workflow history": "Fel vid laddning av arbetsflödeshistorik",
  "No workflow history found": "Ingen arbetsflödeshistorik hittades",
  "Show steps": "Visa steg",
  Reason: "Orsak",
  "An error occurred during processing.": "Ett fel uppstod under bearbetningen.",
  "Processing completed": "Bearbetning slutförd",
  "The process completed successfully.": "Processen har slutförts utan problem.",

  timereportSendingToCustomer: {
    invitationNotFound: "Inbjudan hittades inte",
    ok: "Ok",
    reviewPeriodExpired: "Granskningstiden har gått ut",
    approvedBy: (email: string, reviewedOn: string) =>
      `Tidrapporten godkändes utav ${email} den ${reviewedOn}`,
    rejectedBy: (email: string, reviewedOn: string) =>
      `Tidrapporten avvisades utav ${email} den ${reviewedOn}`,
    approvalPeriodPassed:
      "Godkännandeperioden för din tidrapport har nu passerat. En ny länk har skickats ut så att du kan slutföra godkännandet.",
    alreadyApproved: "Denna tidrapport har redan godkänts.",
    alreadyRejected: "Denna tidrapport har redan avvisats.",
    alreadySentToCustomer: "Denna tidrapport har redan skickats till kunden.",
    notSendableStatus:
      "Tidrapporten kan inte skickas till kunden i sitt nuvarande status.",
    cannotSendEmail: "Det gick inte att skicka e-postmeddelandet",
    // Deliberately about contact persons in general, not only a missing email:
    // the role can be wrong, or there can be no contact person at all, and the
    // user needs to be pointed at the same place either way (CRMDEV-7141).
    noCustomerContact:
      "Det finns ingen kontaktperson att skicka tidrapporten till. Kontrollera kundens kontaktpersoner – de behöver en e-postadress och rätt roll.",
    emailSendFailed:
      "Inget e-postmeddelande nådde kunden. Tidrapportens status är oförändrad – försök igen.",
    emailPartiallySent:
      "Alla mottagare fick inte tidrapporten. Kontrollera e-postadresserna och kontakta de mottagare som saknas.",
    // Fallback for a send that failed without a known code. Every failure path
    // reaches the user before the email goes out or while it is going out, so
    // "kunden har inte fått" holds even without the code (CRMDEV-7141).
    sendFailedUnknown:
      "Tidrapporten kunde inte skickas till kunden. Kontrollera att den inte redan skickats och försök igen.",
    // Shown on the "Skickad till kund" step when the last attempt failed. The
    // status history comes from timereport-api and only knows about successful
    // sends, so without this the failed attempt leaves no trace (CRMDEV-7141).
    stepSendFailed: "Kunde inte skickas:",
    stepNotReached: "Nådde inte:",
    stepNoReason: "Okänd orsak",
    unknownError: "Okänt fel"
  },

  workflowErrors: {
    // Salary / Fortnox
    salaryTransactionsAlreadyExist:
      "Lönetransaktioner finns redan för den här perioden. Ta bort befintliga poster i Fortnox och försök igen.",
    failedToCreateSalaryTransaction:
      "Det gick inte att skapa lönetransaktion i Fortnox. Kontrollera Fortnox och försök igen.",
    pensionFeeMismatch: (formPct: string, expectedPct: string) =>
      `Pensionsavgiften på löneunderlaget är ${formPct}% men Fortnox kräver ${expectedPct}%. Uppdatera löneunderlaget och försök igen.`,
    pensionFeeMismatchSickPay: (formPct: string, expectedPct: string) =>
      `Pensionsavgiften på sjuklöneunderlaget är ${formPct}% men Fortnox kräver ${expectedPct}%. Uppdatera underlaget och försök igen.`,
    emptyBillingRecords: "Löneunderlaget innehåller inga poster att skicka.",
    emptySickRecords: "Sjuklöneunderlaget innehåller inga poster att skicka.",
    employeeNumberMissing: "Anställningsnummer saknas. Kontrollera konsultkontraktet.",
    employeeNumberMissingSickPay:
      "Anställningsnummer saknas på sjuklöneunderlaget. Kontrollera konsultkontraktet.",
    invalidWeekYearPayForm: "Löneunderlaget har ett ogiltigt vecko- eller årsformat.",
    payFormMissingConsultantId: "Löneunderlaget saknar koppling till konsult.",
    payFormMissingContractId: "Löneunderlaget saknar koppling till konsultkontrakt.",
    sickPayFormMissingContractId:
      "Sjuklöneunderlaget saknar koppling till konsultkontrakt.",
    sickPayContractMissingConsultantId:
      "Kontraktet på sjuklöneunderlaget saknar konsultkoppling.",
    // Supplier / subcontractor
    noActiveSupplier:
      "Ingen aktiv leverantör hittades för organisationsnumret i Fortnox.",
    supplierMissingVat: "Leverantören saknar momsregistreringsnummer i Fortnox.",
    noSubcontractorForm: "Inget underkonsultunderlag hittades för bokningen.",
    // Consultant
    noConsultant: "Ingen konsult är kopplad till bokningen.",
    consultantMissingEmail:
      "Konsulten saknar e-postadress. Lägg till en e-postadress och försök igen.",
    // Time report
    timereportMissingApprovalDate: "Tidrapporten saknar kundens godkännandedatum.",
    // Contract
    contractNotFound: "Kontraktet hittades inte.",
    contractNotSigned: "Kontraktet är ännu inte signerat.",
    contractAlreadySigned: "Kontraktet är redan signerat och kan inte avbrytas.",
    contractMissingExternalId: "Kontraktet saknar externt ID. Kontakta support.",
    contractWrongType: "Kontraktet är av fel typ för den här åtgärden.",
    // Generic fallback
    unknownError:
      "Arbetsflödet misslyckades oväntat. Kontakta support om problemet kvarstår."
  }
} as const;

for (const key in svLocale) {
  if (typeof svLocale[key] === "function") {
    svLocale[key] = svLocale[key].bind(svLocale);
  }
}

export { setLocale, svLocale };
